
'use client'

import { useState, useEffect, useRef, Suspense, useMemo } from "react";
import { sanitizeHtml, sanitizeRichHtml } from '@/lib/sanitize-html';
import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import { AvatarPicture, UserAvatar } from "@/components/ui/user-avatar";
import { Bookmark, Star, ChevronLeft, ChevronRight, ThumbsUp, ThumbsDown, Download, Flag, ArrowDownToLine, Vote, Trophy, Maximize, MoreHorizontal, KeyRound, Percent, Languages, ChevronDown as ChevronDownIcon, Link as LinkIcon, Pencil, Repeat, Info, Video, BarChart, MessageSquare, Eye, User, Loader2, ExternalLink } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { cn } from "@/lib/utils";
import { skipImageOptimizer } from "@/lib/image-optimizer";
import { StarRating } from "@/components/ui/star-rating";
import { SellerTier, type SellerTier as SellerTierName } from "@/components/ui/seller-tier";
import { SellerConstellation } from "@/components/ui/seller-constellation";
import { CountPill } from "@/components/ui/count-pill";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCart } from "@/context/cart-context";
import { useWishlist } from "@/context/wishlist-context";
import { useTheme } from "@/context/theme-context";
import { useProductDesignOverride } from "@/context/product-design-context";
import { resolveDesignOverride, resolveDesignWithSellerBrand, resolvePageFonts, sellerPageOverride, type PageDesign } from "@/lib/product-design";
import { SellerPageBackground } from "@/components/seller-page-background";
import { pageFontStyle } from "@/lib/page-font-faces";
import { isFullWidthGallery, isStackedLayout, resolveGalleryRatio, galleryFrameStyle, galleryBlockStyle, galleryThumbnailsOnSide, galleryShowsStandardThumbnails, galleryFilesCleared, composeGalleryMedia, resolveCoverInGallery } from "@/lib/product-gallery";
import { GalleryThumbnails } from "@/components/product/gallery-thumbnails";
import { PlaceholderMedia } from "@/components/ui/placeholder-media";
import { useParams, useRouter, useSearchParams, usePathname } from "next/navigation";
import { useAuth } from "@/context/auth-context";
import { useFirebase } from "@/firebase";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent as AlertDialogContentComponent, AlertDialogDescription as AlertDialogDescriptionComponent, AlertDialogFooter as AlertDialogFooterComponent, AlertDialogHeader as AlertDialogHeaderComponent, AlertDialogTitle as AlertDialogTitleComponent, AlertDialogTrigger, } from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { languages } from "@/lib/languages";
import { Input } from "@/components/ui/input";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { useProductsByIds, normalizeProductDoc, Product } from "@/context/product-context";
import { SuggestedProducts } from "@/components/product/suggested-products";
import { resolveSectionSeparator, sectionSurfaceClasses, sectionLayoutClasses, sectionHeaderClasses, galleryClasses, flattensInnerCard, FLAT_CARD_CLASSES } from "@/lib/product-sections";
import { useOwnedProducts } from "@/context/owned-products-context";
import { isSubscriptionTier, tierFrequency, refundWindowDaysFor } from "@/lib/product-form-shared";
import { canAppealRefund, refundAppealHref } from "@/lib/refund-escalation";
import {
    formatCycleSuffix,
    subscriptionBlockReason,
    subscriptionGateHref,
    subscriptionAccessState,
    subscriptionMs,
    canChangeTier,
    readPendingTierChange,
    formatRecurring,
    SUBSCRIPTION_GATE_COPY,
} from "@/lib/subscriptions";
import { resolveTierAction, tierActionBadge, isOwnedTierAction } from "@/lib/tier-actions";
import { downloadCandidates, tierDisplayName } from "@/lib/tier-entitlement";
import { accessLinkCount } from "@/lib/product-access-links";
import { isOffSale, offersGuestAccess, productPageAccess } from "@/lib/product-page-access";
import { resolveLivePreviewUrl } from "@/lib/live-preview-link";
import { formatFileSize } from "@/lib/product-file-pool";
import { canRequestRefund, purchaseWasPaid } from "@/lib/refund-eligibility";
import { purchasedRefundPolicy } from "@/lib/purchased-refund-policy";
import { VatNotice } from "@/components/vat-notice";
import { useGeoCountry } from "@/context/geo-context";
import { showsVatNotice } from "@/lib/vat-notice";
import { useProductSubscription } from "@/hooks/use-product-subscription";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PRICE_MIN_AUD, PRICE_MAX_AUD } from "@/lib/price-bounds";
import { cartPriceAud } from "@/lib/free-pricing";
import { resolveProductPreview } from "@/lib/product-preview";
import { toEmbedUrl } from "@/lib/video-embed";
import { pwywMinimumLabelAud, pwywSeedAmount, wholeCentFloorAud } from "@/lib/pwyw-seed";
import { currentDenomination, redenominateAmount } from "@/lib/amount-denomination";
import { useHeldDenomination } from "@/hooks/use-held-denomination";
import { EMBED_SANDBOX, isAllowedEmbedUrl } from "@/lib/sanitize-html";
import ReviewDialog from '@/components/product/review-dialog';
import { ReviewActionError, REVIEW_REFRESH_MESSAGE } from '@/lib/review-action-errors';
import { deleteGuestReview, GUEST_REVIEW_VERIFY_MESSAGE } from '@/lib/guest-review-delete';
import { nextReviewDialogKey, type ReviewDialogKey } from '@/lib/review-dialog-key';
import DownloadDialog from '@/components/product/download-dialog';
import FullscreenMedia from '@/components/product/fullscreen-media';
import ProductPreview from '@/components/product/product-preview';
import ReportDialog from '@/components/product/report-dialog';
import { WithdrawFromContract, withdrawFactsFromPurchase } from '@/components/product/withdraw-from-contract';
import { doc, getDoc, getDocs, query, where, orderBy, limit, documentId, collection, onSnapshot, setDoc, deleteDoc, updateDoc, serverTimestamp, arrayUnion, arrayRemove, deleteField, Timestamp, type QuerySnapshot } from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { STOREFRONT_LINK_PROPS, storefrontHref } from "@/lib/storefront-url";
import { track } from '@/lib/analytics';
import { captureSellerSource } from '@/lib/seller-source-capture';
import { reviewLinkStep } from '@/lib/review-link';
import { withRedirect } from '@/lib/safe-redirect';
import { catalogTagsOf, tagSlug } from '@/lib/catalog-tags';
import { OTHER_SLUG, catalogPurposeEntry, catalogTypeEntry } from '@/lib/catalog-taxonomy';
import { categoryFieldsOf, effectiveTaxonomyOf } from '@/lib/product-filters';
import { TEACHING_FIELDS, fieldName, fieldValueSummary, schoolSystemForCountry } from '@/lib/category-fields';
import { aiUseCopyOf, aiUseOf } from '@/lib/ai-disclosure';
import { isResalePack, listingBadgesOf } from '@/lib/listing-badges';
import { pageCountLabel, pagePreviewsOf, pdfPageCountOf } from '@/lib/page-previews';
import { licenceName, licenceSummary, tierLicenceOf } from '@/lib/tier-licences';
import { ListingBadges } from '@/components/product/listing-badges';
import { AlsoInBundles, BundleContents, BundlePriceNote, useBundleView } from '@/components/product/bundle-contents';
import { bundleItemLabel } from '@/lib/product-bundles';
import { LookInside } from '@/components/product/look-inside';
import {
    REVIEW_PAGE_SIZE,
    REVIEWS_SHOWN_ON_ARRIVAL,
    mostHelpfulFirst,
    reviewerSeedFromDoc,
    sellerSummaryFromDoc,
    type GuestReviewSeed,
    type ProductPageSeed,
    type ProductReviewSeed,
    type ReviewSeed,
    type SellerSummary,
} from '@/lib/product-page-seed';
import { onSnapshotFromServer } from '@/firebase/firestore/server-first-snapshot';
import { useHydrated } from '@/hooks/use-hydrated';
import { utcWallClock } from '@/lib/hydration-safe-date';
import { userErrorMessage } from "@/lib/user-error-message";

/** The Type and Purpose links in the details list: the tag links' recipe in a quieter tone. */
const TAXONOMY_LINK = 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-muted text-foreground border border-border hover:bg-muted/70 transition-colors';

type Review = {
  id: string;       // Firestore doc ID = reviewer's uid
  uid: string;
  username: string;
  avatarUrl: string;
  rating: number;
  text: string;
  createdAt: any;
  helpfulUids: string[];
  unhelpfulUids: string[];
  reply?: string;
  replyAt?: any;
  replyHelpfulUids?: string[];
  replyUnhelpfulUids?: string[];
};

type GuestReview = {
  id: string;       // Firestore doc ID = orderId
  orderId: string;
  buyerEmail: string;
  rating: number;
  body: string;
  createdAt: any;
  isGuest: true;
  helpfulUids: string[];
  unhelpfulUids: string[];
  reply?: string;
  replyAt?: any;
};

/**
 * Whether this tab holds a guest order for the product (checkout or /access put
 * it in guestOrderMap), which is what lets a signed-out guest review it here.
 * Read at the moment of asking, so it is safe to call from an effect that runs
 * before the component's own guest helpers are declared.
 */
function tabHasGuestOrder(productId: string): boolean {
  if (typeof window === 'undefined' || !productId) return false;
  try {
    const map = JSON.parse(sessionStorage.getItem('guestOrderMap') || '{}') as Record<string, unknown>;
    return !!map[productId];
  } catch {
    return false;
  }
}

/** A review document as the page holds it, with the vote lists defaulted. */
function reviewFromSnapshot(d: { id: string; data: () => unknown }): Review {
  const data = d.data() as Omit<Review, 'id'>;
  return { ...data, id: d.id, helpfulUids: data.helpfulUids ?? [], unhelpfulUids: data.unhelpfulUids ?? [] };
}

/** A guest review document as the page holds it, with the vote lists defaulted. */
function guestReviewFromSnapshot(d: { id: string; data: () => unknown }): GuestReview {
  const data = d.data() as Omit<GuestReview, 'id' | 'helpfulUids' | 'unhelpfulUids'> & { helpfulUids?: string[]; unhelpfulUids?: string[] };
  return { ...data, id: d.id, helpfulUids: data.helpfulUids ?? [], unhelpfulUids: data.unhelpfulUids ?? [] } as GuestReview;
}

const timestampFromMs = (ms: number | null | undefined) => (ms == null ? null : Timestamp.fromMillis(ms));

/**
 * An account review the server rendered, in the shape the listener delivers:
 * its dates back as Timestamps, so the date line and the "Most Recent" sort
 * read it exactly as they read a live one. `avatarUrl` stays empty, as nothing
 * shows the copy stored on a review (see the reviewer fetch).
 */
function reviewFromSeed(seed: ReviewSeed): Review {
  return {
    id: seed.id,
    uid: seed.uid,
    username: seed.username,
    avatarUrl: '',
    rating: seed.rating,
    text: seed.text,
    createdAt: timestampFromMs(seed.createdAtMs),
    helpfulUids: seed.helpfulUids,
    unhelpfulUids: seed.unhelpfulUids,
    ...(seed.reply !== undefined ? { reply: seed.reply, replyAt: timestampFromMs(seed.replyAtMs) ?? undefined } : {}),
    ...(seed.replyHelpfulUids ? { replyHelpfulUids: seed.replyHelpfulUids } : {}),
    ...(seed.replyUnhelpfulUids ? { replyUnhelpfulUids: seed.replyUnhelpfulUids } : {}),
  };
}

/**
 * A guest review the server rendered. It arrives without the order id and the
 * buyer's email (see GuestReviewSeed); nothing that renders a guest review reads
 * either, and the viewer's own guest review is read by id, with both.
 */
function guestReviewFromSeed(seed: GuestReviewSeed): GuestReview {
  return {
    id: seed.id,
    orderId: '',
    buyerEmail: '',
    rating: seed.rating,
    body: seed.body,
    createdAt: timestampFromMs(seed.createdAtMs),
    isGuest: true,
    helpfulUids: seed.helpfulUids,
    unhelpfulUids: seed.unhelpfulUids,
    ...(seed.reply !== undefined ? { reply: seed.reply, replyAt: timestampFromMs(seed.replyAtMs) ?? undefined } : {}),
  };
}

/**
 * The reviewers the server resolved, split into the three maps the page keeps
 * (see the reviewer fetch), keyed by uid.
 */
function reviewerMapsFromSeed(reviews: ProductReviewSeed | null) {
  const avatarUrls: Record<string, string | null> = {};
  const exists: Record<string, boolean> = {};
  const usernames: Record<string, string> = {};
  for (const [uid, reviewer] of Object.entries(reviews?.reviewers ?? {})) {
    avatarUrls[uid] = reviewer.avatarUrl;
    exists[uid] = reviewer.exists;
    if (reviewer.username) usernames[uid] = reviewer.username;
  }
  return { avatarUrls, exists, usernames };
}

/**
 * The product as this page holds it for a viewer: ownership, whether they are
 * its seller, and the product-level refund answer folded in.
 *
 * Shared by the listener effect and the first render, which builds it from the
 * server's copy before anybody is known (no owner, no signed-in uid), so the two
 * produce the same object for the same viewer. `isPurchased` is only ever SET
 * here, never written false, which is what the effect has always done.
 */
function withViewerState(productData: Product, viewer: { owned: boolean; uid: string | undefined }): Product {
  const out: Product = { ...productData };
  // Who is looking decides these three, never the document, which its seller
  // writes (older writers stored them; the importer still stores false). A
  // stored `isPurchased: true` used to survive here, and once ownership decided
  // who may open a product that is off sale (productPageAccess), it would have
  // let a seller reopen a taken-down listing to every visitor.
  const viewerOnly = out as Partial<Product>;
  delete viewerOnly.isPurchased;
  delete viewerOnly.isTieredPurchase;
  delete viewerOnly.purchasedTierId;
  if (viewer.owned) out.isPurchased = true;
  out.isMerchant = viewer.uid === productData.sellerId;
  // Read per pricing model (src/lib/refund-eligibility.ts). A tiered
  // product prices its tiers, not the product, so the old `price > 0`
  // test read every tiered purchase as free and hid the refund action
  // from buyers the server would have accepted. The buyer's own
  // entitlement and purchase refine this once they load (the policy
  // recorded on their purchase can turn it either way): see
  // refundActionAvailable below.
  out.canRefund = canRequestRefund({
    pricingModel: productData.pricingModel,
    price: productData.price,
    subscriptionPrice: productData.subscriptionPrice,
    priceTiers: productData.priceTiers,
    refundPolicyEnabled: productData.details?.refundPolicy?.enabled === true,
  });
  return out;
}

/**
 * The tier ticked when a product first resolves: the first one for a buyer who
 * owns nothing, none for an owner. See the seed in the listener effect for why.
 */
function initialTierFor(product: Product): number | null {
  return product.pricingModel === 'tiered' && product.priceTiers?.length
    ? (product.isPurchased ? null : product.priceTiers[0].id)
    : null;
}

/**
 * What the pay-what-you-want field opens on: the seller's floor in the buyer's
 * currency, or empty when there is none. See the seed effect for the rest.
 */
function pwywSeedFor(
  product: Product,
  toDisplayAmount: (aud: number) => number,
  displayCurrency: string,
): string {
  const floorAud = product.pricingModel === 'pwyw' && product.pwywMinPriceEnabled && product.pwywMinPrice
    // The same floor the field is validated against below, so the seed can
    // never open on "Amount is below the minimum".
    ? Math.max(PRICE_MIN_AUD, product.pwywMinPrice)
    : null;
  return floorAud === null ? '' : pwywSeedAmount(floorAud, toDisplayAmount, displayCurrency);
}

// The 'none' entry is deliberately absent. supa.place is merchant of record, so
// "No Refunds" on a listing is our representation about the buyer's statutory
// rights, and it is not accurate: a seller can decline to offer a self-service
// path, but every buyer keeps a route to a refund through support. The
// no-self-service case is labelled in getRefundPolicyLabel instead.
const refundPolicies = [
    { value: '7d', label: '7-day money back' },
    { value: '14d', label: '14-day money back' },
    { value: '30d', label: '30-day money back' },
    { value: '60d', label: '60-day money back' },
    { value: '90d', label: '90-day money back' },
    { value: 'custom', label: 'Custom Policy' }
];

type ReviewItemProps = {
    review: Review;
    resolvedAvatarUrl: string | null;
    /** The author's name from their live user doc. Falls back to the review's own
     *  copy only while the fetch is in flight. See the fetch for why the stored
     *  copy is not trusted. */
    resolvedUsername?: string;
    isDeleted: boolean;
    isAuthenticated: boolean;
    formatDate: (date: any) => string;
    onReply: (reviewId: string, replyText: string) => void;
    onEditReply: (reviewId: string, replyText: string) => void;
    onDeleteReply: (reviewId: string) => Promise<void>;
    onVote: (reviewId: string, voteType: 'helpful' | 'unhelpful', forReply?: boolean) => void;
    isMerchant: boolean;
    currentUserId?: string;
    isProductDeleted?: boolean;
};

/**
 * How long the sticky top bar takes to slide away, in ms.
 *
 * MIRRORED by the `duration-300` class on the bar itself. The two have to
 * agree: this is how long the element is kept mounted after it has been told
 * to go, so a shorter value cuts the animation off part-way and a longer one
 * leaves an invisible bar sitting over the top of the page.
 */
const STICKY_BAR_EXIT_MS = 300;

// REVIEW_PAGE_SIZE, the window of reviews of each kind the page holds, lives in
// src/lib/product-page-seed.ts, because the server reads the same window for
// the first render.

const ReviewItem = ({ review, resolvedAvatarUrl, resolvedUsername, isDeleted, isAuthenticated, formatDate, onReply, onEditReply, onDeleteReply, onVote, isMerchant, currentUserId, isProductDeleted = false }: ReviewItemProps) => {
    const isOwnReview = currentUserId === review.uid;
    const helpfulUids = review.helpfulUids ?? [];
    const unhelpfulUids = review.unhelpfulUids ?? [];
    const replyHelpfulUids = review.replyHelpfulUids ?? [];
    const replyUnhelpfulUids = review.replyUnhelpfulUids ?? [];

    const currentVote = currentUserId
        ? helpfulUids.includes(currentUserId) ? 'helpful'
        : unhelpfulUids.includes(currentUserId) ? 'unhelpful'
        : null : null;

    const currentReplyVote = currentUserId
        ? replyHelpfulUids.includes(currentUserId) ? 'helpful'
        : replyUnhelpfulUids.includes(currentUserId) ? 'unhelpful'
        : null : null;

    const [isReplying, setIsReplying] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [isEditingReply, setIsEditingReply] = useState(false);
    const [editReplyText, setEditReplyText] = useState(review.reply || '');

    useEffect(() => { setEditReplyText(review.reply || ''); }, [review.reply]);

    const getFormattedDate = formatDate;

    return (
        <div id={`review-${review.id}`} className={cn("border-b py-6 scroll-mt-24", isOwnReview && "bg-ink/5 -mx-4 px-4 rounded-sm")}>
            {isOwnReview && <p className="text-xs font-medium text-link mb-2">Your Review</p>}
            <div className="flex items-center mb-2">
                {/* null: the reviewer's picture is switched off. '' is on
                    with no picture, which draws their initial. */}
                {!isDeleted && resolvedAvatarUrl !== null && (
                    <UserAvatar className="h-8 w-8 mr-3" src={resolvedAvatarUrl} seed={review.uid} name={resolvedUsername ?? review.username} />
                )}
                <div>
                    {isDeleted ? (
                        <em className="font-semibold text-muted-foreground italic">Deleted User</em>
                    ) : (
                        <span className="font-semibold">{resolvedUsername ?? review.username}</span>
                    )}
                    <p className="text-xs text-muted-foreground">{getFormattedDate(review.createdAt)}</p>
                </div>
                <div className="flex ml-auto items-center gap-1">
                    <StarRating rating={review.rating} />
                </div>
            </div>
            <p className="text-muted-foreground">{review.text}</p>

            <div className="flex items-center gap-3 mt-4 text-sm flex-wrap">
                <span className="text-muted-foreground text-xs">Helpful?</span>
                <Button
                    variant={!isProductDeleted && currentVote === 'helpful' ? 'secondary' : 'ghost'}
                    size="sm" className="gap-1.5 h-7 px-2"
                    onClick={() => !isProductDeleted && !isOwnReview && isAuthenticated && onVote(review.id, 'helpful')}
                    disabled={isProductDeleted || isOwnReview || !isAuthenticated}
                    aria-label={`Mark this review as helpful (${helpfulUids.length} so far)`}
                >
                    <ThumbsUp className="h-3.5 w-3.5" aria-hidden /> {helpfulUids.length}
                </Button>
                <Button
                    variant={!isProductDeleted && currentVote === 'unhelpful' ? 'secondary' : 'ghost'}
                    size="sm" className="gap-1.5 h-7 px-2"
                    onClick={() => !isProductDeleted && !isOwnReview && isAuthenticated && onVote(review.id, 'unhelpful')}
                    disabled={isProductDeleted || isOwnReview || !isAuthenticated}
                    aria-label={`Mark this review as unhelpful (${unhelpfulUids.length} so far)`}
                >
                    <ThumbsDown className="h-3.5 w-3.5" aria-hidden /> {unhelpfulUids.length}
                </Button>
                {!isProductDeleted && isMerchant && !review.reply && isAuthenticated && (
                    <Button variant="outline" size="sm" className="gap-1.5 h-7 px-2 coarse:h-10 ml-auto" onClick={() => setIsReplying(!isReplying)}>
                        <MessageSquare className="h-3.5 w-3.5" /> Reply
                    </Button>
                )}
            </div>

            {/* Reply form */}
            {isReplying && (
                <div className="mt-4 ml-6 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Reply</span>
                        <span className={`text-xs tabular-nums ${replyText.length > 450 ? 'text-destructive' : 'text-muted-foreground'}`}>{replyText.length}/500</span>
                    </div>
                    <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value.slice(0, 500))} placeholder={`Reply to ${review.username}...`} rows={3} maxLength={500} />
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setIsReplying(false); setReplyText(''); }}>Cancel</Button>
                        <Button size="sm" onClick={() => { onReply(review.id, replyText); setIsReplying(false); setReplyText(''); }} disabled={!replyText.trim()}>Submit reply</Button>
                    </div>
                </div>
            )}

            {/* Seller reply */}
            {review.reply && (
                <div className="mt-4 ml-6 border-l-2 border-link/20 pl-4">
                    <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm">Seller response</span>
                                {review.replyAt && <span className="text-xs text-muted-foreground">{getFormattedDate(review.replyAt)}</span>}
                            </div>
                            {isMerchant && isAuthenticated && !isProductDeleted && (
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" className="h-6 px-2 coarse:h-10 text-xs" onClick={() => setIsEditingReply(true)}>Edit</Button>
                                    <ConfirmDialog
                                        trigger={<Button variant="ghost" size="sm" className="h-6 px-2 coarse:h-10 text-xs text-destructive hover:text-destructive">Delete</Button>}
                                        title="Delete your reply?"
                                        description="Your reply is removed from this review for good."
                                        confirmLabel="Delete reply"
                                        cancelLabel="Keep reply"
                                        onConfirm={() => onDeleteReply(review.id)}
                                    />
                                </div>
                            )}
                        </div>
                        {isEditingReply ? (
                            <div className="space-y-2">
                                <div className="flex justify-end">
                                    <span className={`text-xs tabular-nums ${editReplyText.length > 450 ? 'text-destructive' : 'text-muted-foreground'}`}>{editReplyText.length}/500</span>
                                </div>
                                <Textarea value={editReplyText} onChange={(e) => setEditReplyText(e.target.value.slice(0, 500))} rows={3} maxLength={500} />
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" size="sm" onClick={() => setIsEditingReply(false)}>Cancel</Button>
                                    <Button size="sm" onClick={() => { onEditReply(review.id, editReplyText); setIsEditingReply(false); }} disabled={!editReplyText.trim()}>Save</Button>
                                </div>
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-sm">{review.reply}</p>
                        )}
                    </div>
                    {/* Reply helpful/unhelpful — hidden for deleted products */}
                    {!isProductDeleted && (
                    <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-muted-foreground">Reply helpful?</span>
                        <Button
                            variant={currentReplyVote === 'helpful' ? 'secondary' : 'ghost'}
                            size="sm" className="gap-1.5 h-6 px-2 text-xs"
                            onClick={() => !isMerchant && isAuthenticated && onVote(review.id, 'helpful', true)}
                            disabled={isMerchant || !isAuthenticated}
                        >
                            <ThumbsUp className="h-3 w-3" /> {replyHelpfulUids.length}
                        </Button>
                        <Button
                            variant={currentReplyVote === 'unhelpful' ? 'secondary' : 'ghost'}
                            size="sm" className="gap-1.5 h-6 px-2 text-xs"
                            onClick={() => !isMerchant && isAuthenticated && onVote(review.id, 'unhelpful', true)}
                            disabled={isMerchant || !isAuthenticated}
                        >
                            <ThumbsDown className="h-3 w-3" /> {replyUnhelpfulUids.length}
                        </Button>
                    </div>
                    )}
                </div>
            )}
        </div>
    );
};

type GuestReviewItemProps = {
    review: GuestReview;
    formatDate: (date: any) => string;
    isMerchant: boolean;
    isAuthenticated: boolean;
    onReply: (reviewId: string, replyText: string) => void;
    onEditReply: (reviewId: string, replyText: string) => void;
    onDeleteReply: (reviewId: string) => Promise<void>;
    onVote: (reviewId: string, voteType: 'helpful' | 'unhelpful') => void;
    currentUserId?: string;
    isProductDeleted?: boolean;
};

const GuestReviewItem = ({ review, formatDate, isMerchant, isAuthenticated, onReply, onEditReply, onDeleteReply, onVote, currentUserId, isProductDeleted = false }: GuestReviewItemProps) => {
    const helpfulUids = review.helpfulUids ?? [];
    const unhelpfulUids = review.unhelpfulUids ?? [];
    const currentVote = currentUserId
        ? helpfulUids.includes(currentUserId) ? 'helpful'
        : unhelpfulUids.includes(currentUserId) ? 'unhelpful'
        : null : null;

    const [isReplying, setIsReplying] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [isEditingReply, setIsEditingReply] = useState(false);
    const [editReplyText, setEditReplyText] = useState(review.reply || '');

    useEffect(() => { setEditReplyText(review.reply || ''); }, [review.reply]);

    const getFormattedDate = formatDate;

    return (
        <div className="border-b py-6">
            <div className="flex items-center mb-2">
                <div className="mr-3">
                    <span className="font-semibold">Guest User</span>
                    <p className="text-xs text-muted-foreground">{getFormattedDate(review.createdAt)}</p>
                </div>
                <div className="flex ml-auto items-center gap-1">
                    <StarRating rating={review.rating} />
                </div>
            </div>
            {review.body && <p className="text-muted-foreground">{review.body}</p>}

            <div className="flex items-center gap-3 mt-4 text-sm flex-wrap">
                <span className="text-muted-foreground text-xs">Helpful?</span>
                <Button
                    variant={!isProductDeleted && currentVote === 'helpful' ? 'secondary' : 'ghost'}
                    size="sm" className="gap-1.5 h-7 px-2"
                    onClick={() => !isProductDeleted && isAuthenticated && onVote(review.id, 'helpful')}
                    disabled={isProductDeleted || !isAuthenticated}
                    aria-label={`Mark this review as helpful (${helpfulUids.length} so far)`}
                >
                    <ThumbsUp className="h-3.5 w-3.5" aria-hidden /> {helpfulUids.length}
                </Button>
                <Button
                    variant={!isProductDeleted && currentVote === 'unhelpful' ? 'secondary' : 'ghost'}
                    size="sm" className="gap-1.5 h-7 px-2"
                    onClick={() => !isProductDeleted && isAuthenticated && onVote(review.id, 'unhelpful')}
                    disabled={isProductDeleted || !isAuthenticated}
                    aria-label={`Mark this review as unhelpful (${unhelpfulUids.length} so far)`}
                >
                    <ThumbsDown className="h-3.5 w-3.5" aria-hidden /> {unhelpfulUids.length}
                </Button>
                {!isProductDeleted && isMerchant && !review.reply && isAuthenticated && (
                    <Button variant="outline" size="sm" className="gap-1.5 h-7 px-2 coarse:h-10 ml-auto" onClick={() => setIsReplying(!isReplying)}>
                        <MessageSquare className="h-3.5 w-3.5" /> Reply
                    </Button>
                )}
            </div>

            {/* Reply compose box */}
            {isReplying && (
                <div className="mt-4 ml-6 space-y-2">
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">Reply</span>
                        <span className={`text-xs tabular-nums ${replyText.length > 450 ? 'text-destructive' : 'text-muted-foreground'}`}>{replyText.length}/500</span>
                    </div>
                    <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value.slice(0, 500))} placeholder="Reply to Guest User..." rows={3} maxLength={500} />
                    <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setIsReplying(false); setReplyText(''); }}>Cancel</Button>
                        <Button size="sm" onClick={() => { onReply(review.id, replyText); setIsReplying(false); setReplyText(''); }} disabled={!replyText.trim()}>Submit reply</Button>
                    </div>
                </div>
            )}

            {/* Seller reply display */}
            {review.reply && (
                <div className="mt-4 ml-6 border-l-2 border-link/20 pl-4">
                    <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm">Seller response</span>
                                {review.replyAt && <span className="text-xs text-muted-foreground">{getFormattedDate(review.replyAt)}</span>}
                            </div>
                            {isMerchant && isAuthenticated && !isProductDeleted && (
                                <div className="flex gap-1">
                                    <Button variant="ghost" size="sm" className="h-6 px-2 coarse:h-10 text-xs" onClick={() => setIsEditingReply(true)}>Edit</Button>
                                    <ConfirmDialog
                                        trigger={<Button variant="ghost" size="sm" className="h-6 px-2 coarse:h-10 text-xs text-destructive hover:text-destructive">Delete</Button>}
                                        title="Delete your reply?"
                                        description="Your reply is removed from this review for good."
                                        confirmLabel="Delete reply"
                                        cancelLabel="Keep reply"
                                        onConfirm={() => onDeleteReply(review.id)}
                                    />
                                </div>
                            )}
                        </div>
                        {isEditingReply ? (
                            <div className="space-y-2">
                                <Textarea value={editReplyText} onChange={(e) => setEditReplyText(e.target.value.slice(0, 500))} rows={3} maxLength={500} />
                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" size="sm" onClick={() => { setIsEditingReply(false); setEditReplyText(review.reply || ''); }}>Cancel</Button>
                                    <Button size="sm" onClick={() => { onEditReply(review.id, editReplyText); setIsEditingReply(false); }} disabled={!editReplyText.trim()}>Save</Button>
                                </div>
                            </div>
                        ) : (
                            <p className="text-muted-foreground text-sm">{review.reply}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const FaqItem = ({ question, answer }: { question: string; answer: string }) => {
    const [open, setOpen] = useState(false);
    // Whether the visitor has opened this question yet. Until then the answer
    // is MOUNTED but hidden, so it is in the HTML the server sends (a closed
    // Radix panel renders nothing, which left a crawler the questions without
    // their answers) while still looking closed. From the first toggle on,
    // Radix mounts and unmounts it as usual, which is what its open and close
    // animations run on.
    //
    // Hidden with the `hidden` attribute, which overrides the one Radix sets
    // (forceMount makes Radix treat the panel as present, so it never hides
    // it). Not with a class: CollapsibleContent does not pass `className` on.
    const [touched, setTouched] = useState(false);
    return (
        <Collapsible open={open} onOpenChange={(next) => { setTouched(true); setOpen(next); }}>
            <div className="border rounded-lg mb-2 overflow-hidden bg-card text-card-foreground shadow-sm">
                <CollapsibleTrigger asChild>
                    <button
                        type="button"
                        className="flex justify-between items-center w-full text-left gap-4 px-4 py-3 hover:bg-muted/50 transition-colors"
                    >
                        {/* min-w-0 with anywhere-wrapping: the trigger is a flex row and a
                            long unbroken question would otherwise refuse to shrink, pushing
                            the chevron out of the card instead of wrapping. */}
                        <span className="font-seller-heading font-semibold min-w-0 [overflow-wrap:anywhere]">{question}</span>
                        <ChevronDownIcon className={cn("h-5 w-5 shrink-0 transition-transform duration-300", open && "rotate-180")} />
                    </button>
                </CollapsibleTrigger>
                <CollapsibleContent
                    forceMount={touched ? undefined : true}
                    hidden={touched ? undefined : !open}
                    className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up"
                >
                    <div className="px-4 pb-4 pt-3 border-t border-border/60">
                        <div className="font-seller-body text-muted-foreground text-sm [overflow-wrap:anywhere] prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer) }} />
                    </div>
                </CollapsibleContent>
            </div>
        </Collapsible>
    );
};

function ProductPage({ loadingFallback, seed, sellerTierHidden }: {
  loadingFallback: React.ReactNode;
  seed: ProductPageSeed | null;
  sellerTierHidden: boolean;
}) {
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const { addToCart } = useCart();
  const { saved, toggleSave } = useWishlist();
  const {
    formatUserDate, formatCurrency, formatBaseAmount, isBaseCurrency, currencySymbol,
    toBaseAmount, toDisplayAmount, displayCurrency,
  } = useTheme();
  const { setOverride: setDesignOverride, setOverridePending: setDesignOverridePending } = useProductDesignOverride();
  // Whether prices on this page exclude a VAT this visitor will actually be
  // charged. The same test VatNotice applies per price; read here as well because
  // the subscription-management copy states a charge in prose rather than as a
  // price beside a qualifier.
  const viewerGeoCountry = useGeoCountry();
  const euVatBuyer = showsVatNotice(viewerGeoCountry);
  // Year levels in the visitor's own school system (src/lib/category-fields.ts),
  // from the same server-resolved country, so the first paint already has them.
  const schoolSystem = schoolSystemForCountry(viewerGeoCountry);
  const { isAuthenticated, user, authUser, isUserLoading } = useAuth();
  const { firestore } = useFirebase();
  const { toast } = useToast();
  // Just this product. It used to be looked up in a provider-wide map holding
  // the entire catalog, which meant a product page could not render until every
  // other product in the marketplace had been downloaded.
  const productIdsToLoad = useMemo(() => [params.id as string], [params.id]);
  // The server's read of this product, its seller and its reviews, when it
  // could hand them over (a live product; see page.tsx). Everything below that
  // waits on a listener starts from it instead, so the first render, on the
  // server and in the browser alike, is the finished page.
  const pageSeed = seed && seed.id === params.id ? seed : null;
  const productSeed = useMemo(
    () => (pageSeed ? new Map([[pageSeed.id, normalizeProductDoc(pageSeed.id, pageSeed.data)]]) : undefined),
    [pageSeed],
  );
  const { products, isLoaded: productsLoaded } = useProductsByIds(productIdsToLoad, { withDiscounts: true, seed: productSeed });
  const { isOwned } = useOwnedProducts();
  // A bundle's products, from the server's copy first (src/lib/product-bundles.ts).
  const bundleView = useBundleView(products.get(params.id as string) ?? null, pageSeed?.bundleItems);

  // Built from the server's copy on the first render, as the listener effect
  // below would build it for a viewer nobody knows yet: no owner, nobody signed
  // in. The effect then runs right after hydration and folds in who is looking.
  // Starting from null instead is what painted "Product not found." for a frame
  // between the listener landing and that effect catching up.
  const [product, setProduct] = useState<Product | null>(() => {
    const seeded = products.get(params.id as string);
    return seeded ? withViewerState(seeded, { owned: false, uid: authUser?.uid }) : null;
  });
  useDocumentTitle(product?.name);
  // False until the page has hydrated. Dates read it (see getFormattedDateOnly)
  // because they are formatted in the visitor's timezone, which the server
  // cannot know.
  const hydrated = useHydrated();

  // Buyer's own subscription to THIS product, so billing can be managed from
  // the page the product lives on rather than only from the dashboard.
  const productSubscription = useProductSubscription(product?.id);
  const [isCancelingSubscription, setIsCancelingSubscription] = useState(false);
  // Mount-time reference for "has the access lapsed yet". Held in state rather
  // than read during render so the server-rendered HTML and the hydrated tree
  // agree on which side of the boundary a canceled subscription sits, then
  // advanced by the timer below when that boundary passes.
  const [nowMs, setNowMs] = useState(() => Date.now());
  const subscriptionAccess = productSubscription
    ? subscriptionAccessState(productSubscription, nowMs)
    : null;
  const canCancelSubscription = productSubscription?.status === 'active'
    || productSubscription?.status === 'past_due';
  const subscriptionNextChargeMs = subscriptionMs(productSubscription?.nextChargeAt);

  // Re-render exactly when a canceled subscription's access boundary passes.
  // OwnedProductsProvider runs the same timer to drop the entitlement, so
  // without this the CTA would flip to Subscribe while the line beneath it still
  // read "you have access until X" off a clock frozen at mount.
  const accessUntilMs = subscriptionMs(productSubscription?.accessUntil);
  useEffect(() => {
    if (accessUntilMs == null || accessUntilMs <= nowMs) return;
    // setTimeout saturates past ~24.8 days and would fire immediately, so cap
    // the wait and let the next tick re-arm for anything further out.
    const delay = Math.min(accessUntilMs - nowMs + 1000, 6 * 60 * 60 * 1000);
    const timer = setTimeout(() => setNowMs(Date.now()), delay);
    return () => clearTimeout(timer);
  }, [accessUntilMs, nowMs]);

  // Cancel from the product page. The callable returns the instant access
  // expires (the boundary that would have been the next billing date) so the
  // buyer is told the date immediately; the useProductSubscription listener
  // then repaints the page from the server's write, so nothing is flipped
  // optimistically here.
  const handleCancelSubscription = async () => {
    if (!productSubscription) return;
    setIsCancelingSubscription(true);
    try {
      const call = httpsCallable<{ subscriptionId: string }, { accessUntil?: number | null }>(
        getFunctions(), 'cancelSubscription',
      );
      const { data } = await call({ subscriptionId: productSubscription.id });
      const accessUntil = Number(data?.accessUntil) || null;
      toast({
        title: 'Subscription cancelled',
        description: accessUntil
          ? `No further charges will be made. You have access until ${formatUserDate(accessUntil)}.`
          : 'No further charges will be made.',
      });
    } catch (e: unknown) {
      const message = userErrorMessage(e, 'Try again in a moment.');
      toast({ variant: 'destructive', title: 'Could not cancel the subscription', description: message });
    } finally {
      setIsCancelingSubscription(false);
    }
  };

  // Schedule (or undo) a move to a different recurring tier. Nothing is charged
  // here: the server parks the change and the renewal scheduler applies it at
  // the boundary the buyer has already paid through. Passing the tier the
  // subscription is already on is what clears a scheduled switch, so the same
  // callable serves both the "Switch" and "Keep current tier" buttons.
  const [isChangingTier, setIsChangingTier] = useState(false);
  const handleTierChange = async (tierId: number | string, tierLabel: string) => {
    if (!productSubscription) return;
    setIsChangingTier(true);
    try {
      const call = httpsCallable<
        { subscriptionId: string; tierId: string },
        { pending: { tierName: string; priceAud: number } | null; effectiveAt: number | null }
      >(getFunctions(), 'setSubscriptionTierChange');
      const { data } = await call({
        subscriptionId: productSubscription.id,
        tierId: String(tierId),
      });
      const effectiveAt = Number(data?.effectiveAt) || null;
      toast(data?.pending ? {
        title: 'Tier change scheduled',
        description: effectiveAt
          ? `You stay on your current tier until ${formatUserDate(effectiveAt)}, then move to ${tierLabel}.`
          : `You move to ${tierLabel} at your next payment.`,
      } : {
        title: 'Tier change cancelled',
        description: 'Your subscription carries on unchanged.',
      });
    } catch (e: unknown) {
      const message = userErrorMessage(e, 'Try again in a moment.');
      toast({ variant: 'destructive', title: 'Could not change the tier', description: message });
    } finally {
      setIsChangingTier(false);
    }
  };

  // The seller's users doc as the server read it, when it is this product's
  // seller. The seller fields below start from it, so the seller row, the badge
  // and the brand are on the first render, and the read further down refreshes
  // them through the same sellerSummaryFromDoc.
  const sellerSeed: SellerSummary | null =
    pageSeed?.seller && product && pageSeed.seller.sellerId === product.sellerId ? pageSeed.seller : null;
  /** The seller the server rendered the row for. See the seller read. */
  const sellerSeedForRef = useRef<string | null>(sellerSeed?.sellerId ?? null);
  const [sellerAvatarUrl, setSellerAvatarUrl] = useState<string>(() => (sellerSeed?.exists ? sellerSeed.avatarUrl : ''));
  // Seller's badge tier, read from the users doc alongside the avatar. Products
  // carry no denormalized tier, and a founding grant can land long after the
  // listing was written, so the live doc is the only accurate source.
  const [sellerTier, setSellerTier] = useState<SellerTierName | null>(
    () => (sellerSeed?.exists ? (sellerSeed.tier as SellerTierName | null) : null),
  );
  const [showSellerAvatar, setShowSellerAvatar] = useState<boolean>(() => (sellerSeed?.exists ? sellerSeed.showAvatar : false));
  // The seller's storefront brand (users/{uid}.design), read alongside their
  // avatar below. Only consulted when THIS product sets no design of its own.
  const [sellerBrand, setSellerBrand] = useState<PageDesign | null>(() => sellerSeed?.design ?? null);
  // The seller whose users doc read has SETTLED (found, missing or failed), so
  // `sellerBrand` is known to be that seller's answer rather than "not yet".
  // An id rather than a flag: a different seller reads as unsettled on the very
  // render it appears, with no reset effect to lag a frame behind it.
  const [sellerBrandSettledFor, setSellerBrandSettledFor] = useState<string | null>(() => sellerSeed?.sellerId ?? null);
  // Live seller username, read directly from the users doc. Products' denormalized
  // `seller` field gets rewritten by the updateUsername fan-out, but until that
  // sweep lands it can be stale — fall back to the product field, prefer this.
  const [liveSellerName, setLiveSellerName] = useState<string>(() => sellerSeed?.username ?? '');
  // The newest REVIEW_PAGE_SIZE reviews of each kind, as the listeners below
  // deliver them. The lists the page renders (`reviews`, `guestReviews`) are
  // these plus the viewer's own review, which is read by id because it need not
  // be among the newest. Both start from the server's read of the same windows
  // when there is one, which is what puts the reviews in the served HTML.
  const reviewSeed = pageSeed?.reviews ?? null;
  // The product the server's reviews belong to, for the listeners: they hold
  // the server's copy until the server itself answers (onSnapshotFromServer).
  const reviewSeedForRef = useRef<string | null>(reviewSeed && pageSeed ? pageSeed.id : null);
  const [reviewPage, setReviewPage] = useState<Review[]>(() => (reviewSeed ? reviewSeed.reviews.map(reviewFromSeed) : []));
  const [reviewPageLimit, setReviewPageLimit] = useState(REVIEW_PAGE_SIZE);
  const [reviewsLoaded, setReviewsLoaded] = useState(reviewSeed !== null);
  const [guestReviewPage, setGuestReviewPage] = useState<GuestReview[]>(
    () => (reviewSeed ? reviewSeed.guestReviews.map(guestReviewFromSeed) : []),
  );
  const [guestReviewsLoaded, setGuestReviewsLoaded] = useState(reviewSeed !== null);
  // Tagged with the product (and account) they were read for, so a review left
  // over from the previous product or a signed-out account is never merged in.
  const [ownReviewDoc, setOwnReviewDoc] = useState<{ productId: string; uid: string; review: Review | null } | null>(null);
  const [ownGuestReviewDoc, setOwnGuestReviewDoc] = useState<{ productId: string; review: GuestReview | null } | null>(null);
  // Whether the viewer's own review has been looked up for the product on
  // screen. Signed out, there is no account review to find.
  const ownReviewSettled = !product?.id || !authUser?.uid
    || (ownReviewDoc?.productId === product.id && ownReviewDoc.uid === authUser.uid);
  const ownGuestReviewSettled = !product?.id || ownGuestReviewDoc?.productId === product.id;
  const [reviewsToShow, setReviewsToShow] = useState(REVIEWS_SHOWN_ON_ARRIVAL);
  const [isFullScreen, setIsFullScreen] = useState(false);
  // Whether the gallery's CURRENT image has loaded, which is what its spinner
  // and fade-in wait on when the buyer moves to another image. True for the
  // image the page opens on: that one is in the server's HTML, and the browser
  // starts painting it the moment it arrives, while `onLoad` cannot fire until
  // the page has hydrated. Starting false hid an image that had already loaded
  // behind a spinner for as long as hydration took.
  const [mediaLoaded, setMediaLoaded] = useState(true);
  const [buyerRefundDispute, setBuyerRefundDispute] = useState<{ id: string; status: string } | null>(null);
  const [buyerReportDispute, setBuyerReportDispute] = useState<{ status: string } | null>(null);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  // Pay-what-you-want. The buyer types in the currency the page is priced in for
  // them, but the cart, the seller's floor (pwywMinPrice), PRICE_MIN_AUD and
  // PRICE_MAX_AUD are all AUD, and AUD is what the card is charged. So the typed
  // string is converted once, here, and every comparison and the cart line use
  // the AUD figure. Before this the raw number was compared against AUD bounds
  // and added to the cart as-is, which quoted a buyer a minimum in their own
  // currency and then charged the number they typed as AUD.
  //
  // Seeded on the first render when the product is already known (the server
  // rendered it), with exactly what the seed effect below would put there on
  // mount, so the field is filled in the served HTML and that effect finds
  // nothing to change.
  const [initialPwyw] = useState(() => (
    product ? { productId: product.id, seeded: pwywSeedFor(product, toDisplayAmount, displayCurrency) } : null
  ));
  const [pwywPrice, setPwywPrice] = useState(() => initialPwyw?.seeded ?? '');
  const pwywTyped = parseFloat(pwywPrice);
  const pwywAud = Number.isFinite(pwywTyped)
    // Rounded to the cent so the amount validated is exactly the amount added to
    // the cart, and neither can drift a fraction past a bound the other cleared.
    ? Math.round(toBaseAmount(pwywTyped) * 100) / 100
    : NaN;
  // An AUD amount in both currencies, for sentences (toasts) that cannot carry a
  // separate footnote element.
  const fmtWithBase = (aud: number): string =>
    isBaseCurrency ? formatCurrency(aud) : `${formatCurrency(aud)} (${formatBaseAmount(aud)})`;
  // A pay-what-you-want minimum as the buyer reads it: exactly the amount the
  // field is seeded with, in their currency. formatCurrency alone rounds to
  // nearest and could name a unit under it (see pwywMinimumLabelAud).
  const pwywMinimumShown = (floorAud: number): string =>
    formatCurrency(pwywMinimumLabelAud(floorAud, toDisplayAmount, toBaseAmount, displayCurrency));
  // Seeded on the first render when the product is already known, the way the
  // listener effect below seeds it, so the served HTML has a tier ticked.
  const [selectedTierId, setSelectedTierId] = useState<number | null>(() => (product ? initialTierFor(product) : null));
  /** Product id whose tier selection has already been seeded. See the effect. */
  const seededTierForRef = useRef<string | null>(product ? product.id : null);
  /**
   * True while the tick above came from the first render, which cannot know
   * who is looking. The listener effect settles it the first time it runs.
   */
  const tierSeedProvisionalRef = useRef(product !== null);
  // The reviewers the server resolved (see getProductReviewSeed); the fetch
  // below re-reads them, and resolves everyone else, as before.
  const [initialReviewers] = useState(() => reviewerMapsFromSeed(reviewSeed));
  const [reviewerAvatarUrls, setReviewerAvatarUrls] = useState<Record<string, string | null>>(initialReviewers.avatarUrls);
  const [reviewerExists, setReviewerExists] = useState<Record<string, boolean>>(initialReviewers.exists);
  // Reviewer display names, read from the LIVE user doc rather than the copy
  // denormalised onto the review. See the fetch below for why.
  const [reviewerUsernames, setReviewerUsernames] = useState<Record<string, string>>(initialReviewers.usernames);
  const [purchasePaymentIntentId, setPurchasePaymentIntentId] = useState<string | null>(null);
  // What the buyer paid, from their entitlement. `undefined` means "not known
  // yet / not recorded", which refund eligibility must not read as zero.
  const [purchasePricePaid, setPurchasePricePaid] = useState<number | undefined>(undefined);
  // The purchase doc that entitlement was last charged on, tagged with the
  // product it was read for, for the refund policy recorded on its line. See
  // refundActionAvailable.
  const [purchaseRecord, setPurchaseRecord] = useState<{ productId: string; data: Record<string, unknown> } | null>(null);
  const [guestAccessOpen, setGuestAccessOpen] = useState(false);
  const [guestAccessEmail, setGuestAccessEmail] = useState('');
  const [guestLinkSent, setGuestLinkSent] = useState(false);
  const [isSendingGuestLink, setIsSendingGuestLink] = useState(false);
  const [guestAccessError, setGuestAccessError] = useState<string | null>(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  // The rating a star in a review-request email asked for: the dialog opens on
  // it (src/lib/review-link.ts). Null for every other way the dialog opens.
  const [reviewInitialRating, setReviewInitialRating] = useState<number | null>(null);
  // Guests can't subscribe to `refunds` via Firestore (auth required). We
  // mirror the state locally for instant feedback on submit, then poll the
  // server-side `getGuestRefundStatus` callable so Accepted/Denied surface
  // without the guest having to refresh or open the email.
  const [guestRefundStatus, setGuestRefundStatus] = useState<string | null>(null);
  // A denial made by supa.place is the end of the internal process; a denial made
  // by the SELLER can still be escalated to us. See canAppealRefund.
  const [guestRefundResolvedByAdmin, setGuestRefundResolvedByAdmin] = useState(false);
  const [guestRefundRequestedAtMs, setGuestRefundRequestedAtMs] = useState<number | null>(null);

  // When the product card links here with ?action=download or ?action=review,
  // open the matching dialog and strip the param so it doesn't re-trigger.
  useEffect(() => {
    const action = searchParams.get('action');
    if (action !== 'download' && action !== 'review') return;
    // ReviewDialog seeds its rating/text state from existingReview on mount
    // and ignores subsequent prop changes, so opening before the reviews
    // subcollections resolve would mount it with a blank form even when
    // the user already has a review. Hold off until both listeners report
    // back, and the by-id reads of the viewer's own review with them (it need
    // not be among the newest the listeners hold), so existingReview is
    // correct on first render.
    if (action === 'review' && !(reviewsLoaded && guestReviewsLoaded && ownReviewSettled && ownGuestReviewSettled)) return;
    if (action === 'review') {
      // A review-request email's star (src/lib/review-link.ts) is often opened
      // signed out: a visitor with no account session and no guest order in
      // this tab signs in first and comes back to this same link.
      const step = reviewLinkStep({
        authSettled: !isUserLoading,
        signedIn: !!authUser,
        guestOrderInTab: tabHasGuestOrder(params.id as string),
        ratingParam: searchParams.get('rating'),
      });
      if (step.kind === 'wait') return;
      if (step.kind === 'signIn') {
        router.replace(withRedirect('/login', `${pathname}?${searchParams.toString()}`));
        return;
      }
      if (step.initialRating !== null) setReviewInitialRating(step.initialRating);
    }
    if (action === 'download') setDownloadOpen(true);
    if (action === 'review') setReviewOpen(true);
    const next = new URLSearchParams(searchParams.toString());
    next.delete('action');
    next.delete('rating');
    const qs = next.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    // `params.id` is the route's product, fixed for this page.
  }, [searchParams, router, pathname, reviewsLoaded, guestReviewsLoaded, ownReviewSettled, ownGuestReviewSettled, isUserLoading, authUser?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSendGuestLink = async () => {
    if (!guestAccessEmail.trim() || !product) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(guestAccessEmail.trim())) {
      setGuestAccessError('Please enter a valid email address.');
      return;
    }
    setIsSendingGuestLink(true);
    setGuestAccessError(null);
    try {
      const fn = httpsCallable(getFunctions(), 'sendGuestMagicLink');
      await fn({ email: guestAccessEmail.trim(), productId: product.id });
      setGuestLinkSent(true);
    } catch {
      setGuestAccessError("We couldn't send the link. Check your connection and try again.");
    } finally {
      setIsSendingGuestLink(false);
    }
  };

  const isGuestOwned = (id: string): boolean => {
    if (typeof window === 'undefined') return false;
    try {
      const stored: string[] = JSON.parse(sessionStorage.getItem('guestOwnedProducts') || '[]');
      return stored.includes(id);
    } catch { return false; }
  };

  const getGuestOrderInfo = (id: string): { orderId: string; email: string } | null => {
    if (typeof window === 'undefined') return null;
    try {
      const map = JSON.parse(sessionStorage.getItem('guestOrderMap') || '{}');
      return map[id] || null;
    } catch { return null; }
  };

  // Build the guest receipt URL with the stashed client_secret when
  // we have it. The receipt page calls /api/guest/get-receipt, which requires
  // either the original checkout redirect's client_secret or — for users who
  // came in via the email magic-link flow — a server-issued cookie that the
  // current architecture doesn't surface here. Falling back to the no-secret
  // URL would render the email-fallback placeholder, so we only construct
  // the link when a secret is on hand and skip the dropdown item otherwise.
  const getGuestReceiptHref = (orderId: string): string | null => {
    if (typeof window === 'undefined') return null;
    try {
      const secrets = JSON.parse(sessionStorage.getItem('guestPiSecrets') || '{}');
      const secret = secrets[orderId];
      if (!secret) return null;
      const params = new URLSearchParams({
        payment_intent: orderId,
        redirect_status: 'succeeded',
        payment_intent_client_secret: secret,
      });
      return `/receipt?${params.toString()}`;
    } catch { return null; }
  };

  useEffect(() => {
    if (!productsLoaded) return;
    const currentId = params.id as string;
    const productData = products.get(currentId);

    if (productData) {
        // Ownership comes from authoritative sources only:
        //   - `isOwned()` reads users/{uid}/ownedProducts via OwnedProductsProvider,
        //     which is mounted at the app root so it's pre-loaded on navigation.
        //   - `isGuestOwned()` reads sessionStorage written after a successful
        //     guest checkout/access flow.
        // The previous `?purchased=true` / `?merchant=true` / `?affiliate=true`
        // URL params let any visitor flip the UI into "owned" / "seller" /
        // "affiliate" state — no real download bypass (server still gates), but
        // it showed buttons that 403 and exposed seller-management actions to
        // non-sellers. Whether the viewer is the seller, and the product-level
        // refund answer, are folded in by withViewerState too.
        const mutableProductData = withViewerState(productData, {
            owned: isOwned(currentId) || isGuestOwned(currentId),
            uid: authUser?.uid,
        });

        // Seed the tier selection ONCE per product, never on later snapshots.
        //
        // This effect re-runs whenever the products map changes identity, which
        // it does every 15s: the provider rebuilds it on a ticker so a discount
        // window can go live without a reload. Seeding unconditionally therefore
        // overwrote whatever tier the buyer had picked, roughly every 15
        // seconds, with the default. The ref pins the seed to the first
        // resolution of each product id.
        //
        // The seed itself: a buyer who owns nothing needs a tier in play from
        // the first paint — the price, the CTA and the cadence all read from it
        // — so the first tier is ticked for them. An OWNER starts with nothing
        // ticked: their own tier is not a checkbox, and pre-ticking one of the
        // others would present a tier they never chose as the one they are
        // about to act on. (Ownership can resolve after this; the ownedProducts
        // effect clears the tick when it does.)
        //
        // A product the server rendered was seeded on the first render, before
        // this browser could say who is looking, so this first run settles it:
        // a guest whose purchase is recorded in this session owns the product,
        // and an owner starts with nothing ticked. It runs right after
        // hydration, before anything can have been clicked.
        if (seededTierForRef.current !== currentId) {
            seededTierForRef.current = currentId;
            tierSeedProvisionalRef.current = false;
            setSelectedTierId(initialTierFor(mutableProductData));
        } else if (tierSeedProvisionalRef.current) {
            tierSeedProvisionalRef.current = false;
            if (mutableProductData.isPurchased) setSelectedTierId(null);
        }

        // Which tier the buyer holds is not on the listing, so this rebuild
        // cannot produce it: only the ownedProducts read further down sets
        // isTieredPurchase and purchasedTierId, and that effect does not re-run
        // on a rebuild because nothing it depends on has changed. Replacing the
        // product outright therefore wiped "Your tier" (and scoped downloads
        // and the refund row to no tier) within 15 seconds of it appearing.
        // Carried across a rebuild of the SAME product, and only while it is
        // still owned, so a refund that ends the entitlement still clears it.
        setProduct(prev => (
            prev?.id === currentId && prev.isTieredPurchase && mutableProductData.isPurchased
                ? { ...mutableProductData, isTieredPurchase: true, purchasedTierId: prev.purchasedTierId }
                : mutableProductData
        ));
    } else {
        setProduct(null);
    }
  }, [params.id, searchParams, products, productsLoaded, authUser?.uid, isOwned]);

  // Seed the pay-what-you-want field with the seller's floor, once per product.
  //
  // It used to be seeded inside the effect above, which re-runs every 15
  // seconds (see the tier seed there), so whatever the buyer had typed was put
  // back to the floor while they were deciding. And it wrote the floor in AUD
  // into a field the page reads in the buyer's display currency, so a buyer
  // paying in yen opened on a few yen, flagged as below the minimum, and one
  // paying in US dollars on half as much again as the floor.
  //
  // The ref remembers what was seeded and for which product. A new product
  // seeds afresh (or clears the field). The same product re-seeds only when the
  // seed itself changes (the rates or the display currency land after the
  // first paint, or the seller moves the floor), and then only while the field
  // still holds the previous seed: an amount the buyer typed is theirs.
  //
  // It starts from what the first render already put in the field for a
  // product the server rendered (`initialPwyw`), so the mount run below finds
  // the same product and the same seed and leaves the field alone.
  const pwywSeedRef = useRef<{ productId: string; seeded: string } | null>(initialPwyw);
  useEffect(() => {
    if (!product) return;
    const seeded = pwywSeedFor(product, toDisplayAmount, displayCurrency);
    const prev = pwywSeedRef.current;
    pwywSeedRef.current = { productId: product.id, seeded };
    if (prev?.productId !== product.id) {
        setPwywPrice(seeded);
        return;
    }
    if (prev.seeded !== seeded) {
        setPwywPrice(current => (current === prev.seeded ? seeded : current));
    }
  }, [product?.id, product?.pricingModel, product?.pwywMinPriceEnabled, product?.pwywMinPrice, toDisplayAmount, displayCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  // An amount the buyer TYPED is kept at what it was worth when the display
  // currency changes under it (a rate landing late, or another currency picked
  // in another tab): restated at the same AUD value, in the same render, so 20
  // typed under A$ is never charged as 20 of the new currency
  // (src/lib/amount-denomination.ts). The seed is left to the effect above,
  // which rounds a minimum so it never reads under the floor.
  useHeldDenomination(
    currentDenomination(displayCurrency, toDisplayAmount),
    () => null,
    (from, to) => setPwywPrice((current) => (
      current.trim() === '' || current === pwywSeedRef.current?.seeded
        ? current
        : String(redenominateAmount(current, from, to, {}))
    )),
  );

  // Publish the seller's appearance override to the app shell while this page
  // is open. ClientLayout reads the two background effects (the accent
  // background in dark mode only; the starfield in dark mode, and in light
  // mode only where the viewer turned it on there) and ThemeProvider reads the
  // accent colour and the gradient's colour and direction. Cleared on unmount and whenever the resolved override changes, so
  // leaving the page restores the buyer's own theme.
  //
  // The product's own design wins; a product that sets none inherits the
  // seller's storefront brand, minus its theme. resolveDesignWithSellerBrand
  // owns that decision and says why the theme is the exception. When neither
  // sets anything the page is plain (sellerPageOverride): no stars, planets or
  // gradient, whatever the buyer's own settings say (owner decision 2026-10-19).
  useEffect(() => {
    setDesignOverride(sellerPageOverride(resolveDesignWithSellerBrand(product?.design, sellerBrand)));
    return () => setDesignOverride(null);
    // Depend on the primitive design fields, not the object identity, so an
    // unrelated product re-render doesn't churn the override. Every field the
    // resolver reads has to be listed: the eslint-disable below means the
    // linter will not notice an omission, and an omitted field does not fail
    // loudly — it simply never takes effect.
    // `sellerBrand` by identity rather than by field: it is set once per
    // sellerId from a one-shot read, so the object is stable after it lands.
  }, [product?.design?.enabled, product?.design?.starfield, product?.design?.accentBackground, product?.design?.accentColor, product?.design?.theme, product?.design?.backgroundColor, product?.design?.gradientColor, product?.design?.gradientDirection, sellerBrand, setDesignOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tell the shell the override above is not KNOWN yet. Until the design lands,
  // the plain page published above stands in for an answer nobody has yet, and
  // reading it as the answer would tear a seller's sky down and paint it again
  // on a client-side move between two of their pages. Before seller pages were
  // plain by default (2026-10-19) the same window painted the viewer's own sky
  // over a seller who had switched it off. src/lib/cosmic-effects.ts holds what
  // was showing instead.
  //
  // Pending until the product has resolved and then, for a product with no
  // design of its own, until the seller's brand read has settled. A product
  // that is not found, has no seller, or carries its own design has nothing
  // further to wait for, so no branch can leave the page effect-less for good.
  // `product` is set by an effect one render after the listener resolves, so
  // "resolved" also needs that state to have caught up with the loaded map;
  // otherwise that one render would read as "not found" and release early.
  const productResolved = productsLoaded
    && (products.has(params.id as string) ? product?.id === params.id : true);
  const designOverridePending = !productResolved
    || (!!product
      && !resolveDesignOverride(product.design)
      && !!product.sellerId
      && !!firestore
      && sellerBrandSettledFor !== product.sellerId);
  useEffect(() => {
    setDesignOverridePending(designOverridePending);
    return () => setDesignOverridePending(false);
  }, [designOverridePending, setDesignOverridePending]);

  // Cross-tab guest-access sync: when the magic-link tab confirms access, mirror
  // the granted productIds into this tab's sessionStorage and flip isPurchased
  // on the current product without a reload.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('guest-access');
    ch.onmessage = (event) => {
        if (event.data?.type !== 'granted') return;
        const grantedIds: string[] = Array.isArray(event.data.productIds) ? event.data.productIds : [];
        const piId: string | undefined = event.data.piId;
        const email: string | undefined = event.data.email;
        if (!grantedIds.length || !piId || !email) return;

        try {
            const existing: string[] = JSON.parse(sessionStorage.getItem('guestOwnedProducts') || '[]');
            const merged = [...new Set([...existing, ...grantedIds])];
            sessionStorage.setItem('guestOwnedProducts', JSON.stringify(merged));

            const existingMap = JSON.parse(sessionStorage.getItem('guestOrderMap') || '{}');
            const newMap = { ...existingMap };
            grantedIds.forEach((id: string) => { newMap[id] = { orderId: piId, email }; });
            sessionStorage.setItem('guestOrderMap', JSON.stringify(newMap));
        } catch { /* ignore storage failures */ }

        const currentId = params.id as string;
        if (grantedIds.includes(currentId)) {
            setProduct(prev => prev ? { ...prev, isPurchased: true } : prev);
            setGuestAccessOpen(false);
            setGuestAccessEmail('');
            setGuestLinkSent(false);
        }
    };
    return () => { ch.close(); };
  }, [params.id]);

  // Analytics funnel, step 1: once per product opened. A no-op until the visitor
  // accepts analytics cookies (track() does nothing before PostHog starts).
  useEffect(() => {
    if (!product?.id) return;
    track('product.viewed', { productId: product.id, ...(product.sellerId ? { sellerId: product.sellerId } : {}) });
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // How the buyer reached this seller (a link from elsewhere, search, or the
  // marketplace), kept per seller so a purchase can say where it came from. Once
  // per product opened, like the step above. See src/lib/seller-source-capture.ts.
  useEffect(() => {
    if (!product?.id || !product.sellerId) return;
    captureSellerSource({ sellerId: product.sellerId, page: 'product' });
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch seller's avatar from Firestore, respecting their showProfilePicture
  // setting. Read through sellerSummaryFromDoc, the function the server built
  // the first render's seller row with, so this refresh can only change what
  // the document itself has changed since.
  useEffect(() => {
    if (!product?.sellerId || !firestore) return;
    const sellerId = product.sellerId;
    // A read for a seller this page has since left must not land: it would
    // stamp that seller's brand, and mark it settled, on the new one.
    let stale = false;
    // The seller the server already rendered the row and brand for, if it is
    // this one: a copy from this browser's cache (all getDoc has offline) is
    // older than that, and a failed read says nothing new, so neither may
    // replace it.
    const seededForThisSeller = sellerSeedForRef.current === sellerId;
    getDoc(doc(firestore, 'users', sellerId)).then(snap => {
        if (stale) return;
        if (seededForThisSeller && snap.metadata.fromCache) return;
        const seller = sellerSummaryFromDoc(sellerId, snap.exists() ? snap.data() : null);
        if (seller.exists) {
            setShowSellerAvatar(seller.showAvatar);
            setSellerAvatarUrl(seller.avatarUrl);
            if (seller.username) setLiveSellerName(seller.username);
            setSellerTier(seller.tier as SellerTierName | null);
        }
        // Free: the same document the avatar came from. See
        // resolveDesignWithSellerBrand for what is inherited and what is not.
        setSellerBrand(seller.design);
    }).catch(() => {
        // Offline or refused: render without the brand rather than hold the
        // background effects off until a reload. Unless the server rendered
        // it, in which case the brand on screen is the right one.
        if (!stale && !seededForThisSeller) setSellerBrand(null);
    }).finally(() => {
        // Found, missing or failed, the answer for this seller is final, which
        // is what releases the pending override above.
        if (!stale) setSellerBrandSettledFor(sellerId);
    });
    return () => { stale = true; };
  }, [product?.sellerId, firestore]);

  // Reviews, real time and visible to every visitor: the newest
  // `reviewPageLimit` of each subcollection, never the whole of either.
  //
  // `createdAt` is on every review of both kinds: handleSaveReview stamps it on
  // each save, submitGuestReview on the guest's, and the guest-to-account
  // migration in functions copies the guest review's own. A single-field
  // descending order on one collection is served by Firestore's automatic
  // single-field index, so neither query needs a composite one.
  //
  // The error callbacks still mark the list loaded: a refused or failed read
  // must not hold the ?action=review dialog shut for good (see that effect).
  //
  // For a product the server rendered, the opening window of each kind is
  // already on screen from its read (reviewSeedForRef), so those two listeners
  // skip this browser's cached copy and wait for the server's answer
  // (onSnapshotFromServer). A wider account window after "Show More Reviews"
  // was never rendered by the server and listens as before.
  useEffect(() => {
    if (!product?.id || !firestore) return;
    const reviewsQuery = query(
        collection(firestore, 'products', product.id, 'reviews'),
        orderBy('createdAt', 'desc'),
        limit(reviewPageLimit),
    );
    const onNext = (snap: QuerySnapshot) => {
        setReviewPage(snap.docs.map(reviewFromSnapshot));
        setReviewsLoaded(true);
    };
    const onError = () => setReviewsLoaded(true);
    const seeded = reviewSeedForRef.current === product.id && reviewPageLimit === REVIEW_PAGE_SIZE;
    const unsubscribe = seeded
        ? onSnapshotFromServer(reviewsQuery, onNext, onError)
        : onSnapshot(reviewsQuery, onNext, onError);
    return () => unsubscribe();
  }, [product?.id, firestore, reviewPageLimit]);

  useEffect(() => {
    if (!product?.id || !firestore) return;
    const guestReviewsQuery = query(
        collection(firestore, 'products', product.id, 'guestReviews'),
        orderBy('createdAt', 'desc'),
        limit(REVIEW_PAGE_SIZE),
    );
    const onNext = (snap: QuerySnapshot) => {
        setGuestReviewPage(snap.docs.map(guestReviewFromSnapshot));
        setGuestReviewsLoaded(true);
    };
    const onError = () => setGuestReviewsLoaded(true);
    const unsubscribe = reviewSeedForRef.current === product.id
        ? onSnapshotFromServer(guestReviewsQuery, onNext, onError)
        : onSnapshot(guestReviewsQuery, onNext, onError);
    return () => unsubscribe();
  }, [product?.id, firestore]);

  // The viewer's OWN review, read by id. It need not be among the newest, and
  // the ?action=review flow, the "Your Review" row and edit / delete all have to
  // find it: the review dialog seeds its form from it on mount, so missing it
  // would open a blank form over a review that exists and overwrite it on save.
  // One document, and live, so an edit or a delete shows at once.
  useEffect(() => {
    if (!product?.id || !firestore) return;
    const productId = product.id;
    const uid = authUser?.uid;
    if (!uid) {
        setOwnReviewDoc(null);
        return;
    }
    const unsubscribe = onSnapshot(
        doc(firestore, 'products', productId, 'reviews', uid),
        snap => setOwnReviewDoc({ productId, uid, review: snap.exists() ? reviewFromSnapshot(snap) : null }),
        () => setOwnReviewDoc({ productId, uid, review: null }),
    );
    return () => unsubscribe();
  }, [product?.id, firestore, authUser?.uid]);

  // The same for a guest buyer, whose review is keyed on their order:
  // submitGuestReview writes it as guestReviews/guest_{orderId}. Re-run when the
  // product flips to purchased, which is when a guest-access grant from another
  // tab lands the order in this tab's sessionStorage.
  useEffect(() => {
    if (!product?.id || !firestore) return;
    const productId = product.id;
    const info = getGuestOrderInfo(productId);
    if (!info) {
        setOwnGuestReviewDoc({ productId, review: null });
        return;
    }
    const unsubscribe = onSnapshot(
        doc(firestore, 'products', productId, 'guestReviews', `guest_${info.orderId}`),
        snap => setOwnGuestReviewDoc({ productId, review: snap.exists() ? guestReviewFromSnapshot(snap) : null }),
        () => setOwnGuestReviewDoc({ productId, review: null }),
    );
    return () => unsubscribe();
  }, [product?.id, product?.isPurchased, firestore]); // eslint-disable-line react-hooks/exhaustive-deps

  // What the page renders: the loaded window plus the viewer's own review when
  // it is not in it. Where both hold the same document the by-id read wins;
  // both are live, so they differ only between the two listeners firing.
  const viewerReview = ownReviewDoc && ownReviewDoc.productId === product?.id && ownReviewDoc.uid === authUser?.uid
      ? ownReviewDoc.review
      : null;
  const reviews = useMemo(
      () => (viewerReview ? [viewerReview, ...reviewPage.filter(r => r.id !== viewerReview.id)] : reviewPage),
      [reviewPage, viewerReview],
  );
  const viewerGuestReview = ownGuestReviewDoc && ownGuestReviewDoc.productId === product?.id
      ? ownGuestReviewDoc.review
      : null;
  const guestReviews = useMemo(
      () => (viewerGuestReview ? [viewerGuestReview, ...guestReviewPage.filter(r => r.id !== viewerGuestReview.id)] : guestReviewPage),
      [guestReviewPage, viewerGuestReview],
  );

  // Fetch the paymentIntentId for the receipt link
  useEffect(() => {
    if (!authUser || !product?.isPurchased || !firestore) return;
    const productId = product.id;
    // Set by the cleanup, so a purchase read that lands after the buyer has
    // moved on to another product cannot overwrite that product's record.
    let stale = false;
    getDoc(doc(firestore, 'users', authUser.uid, 'ownedProducts', productId)).then(snap => {
        if (snap.exists()) {
            const d = snap.data();
            setPurchasePaymentIntentId(d?.paymentIntentId ?? null);
            // The purchase that intent belongs to, for the refund policy its
            // line recorded at fulfilment. One doc by id, and normally the same
            // one submitDispute reads: a renewal re-points the entitlement at
            // its own charge. Not found leaves the live listing's policy in
            // charge, exactly as the server falls back; so does a failed read.
            const intentId = typeof d?.paymentIntentId === 'string' ? d.paymentIntentId : '';
            if (intentId) {
                getDoc(doc(firestore, 'users', authUser.uid, 'purchases', intentId))
                    .then(p => { if (!stale) setPurchaseRecord(p.exists() ? { productId, data: p.data() } : null); })
                    .catch(() => { /* the live policy stays in charge */ });
            }
            // What this buyer actually paid, which is the figure submitDispute
            // gates the refund request on. Left undefined on entitlements
            // written before the field existed, so eligibility falls back to
            // reading the product's pricing rather than assuming a free grant.
            setPurchasePricePaid(typeof d?.pricePaid === 'number' ? d.pricePaid : undefined);
            // Record which tier this buyer owns (fulfillment stores it as
            // `tierId`) so the download dialog scopes to it and the tier UI can
            // show "you own this tier".
            if (d?.tierId != null) {
                setProduct(prev => prev ? { ...prev, isTieredPurchase: true, purchasedTierId: Number(d.tierId) } : prev);
                // Clear the selection. Ownership can resolve after the first
                // paint, so the page may have opened with tier one ticked — for
                // an owner that is a tier they cannot buy, presented as the one
                // they are about to act on. With nothing ticked the page answers
                // for the tier they actually hold (contextTier).
                setSelectedTierId(null);
            }
        }
    });
    return () => { stale = true; };
  }, [authUser, product?.isPurchased, product?.id, firestore]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time listeners for buyer's refunds and product reports on this product
  useEffect(() => {
    if (!authUser || !product?.isPurchased || !product?.id || !firestore) return;

    const latestStatus = (docs: import('firebase/firestore').QueryDocumentSnapshot[]) => {
      if (!docs.length) return null;
      const sorted = [...docs].sort(
        (a, b) => (b.data().createdAt?.seconds || 0) - (a.data().createdAt?.seconds || 0),
      );
      return { status: sorted[0].data().status as string };
    };

    // Refund disputes are bound to a specific PaymentIntent. After a buyer
    // refunds and re-purchases, the OLD dispute (Accepted) still matches
    // the (buyerUid, productId) query, but it belongs to the previous
    // PaymentIntent and is irrelevant to the new ownership. Filter to
    // disputes for the current purchase only so the UI reverts to the
    // "Request Refund" CTA on a fresh PaymentIntent. If the current
    // PaymentIntent isn't loaded yet, fall back to "no active dispute".
    const latestRefund = (docs: import('firebase/firestore').QueryDocumentSnapshot[]) => {
      if (!docs.length) return null;
      const matching = purchasePaymentIntentId ?
        docs.filter((d) => d.data().paymentIntentId === purchasePaymentIntentId) :
        [];
      if (!matching.length) return null;
      const sorted = matching.sort(
        (a, b) => (b.data().createdAt?.seconds || 0) - (a.data().createdAt?.seconds || 0),
      );
      return { id: sorted[0].id, status: sorted[0].data().status as string };
    };

    const refundQ = query(
      collection(firestore, 'refunds'),
      where('buyerUid', '==', authUser.uid),
      where('productId', '==', product.id),
    );
    const unsubRefund = onSnapshot(refundQ,
      snap => { setBuyerRefundDispute(latestRefund(snap.docs)); },
      () => { setBuyerRefundDispute(null); },
    );

    const reportQ = query(
      collection(firestore, 'productReports'),
      where('buyerUid', '==', authUser.uid),
      where('productId', '==', product.id),
    );
    const unsubReport = onSnapshot(reportQ,
      snap => { setBuyerReportDispute(latestStatus(snap.docs)); },
      () => { setBuyerReportDispute(null); },
    );

    return () => { unsubRefund(); unsubReport(); };
    // purchasePaymentIntentId intentionally included so the snapshot
    // re-resolves once the current PaymentIntent loads after a re-buy.
  }, [authUser, product?.isPurchased, product?.id, firestore, purchasePaymentIntentId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guest refund status poll. Guests have no auth token so Firestore rules
  // deny the refunds query — instead we call `getGuestRefundStatus` with the
  // (orderId, email, productId) the guest already holds in sessionStorage.
  // Uses exponential backoff on failures (30s → 1m → 2m → 5m max) so a
  // transient outage or rate-limit hit doesn't hammer the callable from
  // every open tab. Stops on terminal states (Accepted / Denied).
  useEffect(() => {
    if (!product?.id) return;
    const guestInfo = (typeof window !== 'undefined' && product.isPurchased) ?
      (() => {
        try {
          const map = JSON.parse(sessionStorage.getItem('guestOrderMap') || '{}');
          return map[product.id] as { orderId?: string; email?: string } | undefined;
        } catch { return undefined; }
      })() :
      undefined;
    if (!guestInfo?.orderId || !guestInfo?.email) return;

    let cancelled = false;
    let consecutiveFailures = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const fn = httpsCallable(getFunctions(), 'getGuestRefundStatus');

    const schedule = (delayMs: number) => {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void tick(); }, delayMs);
    };

    const tick = async () => {
      try {
        const result = await fn({
          orderId: guestInfo.orderId,
          email: guestInfo.email,
          productId: product.id,
        }) as { data: {
          status: string | null; resolvedByAdmin?: boolean; requestedAtMs?: number | null;
          refundPolicy?: Record<string, unknown> | null;
        } };
        if (cancelled) return;
        consecutiveFailures = 0;
        // The policy recorded on the guest's purchase line, which they cannot
        // read themselves. Shaped as a one-line purchase so refundActionAvailable
        // reads it exactly as it reads a signed-in buyer's purchase doc.
        const recordedPolicy = result.data?.refundPolicy;
        if (recordedPolicy && typeof recordedPolicy === 'object') {
          setPurchaseRecord({ productId: product.id, data: { items: [{ productId: product.id, refundPolicy: recordedPolicy }] } });
        }
        const status = result.data?.status ?? null;
        setGuestRefundStatus(status);
        // Carried alongside the status so a Denied row knows whether an appeal is
        // still open to the buyer (see canAppealRefund).
        setGuestRefundResolvedByAdmin(result.data?.resolvedByAdmin === true);
        setGuestRefundRequestedAtMs(result.data?.requestedAtMs ?? null);
        // Stop polling on terminal states.
        if (status === 'Accepted' || status === 'Denied') return;
        schedule(30_000);
      } catch {
        // Back off on repeated failures so rate-limit / outage doesn't
        // keep the callable hot from every open tab.
        consecutiveFailures += 1;
        const backoffMs = Math.min(
          30_000 * Math.pow(2, consecutiveFailures - 1),
          5 * 60_000,
        );
        schedule(backoffMs);
      }
    };

    void tick();
    const onFocus = () => { consecutiveFailures = 0; void tick(); };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [product?.id, product?.isPurchased]);

  const userReview = useMemo(() => reviews.find(r => r.uid === authUser?.uid) || null, [reviews, authUser?.uid]);

  const userGuestReview = useMemo(() => {
    if (!product) return null;
    const info = getGuestOrderInfo(product.id);
    if (!info) return null;
    return guestReviews.find(r => r.orderId === info.orderId) || null;
  }, [guestReviews, product]); // eslint-disable-line react-hooks/exhaustive-deps

  // The review dialog's key: the viewer's review, so the dialog remounts and
  // reseeds its form when that review resolves or goes away, but held still
  // while the dialog is open, because saving or deleting moves the review under
  // it. See src/lib/review-dialog-key.ts. The guest test is isGuestPurchase's,
  // below.
  const liveReviewDialogKey = (product?.isPurchased && isGuestOwned(product.id) ? userGuestReview?.id : userReview?.id) ?? 'new';
  const ownReviewLookupSettled = ownReviewSettled && ownGuestReviewSettled;
  const [reviewDialogKey, setReviewDialogKey] = useState<ReviewDialogKey>(
    () => ({ key: liveReviewDialogKey, settled: ownReviewLookupSettled }),
  );
  useEffect(() => {
    setReviewDialogKey(held => nextReviewDialogKey(held, { key: liveReviewDialogKey, settled: ownReviewLookupSettled }, reviewOpen));
  }, [reviewOpen, liveReviewDialogKey, ownReviewLookupSettled]);

  // The ratings of the reviews this page has loaded: what the star histogram
  // counts, and the summary's fallback below.
  const allRatings = useMemo(() => [
    ...reviews.map(r => r.rating),
    ...guestReviews.map(r => r.rating),
  ], [reviews, guestReviews]);

  // The summary reads the product's own aggregates: functions recount
  // avgRating and totalReviews from BOTH subcollections on every review written
  // or deleted, and normalizeProductDoc hands them over as `rating` and
  // `reviewCount`. The page loads only the newest reviews now, so counting what
  // it holds would understate any product with more.
  //
  // The loaded reviews still answer when they have seen MORE than the aggregate
  // has: in the moment between a review being written and the recount landing,
  // and on a product whose reviews predate the trigger and were never
  // recounted. Whichever has seen more reviews is the better figure.
  const aggregateReviewCount = product?.reviewCount ?? 0;
  const summaryFromAggregate = aggregateReviewCount > 0 && aggregateReviewCount >= allRatings.length;
  const totalReviews = summaryFromAggregate ? aggregateReviewCount : allRatings.length;
  const averageRating = useMemo(() => {
    if (summaryFromAggregate) return product?.rating ?? 0;
    if (allRatings.length === 0) return 0;
    return allRatings.reduce((acc, r) => acc + r, 0) / allRatings.length;
  }, [summaryFromAggregate, product?.rating, allRatings]);

  // The star histogram has no aggregate on the product, so it is counted from
  // the loaded reviews and drawn as shares of THEM (not of totalReviews, which
  // would leave the bars summing to less than 100%). Exact while a product has
  // no more than REVIEW_PAGE_SIZE reviews of each kind; past that, the spread of
  // the newest.

  const reviewDistribution = useMemo(() => {
    const distribution = [0, 0, 0, 0, 0]; // For 5, 4, 3, 2, 1 stars
    allRatings.forEach(rating => {
        const ratingIndex = 5 - Math.round(rating);
        if (ratingIndex >= 0 && ratingIndex < 5) {
            distribution[ratingIndex]++;
        }
    });
    return distribution;
  }, [allRatings]);

  const [reviewSort, setReviewSort] = useState<'helpful' | 'recent' | 'controversial'>('helpful');
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [reviewsOpen, setReviewsOpen] = useState(true);
  const sortedReviews = useMemo(() => {
    const ownReview = authUser?.uid ? reviews.filter(r => r.uid === authUser.uid) : [];
    let others = authUser?.uid ? reviews.filter(r => r.uid !== authUser.uid) : [...reviews];
    // The opening order. Shared with the server, which resolves the reviewers
    // of the first few in this order before the page is sent
    // (reviewerUidsShownOnArrival), so the names it rendered are the ones on
    // screen.
    if (reviewSort === 'helpful') others = mostHelpfulFirst(others);
    else if (reviewSort === 'recent') others.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.()?.getTime() ?? 0;
        const bTime = b.createdAt?.toDate?.()?.getTime() ?? 0;
        return bTime - aTime;
    });
    else others.sort((a, b) => {
        const aDiff = Math.abs((a.helpfulUids?.length ?? 0) - (a.unhelpfulUids?.length ?? 0));
        const bDiff = Math.abs((b.helpfulUids?.length ?? 0) - (b.unhelpfulUids?.length ?? 0));
        return aDiff - bDiff;
    });
    return [...ownReview, ...others];
  }, [reviews, authUser?.uid, reviewSort]);

  // "Show More Reviews": five more of what is loaded, and once that runs past
  // the end of a FULL window, the listener widens by another page, since older
  // reviews may be waiting on the server. A window that came back short
  // already holds every review there is.
  const showMoreReviews = () => {
    const next = reviewsToShow + 5;
    setReviewsToShow(next);
    if (next >= reviewPage.length && reviewPage.length >= reviewPageLimit) {
      setReviewPageLimit(prev => prev + REVIEW_PAGE_SIZE);
    }
  };

  // Each DISPLAYED reviewer's user doc, for their showProfilePicture setting
  // (whether to show their avatar) and their name.
  //
  // The USERNAME is resolved from the same snapshot, and that is a control
  // rather than a convenience. `username` and `avatarUrl` are denormalised onto
  // the review document at write time, and the review is written straight from
  // the client, so both are whatever its author last put there. Usernames are
  // otherwise unique and validated: they are claimed transactionally against
  // usernames/{lower} by the updateUsername callable, and firestore.rules keeps
  // the field off users/{uid} entirely. The copy on a review answered to none of
  // that, so a reviewer could display any name at all beside their own review,
  // including another seller's or one dressed up as staff.
  //
  // The avatar was already read from here, so only the name was still trusting
  // the review. Reading both from the user doc makes the stored copies purely
  // decorative, which also means a reviewer who later renames is shown under
  // their current name rather than a stale one.
  //
  // Only the reviews on screen, in batches of up to 30 (the most a documentId()
  // `in` filter takes), and each uid once per visit. This used to read every
  // reviewer the listener held, one document each, every time the list changed
  // identity, which is every snapshot. The cost of the cache: a reviewer who
  // switches their picture off or renames mid-visit is shown the old way until
  // the next page load.
  const shownReviewerUids = useMemo(
    () => [...new Set(sortedReviews.slice(0, reviewsToShow).map(r => r.uid).filter(Boolean))],
    [sortedReviews, reviewsToShow],
  );
  const shownReviewerKey = shownReviewerUids.join(',');
  const requestedReviewerUids = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!firestore) return;
    const missing = shownReviewerUids.filter(uid => !requestedReviewerUids.current.has(uid));
    if (missing.length === 0) return;
    missing.forEach(uid => requestedReviewerUids.current.add(uid));
    for (let i = 0; i < missing.length; i += 30) {
      const batch = missing.slice(i, i + 30);
      getDocs(query(collection(firestore, 'users'), where(documentId(), 'in', batch))).then(snap => {
        const nextUrls: Record<string, string | null> = {};
        const nextExists: Record<string, boolean> = {};
        const nextNames: Record<string, string> = {};
        // A uid the query did not return has no user doc: a deleted account.
        // Only the server can say so. Offline, getDocs answers from this
        // browser's cache, which has simply never seen most reviewers, and
        // taking that as "deleted" turned the names the server rendered into
        // "Deleted User". Such a batch is forgotten instead, so a later render
        // asks again.
        if (snap.metadata.fromCache) {
          batch.forEach(uid => requestedReviewerUids.current.delete(uid));
        } else {
          batch.forEach(uid => { nextExists[uid] = false; nextUrls[uid] = null; });
        }
        // Through reviewerSeedFromDoc, the same reading the server resolved
        // the first reviewers with (getProductReviewSeed).
        snap.docs.forEach(d => {
          const reviewer = reviewerSeedFromDoc(d.data());
          nextExists[d.id] = reviewer.exists;
          // null = picture disabled (show nothing), string = enabled (show avatar or placeholder)
          nextUrls[d.id] = reviewer.avatarUrl;
          if (reviewer.username) nextNames[d.id] = reviewer.username;
        });
        setReviewerAvatarUrls(prev => ({ ...prev, ...nextUrls }));
        setReviewerExists(prev => ({ ...prev, ...nextExists }));
        setReviewerUsernames(prev => ({ ...prev, ...nextNames }));
      }).catch(() => {
        // Forget the batch so a later render asks again, rather than leaving
        // these reviewers unresolved for the rest of the visit.
        batch.forEach(uid => requestedReviewerUids.current.delete(uid));
      });
    }
  }, [shownReviewerKey, firestore]); // eslint-disable-line react-hooks/exhaustive-deps

  // The seller's optional public sample. resolveProductPreview is the single
  // predicate for whether a preview section exists at all: it returns null for
  // a product written before the feature shipped, for a preview the seller
  // switched off, and for one whose type this build has no renderer for.
  //
  // A soft-deleted product with no customers is excluded here for the same
  // reason the gallery above renders an explanation instead of media:
  // onProductUpdated has already swept the whole products/{id}/ storage prefix,
  // so the preview object went with everything else and only its dead URL is
  // left on the document.
  //
  // The field is read through a structural cast because the Product interface
  // in src/context/product-context.tsx does not declare `preview`. snapshotToMap
  // spreads the raw document, so the value is present at runtime regardless;
  // the cast keeps resolveProductPreview the only thing that trusts its shape,
  // which is cheaper than an `any` and stays correct once the interface gains
  // the field.
  const resolvedPreview = useMemo(() => {
    if (!product) return null;
    if (product.status === 'deleted' && product.filesClearedAt) return null;
    // The product id is passed so the resolver can require the preview to
    // address THIS product's own preview prefix, rather than merely some object
    // in our bucket. See resolveProductPreview.
    return resolveProductPreview((product as unknown as { preview?: unknown }).preview, product.id);
  }, [product]);

  // Sticky mobile buy bar, mirroring the one on checkout
  // (src/app/checkout/checkout-client.tsx). This page stacks to a single column
  // below `md` and runs long (gallery, description, sample, details, reviews),
  // so the real CTA leaves the viewport early and does not come back. The bar
  // shows only while it is off screen, so the CTA is never more than a thumb
  // away without permanently sitting on top of the page.
  //
  // Declared HERE, above the `!productsLoaded` and `!product` early returns,
  // because hooks cannot run conditionally. The ref simply stays null on the
  // branches that render no CTA, and the effect no-ops.
  const buyCtaRef = useRef<HTMLDivElement | null>(null);
  const [buyCtaInView, setBuyCtaInView] = useState(true);
  // Scrolled PAST the buy controls, which is a stricter thing than "not on
  // screen": a CTA still below the fold on a fresh page load is also not
  // visible, and a top bar that appeared then would greet every arrival with a
  // summary of a page they have not read yet. The bottom edge of the element
  // sitting above the top edge of the viewport is what "past" actually means.
  const [buyCtaAbove, setBuyCtaAbove] = useState(false);
  // MEASURED, NOT OBSERVED, and that is the whole point of this effect.
  //
  // This was an IntersectionObserver, which cannot answer the question the top
  // bar asks. An observer only fires when the intersection RATIO crosses a
  // threshold, and on a phone the CTA goes from below the fold (ratio 0) to
  // above it (ratio 0) without ever being on screen in between, so no callback
  // ever ran and the bar never appeared. Any jump does it: a flick scroll, an
  // in-page anchor, a restored scroll position on back-navigation. Desktop hid
  // the bug because there the CTA sits beside the gallery and IS on screen at
  // the top of the page, so the ratio really did change on the way past.
  //
  // Reading the rect on scroll answers it directly and for both flags at once,
  // which also means the two bars can never disagree about where the CTA is.
  // One `getBoundingClientRect` per animation frame, only while scrolling, and
  // only on this page.
  useEffect(() => {
    const el = buyCtaRef.current;
    if (!el || typeof window === 'undefined') return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      // Past it: the whole element is above the top of the viewport.
      setBuyCtaAbove(rect.bottom <= 0);
      // On screen, against a viewport shortened by roughly the height of the
      // bottom bar, so the two cannot fight: that bar must never cover the CTA
      // whose visibility is what hides it. Same 88px the old rootMargin used.
      setBuyCtaInView(rect.bottom > 0 && rect.top < window.innerHeight - 88);
    };
    const onScroll = () => {
      // Coalesced to one measurement per frame; a raw scroll handler runs far
      // more often than the page can paint.
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
    // Re-measured when the CTA branch changes, since a different branch mounts a
    // different node and this would otherwise hold the detached one.
  }, [productsLoaded, product?.id, product?.isPurchased, product?.status, selectedTierId]);

  /**
   * The top bar's own mount, which outlives `buyCtaAbove` by the length of its
   * exit animation.
   *
   * Unmounting the moment the flag flips is what made the bar vanish rather
   * than leave: an element that is gone from the tree has nothing left to
   * animate. So the flag drives an INTENT, and this state drives the mount,
   * staying true through the slide back up and only then letting go.
   *
   * `leaving` is what swaps the enter animation for the exit one, so the bar
   * travels the same distance in both directions.
   */
  const [stickyBarMounted, setStickyBarMounted] = useState(false);
  const [stickyBarLeaving, setStickyBarLeaving] = useState(false);
  useEffect(() => {
    if (buyCtaAbove) {
      setStickyBarMounted(true);
      setStickyBarLeaving(false);
      return;
    }
    if (!stickyBarMounted) return;
    setStickyBarLeaving(true);
    // Matched to the duration class on the bar. Someone who has asked their
    // system for reduced motion gets no animation to wait for, so the bar is
    // dropped immediately rather than lingering for 300ms of nothing.
    const reduced = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timer = setTimeout(() => {
      setStickyBarMounted(false);
      setStickyBarLeaving(false);
    }, reduced ? 0 : STICKY_BAR_EXIT_MS);
    return () => clearTimeout(timer);
    // `stickyBarMounted` is deliberately not a dependency: re-running this on
    // its own change would restart the timer it just set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyCtaAbove]);

  // The loading state, until the listener resolves. A product the server
  // rendered never takes this branch: its read is already loaded (see
  // pageSeed), and the page renders complete on the server and on the
  // browser's first render alike. What does take it is a product the server
  // could not hand over (a draft for its seller, an archived or deleted
  // purchase for its owner, a failed read), whose first render is this on the
  // server and in the browser, so the two still match.
  //
  // A product the listener HAS delivered but the effect above has not yet
  // turned into `product` is still loading too. Falling through to "not
  // found" there painted that heading for a frame on every such page.
  if (!productsLoaded || (!product && products.has(params.id as string))) {
    return <>{loadingFallback}</>;
  }

  if (!product) {
      return (
        <div className="py-12 text-center w-full">
            <h1>Product not found.</h1>
            <p className="text-muted-foreground">This product may have been removed or the link is incorrect.</p>
            <div className="mt-4 flex justify-center">
                <BackButton href="/marketplace" label="Back to Marketplace" />
            </div>
        </div>
      )
  }

  // Guest re-access ("Already purchased? Get access link"), for unverified,
  // anonymous or signed-out visitors on paid products. A guest is recognised as
  // an owner only in the browser session they bought in, so this form is how
  // they get back to their files from anywhere else, and the download dialog and
  // the used-link page both send them here for it. It has to survive the listing
  // going off sale: archiving (by the seller, or automatically when an identity
  // check fails) and deleting both keep every buyer's access, and the server
  // sends the link whatever the listing's status. Rendered by the main page and
  // by the "not available" screen below, which used to return before reaching it.
  const showGuestAccess = (!authUser || authUser.isAnonymous || (!authUser.emailVerified && !user))
    && !product.isPurchased && !product.isMerchant && product.pricingModel !== 'free';
  const renderGuestAccess = (className: string) => (
            <div className={className}>
              {!guestAccessOpen && (
                <p className="text-xs text-muted-foreground">
                  Already purchased?{' '}
                  <button
                    type="button"
                    className="underline hover:text-foreground transition-colors"
                    onClick={() => setGuestAccessOpen(true)}
                  >
                    Get access link
                  </button>
                </p>
              )}
              <Collapsible open={guestAccessOpen}>
                <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
                  <div className="border rounded-lg p-4 space-y-3 text-left mt-2">
                    {guestLinkSent ? (
                      <div className="text-center space-y-1 py-2">
                        <p className="text-sm font-medium">Check your email</p>
                        <p className="text-xs text-muted-foreground">
                          If the email is correct, then we sent an access link to {guestAccessEmail}.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          Enter the email you used at checkout to receive a one-click access link.
                        </p>
                        <Input
                          type="email"
                          placeholder="you@example.com"
                          value={guestAccessEmail}
                          onChange={e => setGuestAccessEmail(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSendGuestLink()}
                          autoComplete="email"
                        />
                        {guestAccessError && (
                          <p className="text-xs text-destructive">{guestAccessError}</p>
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={handleSendGuestLink}
                            disabled={!guestAccessEmail.trim()}
                            loading={isSendingGuestLink}
                            className="flex-1"
                          >
                            Send access link
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setGuestAccessOpen(false);
                              setGuestAccessEmail('');
                              setGuestAccessError(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
  );

  // Off sale, but the people who already bought it keep the page.
  //
  // Every status that is not live keeps it for the seller and for its owners
  // (productPageAccess). Downloads, the licence, the update history and the
  // refund route all live on this page, so losing it is losing the purchase.
  // A moderation takedown and a seller's ban ('unpublished') used to fall
  // through to a catch-all that let only the seller in, which took every
  // buyer's files with the listing (2026-10-26 audit). Off sale, the page
  // sells nothing (isWithdrawnListing below).
  if (productPageAccess(product.status, product) === 'closed') {
    if (product.status === 'deleted' || product.status === 'archived') {
      return (
        <div className="flex flex-col items-center justify-center text-center w-full flex-grow">
            <h1>Product not available.</h1>
            {/* Archived and deleted both land here, and they are not the same
                thing: one is reversible and the other is not. "No longer"
                closes the door on a listing the seller may well reopen next
                week, so a paused product says so. Deliberately does not name
                the seller's reason or an expected date — neither is knowable
                from here, and archiving is not an announcement the seller has
                chosen to make. */}
            <p className="text-muted-foreground">
              {product.status === 'archived'
                ? 'This product is not on sale at the moment. It may come back.'
                : 'This product is no longer publicly accessible.'}
            </p>
            {/* A guest who bought it before it went off sale still owns it, and
                this is the page their emails send them to for a fresh link. */}
            {showGuestAccess && offersGuestAccess(product.status) && renderGuestAccess('mt-6 w-full max-w-sm text-center')}
            <div className="mt-4 flex justify-center">
                <BackButton href="/marketplace" label="Back to Marketplace" />
            </div>
        </div>
      );
    }
    return (
        <div className="py-12 text-center w-full">
            <h1>Product not available.</h1>
            <p className="text-muted-foreground">This product is currently not available for viewing.</p>
            {/* The same form for a taken-down listing: a guest who bought it
                still owns it, and their emailed links land here. */}
            {showGuestAccess && offersGuestAccess(product.status) && renderGuestAccess('mt-6 mx-auto w-full max-w-sm text-center')}
            <div className="mt-4 flex justify-center">
                <BackButton href="/marketplace" label="Back to Marketplace" />
            </div>
        </div>
      )
  }

  const isSaved = saved.some(item => item.product.id === product.id);
  const isGuestPurchase = product.isPurchased && isGuestOwned(product.id);
  const guestOrderInfo = isGuestPurchase ? getGuestOrderInfo(product.id) : null;
  // The page's own confirmation of a withdrawal. The control sits among the
  // owner actions, which go the moment the entitlement is revoked (often before
  // the call returns), taking its dialog with them; a toast outlives that.
  const handleWithdrawn = (result: { refundIssued: boolean }) => {
    toast({
      title: 'Withdrawal received',
      description: 'We have emailed you an acknowledgement. ' + (result.refundIssued ?
        'Your refund is on its way to your original payment method.' :
        'Your refund is being processed.'),
    });
    if (isGuestPurchase) {
      // A guest's ownership on this page lives in sessionStorage, which the
      // server's revocation cannot reach: drop it, so Download and Review go too.
      try {
        const owned: string[] = JSON.parse(sessionStorage.getItem('guestOwnedProducts') || '[]');
        sessionStorage.setItem('guestOwnedProducts', JSON.stringify(owned.filter((id) => id !== product.id)));
      } catch { /* storage blocked: the server still refuses the download */ }
      setProduct(prev => (prev ? { ...prev, isPurchased: false } : prev));
    }
  };

  const toDate = (date: any): Date | null => {
    if (!date) return null;
    const d: Date = typeof date?.toDate === 'function' ? date.toDate() : new Date(date);
    return isNaN(d.getTime()) ? null : d;
  };
  // Until hydration a date is formatted as it falls in UTC, which is what the
  // server's render printed whatever its own timezone, so the browser's first
  // render matches it; from then on, in the visitor's local time, as always.
  // The two differ only for an instant near midnight UTC. See utcWallClock.
  const shownDate = (d: Date) => (hydrated ? d : utcWallClock(d));
  const getFormattedDate = (date: any) => {
    const d = toDate(date);
    return d ? formatUserDate(shownDate(d), { withTime: true }) : '';
  };
  const getFormattedDateOnly = (date: any) => {
    const d = toDate(date);
    return d ? formatUserDate(shownDate(d)) : '';
  };
  
  // Null when this buyer may start a subscription. Recurring products and
  // recurring tiers are account-only (the server refuses a guest subscription
  // intent), so this drives the CTA below and blocks the cart write outright.
  // While auth is still resolving nobody is classified yet: showing the gate
  // then would flash "Sign in to subscribe" at a signed-in buyer, so the CTA
  // waits (disabled) instead of guessing.
  const subscriptionGate = isUserLoading ? null : subscriptionBlockReason({ authUser, user });

  // `e` is the click that asked for the add. Its currentTarget is the button
  // the cart launches the add-to-cart shooting star from; the cart measures it
  // during the click, because React clears currentTarget once the handler
  // returns. Optional: the add never depends on it, and a call without it
  // still adds, with nowhere to launch a flight from.
  const handleAddToCart = (e?: React.MouseEvent<HTMLElement>) => {
    let productToAdd: any;
    if (product.pricingModel === 'pwyw') {
        // Whole cent, rounded UP: a floor saved from another currency can be
        // A$7.5414, and "Minimum is A$7.54" would name an amount the server
        // refuses. Against the cent-rounded `pwywAud` it tests the same.
        const pwywMin = wholeCentFloorAud(Math.max(PRICE_MIN_AUD, product.pwywMinPriceEnabled ? (product.pwywMinPrice ?? 0) : 0));
        // AUD, converted from what was typed. The cart, the checkout intent and
        // the bounds below are all in AUD.
        const price = pwywAud;
        if (!price || price < pwywMin) {
            const shownMin = pwywMinimumShown(pwywMin);
            toast({
                title: 'Amount too low',
                description: `Minimum is ${isBaseCurrency ? shownMin : `${shownMin} (${formatBaseAmount(pwywMin)})`}.`,
                variant: 'destructive',
            });
            return;
        }
        // The platform cap applies to what a BUYER names too, not just what a
        // seller sets. createPaymentIntent rejects anything over it, so catching
        // it here turns a failed checkout into a correctable typo.
        if (price > PRICE_MAX_AUD) {
            toast({ title: 'Amount too high', description: `Maximum is ${fmtWithBase(PRICE_MAX_AUD)}.`, variant: 'destructive' });
            return;
        }
        productToAdd = { id: product.id, name: product.name, price: price, image: typeof product.image === 'string' ? product.image : product.image.src };
    } else if (product.pricingModel === 'tiered' && selectedTierId) {
        const tier = product.priceTiers?.find(t => t.id === selectedTierId);
        // `tierId` is what the server prices this line against (the cart id keeps
        // its `<productId>-<tierId>` shape only so two tiers stay distinct rows).
        // A recurring tier carries the same subscription flags as a whole
        // subscription product, so the cart, checkout consent, and the server's
        // one-subscription-per-checkout rule all treat it identically.
        if (tier) productToAdd = {
            id: `${product.id}-${tier.id}`,
            name: `${product.name} - ${tier.name}`,
            price: tier.price,
            image: typeof product.image === 'string' ? product.image : product.image.src,
            tierId: String(tier.id),
            ...(isSubscriptionTier(tier)
                ? { isSubscription: true, subscriptionFrequency: tierFrequency(tier) }
                : {}),
        };
    } else if (product.pricingModel === 'subscription') {
        // Flagging the line item as a subscription activates the cart's
        // "Recurring purchases" section and "Renews ..." badge. The server
        // charges subscriptionPrice per cycle; carry it as the line price so
        // the cart shows the recurring amount.
        productToAdd = {
            id: product.id,
            name: product.name,
            price: product.subscriptionPrice ?? product.price,
            image: typeof product.image === 'string' ? product.image : product.image.src,
            isSubscription: true,
            subscriptionFrequency: product.subscriptionFrequency,
        };
    } else {
        // Standard and free both land here. cartPriceAud returns 0 for free
        // without reading the price fields, so the cart shows the buyer the same
        // A$0.00 this page already displays rather than a leftover amount from
        // before the seller switched the model.
        productToAdd = { id: product.id, name: product.name, price: cartPriceAud(product), image: typeof product.image === 'string' ? product.image : product.image.src };
    }
    // Both recurring shapes (whole subscription product, recurring tier) set
    // `isSubscription`, so one check covers them. The CTA is already replaced
    // by the sign-in gate for these buyers; this keeps a guest cart free of an
    // item that could never be paid for even if the click arrives another way.
    if (subscriptionGate && productToAdd?.isSubscription) return;
    if (productToAdd) addToCart(productToAdd, { from: e?.currentTarget ?? null });
  };

  const handleSaveToggle = () => {
    const simplifiedProduct = {
      id: product.id,
      name: product.name,
      price: product.discountedPrice || product.price,
      image: typeof product.image === 'string' ? product.image : product.image.src,
    };
    toggleSave(simplifiedProduct);
  };
  const galleryMedia = product.images
    .map(m => typeof m === 'string' ? {type: 'image' as const, src: m} : {...m, type: (m.type || 'image') as 'image' | 'video'})
    .filter(m => m.src && !m.src.startsWith('blob:'));
  // The cover leads the gallery unless the seller turned that off. Both
  // editors write `details.coverInGallery` and both of their previews honour
  // it; this page used to ignore it entirely and show the gallery images alone
  // whenever there were any, so the switch did nothing on the published page
  // and the preview disagreed with what buyers got. composeGalleryMedia is the
  // one answer all three sites now share.
  const coverItem = [typeof product.image === 'object' ? {type: 'image' as const, ...product.image} : {type: 'image' as const, src: product.image}]
    .filter(m => m.src && !m.src.startsWith('blob:'))[0] ?? null;
  const allDisplayMedia = composeGalleryMedia(coverItem, galleryMedia, resolveCoverInGallery(product.details));

  const nextImage = () => { setMediaLoaded(false); setActiveImageIndex((prev) => (prev + 1) % allDisplayMedia.length); };
  const prevImage = () => { setMediaLoaded(false); setActiveImageIndex((prev) => (prev - 1 + allDisplayMedia.length) % allDisplayMedia.length); };

  // The four review handlers resolve once the change is stored and THROW when
  // it is not. ReviewDialog awaits them and is the one place that toasts, so a
  // refused write is never reported as saved and each attempt ends in exactly
  // one message. A missing precondition throws as well: returning quietly would
  // read to the dialog as success.
  const handleSaveReview = async (review: { rating: number; text: string }) => {
    if (!authUser || !product || !firestore) throw new ReviewActionError(REVIEW_REFRESH_MESSAGE);
    const reviewRef = doc(firestore, 'products', product.id, 'reviews', authUser.uid);
    await setDoc(reviewRef, {
        uid: authUser.uid,
        // The author's USERNAME, or nothing. Not authUser.displayName, which was
        // the third link in this chain and is a different kind of value: for an
        // OAuth signup it is the person's real name ("John Smith"), which is not
        // a username, is not bounded by the username rules, and routinely runs
        // past the 20 characters firestore.rules now caps this field at — so the
        // whole review write would have been rejected for anyone whose profile
        // had not loaded yet. It cost nothing to lose: the field is decorative
        // (the effect above re-resolves every reviewer's name from their live
        // user doc and only falls back to this copy while that fetch is in
        // flight), so a placeholder for a fraction of a second beats a write the
        // rules refuse.
        username: user?.username || 'Anonymous',
        avatarUrl: user?.avatarUrl || '',
        rating: review.rating,
        text: review.text,
        createdAt: serverTimestamp(),
    }, { merge: true });
    // Aggregate counters (totalReviews, avgRating) are maintained by the
    // onReviewCreated / onReviewUpdated cloud function triggers — no client update needed.
  };

  const handleDeleteReview = async () => {
    if (!authUser || !product || !firestore) throw new ReviewActionError(REVIEW_REFRESH_MESSAGE);
    await deleteDoc(doc(firestore, 'products', product.id, 'reviews', authUser.uid));
    // Aggregate counters are maintained by the onReviewDeleted cloud function trigger.
  };

  const handleSaveGuestReview = async (review: { rating: number; text: string }) => {
    if (!product) throw new ReviewActionError(REVIEW_REFRESH_MESSAGE);
    // The order this tab was given by checkout or /access (guestOrderMap).
    const info = getGuestOrderInfo(product.id);
    if (!info) throw new ReviewActionError(GUEST_REVIEW_VERIFY_MESSAGE);
    const fn = httpsCallable(getFunctions(), 'submitGuestReview');
    await fn({ orderId: info.orderId, email: info.email, productId: product.id, rating: review.rating, body: review.text });
  };

  // A guest proves they are the buyer with the httpOnly download cookie, never
  // with (orderId, email), which the seller can read: see
  // src/lib/guest-review-delete.ts and the route it calls. Delete is offered
  // whether or not this browser holds the cookie, because the page cannot see
  // it (httpOnly, and sent only to /api/download), and a refusal tells the guest
  // how to get one: the access link from their purchase email.
  const handleDeleteGuestReview = async () => {
    if (!product) throw new ReviewActionError(REVIEW_REFRESH_MESSAGE);
    const info = getGuestOrderInfo(product.id);
    if (!info) throw new ReviewActionError(GUEST_REVIEW_VERIFY_MESSAGE);
    await deleteGuestReview({ productId: product.id, orderId: info.orderId });
    // onGuestReviewDeleted recounts the aggregates, and a seller reply goes
    // with the document, as it does on an account review.
  };

  const handleReplyToReview = async (reviewId: string, replyText: string) => {
    if (!product || !firestore) return;
    await updateDoc(doc(firestore, 'products', product.id, 'reviews', reviewId), {
        reply: replyText,
        replyAt: serverTimestamp(),
    });
  };

  const handleEditReply = async (reviewId: string, replyText: string) => {
    if (!product || !firestore) return;
    await updateDoc(doc(firestore, 'products', product.id, 'reviews', reviewId), {
        reply: replyText,
        replyAt: serverTimestamp(),
    });
  };

  // Both reply deletes report their outcome; the confirm that asked spins
  // until this settles.
  const handleDeleteReply = async (reviewId: string) => {
    if (!product || !firestore) return;
    try {
      await updateDoc(doc(firestore, 'products', product.id, 'reviews', reviewId), {
          reply: deleteField(),
          replyAt: deleteField(),
      });
      toast({ title: 'Reply deleted' });
    } catch {
      toast({ variant: 'destructive', title: 'Could not delete the reply', description: 'Nothing changed. Try again.' });
    }
  };

  const handleReplyToGuestReview = async (reviewId: string, replyText: string) => {
    if (!product || !firestore) return;
    await updateDoc(doc(firestore, 'products', product.id, 'guestReviews', reviewId), {
        reply: replyText,
        replyAt: serverTimestamp(),
    });
  };

  const handleEditGuestReply = async (reviewId: string, replyText: string) => {
    if (!product || !firestore) return;
    await updateDoc(doc(firestore, 'products', product.id, 'guestReviews', reviewId), {
        reply: replyText,
        replyAt: serverTimestamp(),
    });
  };

  const handleDeleteGuestReply = async (reviewId: string) => {
    if (!product || !firestore) return;
    try {
      await updateDoc(doc(firestore, 'products', product.id, 'guestReviews', reviewId), {
          reply: deleteField(),
          replyAt: deleteField(),
      });
      toast({ title: 'Reply deleted' });
    } catch {
      toast({ variant: 'destructive', title: 'Could not delete the reply', description: 'Nothing changed. Try again.' });
    }
  };

  // Votes count one per account, so firestore.rules takes them only from a
  // verified address (an unverified account costs nothing to make). Say so,
  // rather than let the write fail without a word.
  const showVerifyToVote = () => toast({
    title: 'Verify your email to vote',
    description: 'Votes on reviews count once your email address is verified.',
  });
  const voteNeedsVerifiedEmail = () => {
    if (authUser?.emailVerified) return false;
    showVerifyToVote();
    return true;
  };
  // The rules read the sign-in token's email_verified, and a token minted before
  // the address was verified keeps saying false for up to an hour (the app does
  // not force a refresh after verification). So a refused vote from an account
  // the browser already knows is verified gets one fresh token and one retry.
  const writeVote = async (write: () => Promise<void>) => {
    try {
      await write();
    } catch (err) {
      if ((err as { code?: unknown } | null)?.code !== 'permission-denied' || !authUser) throw err;
      try {
        await authUser.getIdToken(true);
        await write();
      } catch {
        showVerifyToVote();
      }
    }
  };

  const handleVoteOnReview = async (reviewId: string, voteType: 'helpful' | 'unhelpful', forReply = false) => {
    if (!authUser || !product || !firestore) return;
    if (voteNeedsVerifiedEmail()) return;
    const reviewRef = doc(firestore, 'products', product.id, 'reviews', reviewId);
    const review = reviews.find(r => r.id === reviewId);
    if (!review) return;
    const helpfulField = forReply ? 'replyHelpfulUids' : 'helpfulUids';
    const unhelpfulField = forReply ? 'replyUnhelpfulUids' : 'unhelpfulUids';
    const currentHelpful = (forReply ? review.replyHelpfulUids : review.helpfulUids) ?? [];
    const currentUnhelpful = (forReply ? review.replyUnhelpfulUids : review.unhelpfulUids) ?? [];
    const alreadyVotedThis = voteType === 'helpful' ? currentHelpful.includes(authUser.uid) : currentUnhelpful.includes(authUser.uid);
    await writeVote(() => updateDoc(reviewRef, {
        [helpfulField]: voteType === 'helpful'
            ? (alreadyVotedThis ? arrayRemove(authUser.uid) : arrayUnion(authUser.uid))
            : arrayRemove(authUser.uid),
        [unhelpfulField]: voteType === 'unhelpful'
            ? (alreadyVotedThis ? arrayRemove(authUser.uid) : arrayUnion(authUser.uid))
            : arrayRemove(authUser.uid),
    }));
  };

  const handleVoteOnGuestReview = async (reviewId: string, voteType: 'helpful' | 'unhelpful') => {
    if (!authUser || !product || !firestore) return;
    if (voteNeedsVerifiedEmail()) return;
    const reviewRef = doc(firestore, 'products', product.id, 'guestReviews', reviewId);
    const review = guestReviews.find(r => r.id === reviewId);
    if (!review) return;
    const currentHelpful = review.helpfulUids ?? [];
    const currentUnhelpful = review.unhelpfulUids ?? [];
    const alreadyVotedThis = voteType === 'helpful' ? currentHelpful.includes(authUser.uid) : currentUnhelpful.includes(authUser.uid);
    await writeVote(() => updateDoc(reviewRef, {
        helpfulUids: voteType === 'helpful'
            ? (alreadyVotedThis ? arrayRemove(authUser.uid) : arrayUnion(authUser.uid))
            : arrayRemove(authUser.uid),
        unhelpfulUids: voteType === 'unhelpful'
            ? (alreadyVotedThis ? arrayRemove(authUser.uid) : arrayUnion(authUser.uid))
            : arrayRemove(authUser.uid),
    }));
  };

    const getCategoryDisplay = () => {
        const { category, subcategory } = product.details;
        if (subcategory && subcategory !== 'All Subcategories' && subcategory === 'Other') {
            return `${category} > Other`;
        }
        if (subcategory && subcategory !== 'All Subcategories') {
            return subcategory;
        }
        return category;
    };
    
    // How many access links the product delivers. The links themselves are not
    // on this document: they live in the seller-only delivery document, and a
    // buyer receives them from the download dialog's manifest request
    // (src/lib/product-access-links.ts). This count is what the spec table
    // shows and what tells the dialog to expect them.
    const linkCount = accessLinkCount(product.details);
    const hasAccessLinks = linkCount > 0;
    // The seller's optional public preview page (src/lib/live-preview-link.ts),
    // or null when there is none or it is not a link that is safe to render.
    const livePreviewUrl = resolveLivePreviewUrl(product.details);
    // Files this buyer may actually download, via the SAME function /api/download
    // gates on (src/lib/tier-entitlement.ts) rather than a second copy of the
    // rule. The copy that used to live here diverged in two ways that both ended
    // with the dialog listing files the server then refused: it flattened every
    // tier's bucket on a legacy per-tier `files` product, and it served the whole
    // pool when a tiered product had no `tierFiles` map at all.
    //
    // This is the page-level answer — it drives the file count, the details table
    // and whether a Download button renders at all, so it has to be synchronous.
    // The dialog re-resolves the same list server-side on open, which is what
    // covers a GUEST buyer of a tiered product: their tier lives on a
    // guestPurchases doc they cannot read, so nothing here can scope them.
    const downloadableFiles: Array<{ name: string; size?: number; path?: string }> =
        downloadCandidates(product, product.purchasedTierId).candidates;
    // Whether the list above is a real answer or the ungated fallback.
    //
    // A tiered product with no known tier resolves to the WHOLE pool
    // (`legacy-order` in tier-entitlement.ts) — the deliberate fail-open that
    // keeps a pre-tier purchase downloadable. That is the right call for the
    // enforcement point, which knows the tier is genuinely absent, but here it
    // is indistinguishable from "the tier just hasn't loaded", and it is the
    // reason an unscoped list can reach the dialog. Only the server can settle
    // it (a guest's tier lives on a guestPurchases doc they cannot read), so
    // the dialog is told to wait for the manifest rather than trust this.
    const tierScopeResolved = product.pricingModel !== 'tiered' || product.purchasedTierId != null;
    // Named only when this buyer's list is actually narrowed to a tier, so the
    // dialog says "the files your tier includes" instead of implying a product
    // ships fewer files than it does.
    const purchasedTierName = product.pricingModel === 'tiered'
        ? tierDisplayName(product.priceTiers, product.purchasedTierId)
        : '';
    
    // NEVER the words "No Refunds". supa.place is merchant of record, so a blanket
    // no-refunds statement is OUR representation about the buyer's rights, and it
    // is not true: the seller's policy governs the self-service path only, and
    // every buyer keeps a route to a refund by escalating to support. The label
    // below says exactly that much and the disclaimer beside it says the rest.
    //
    // A custom policy shows its ENFORCED window, not just its name, because the
    // free-text description and the server's window used to be able to disagree.
    const getRefundPolicyLabel = (policy = product.details.refundPolicy) => {
        if (!policy?.enabled) return 'Seller does not offer self-service refunds';
        if (policy.type === 'custom') {
            const days = refundWindowDaysFor(policy);
            const name = policy.name || 'Custom policy';
            return days ? `${name} (${days}-day window)` : name;
        }
        return refundPolicies.find(p => p.value === policy.type)?.label;
    }

    // --- What this buyer can do with each tier ------------------------------
    //
    // A buyer holds ONE entitlement per product, so owning a tier does not put
    // the other tiers up for sale — the only supported move between tiers is a
    // subscription switching at its billing boundary. resolveTierAction is the
    // client mirror of the server rules; everything below renders from it so no
    // tier ever offers an action checkout would refuse.

    // The subscription this page can actually switch, or null. Canceled and
    // incomplete ones are excluded: neither reaches another billing boundary.
    const switchableSubscription = productSubscription && canChangeTier(productSubscription)
        ? {
            tierId: productSubscription.tierId ?? '',
            ...(productSubscription.pendingTierId ? { pendingTierId: productSubscription.pendingTierId } : {}),
        }
        : null;

    // Tier of the buyer's live entitlement, or null when they hold none. A live
    // subscription is the fresher source (a switch that lands mid-session moves
    // it, while the entitlement is read once on mount), so it wins where both
    // exist. '' means "owned, but no tier recorded" — a legacy order — which
    // deliberately matches no tier rather than guessing one.
    const hasLiveEntitlement = !!(product.isPurchased || product.isTieredPurchase);
    const ownedTierId = switchableSubscription
        ? switchableSubscription.tierId
        : hasLiveEntitlement
            ? (product.purchasedTierId != null ? String(product.purchasedTierId) : '')
            : null;

    // A cancelled subscription still inside its paid-for cycle holds the
    // entitlement, but only until `accessUntil`. That makes the buyer's own tier
    // re-takeable and every other tier blocked temporarily rather than for good.
    const entitlementLapsing = !!subscriptionAccess && !subscriptionAccess.expired;

    const tierActionFor = (tier: { id: number | string; purchaseType?: string; subscriptionFrequency?: string }) =>
        resolveTierAction({
            tier: { id: String(tier.id), recurring: isSubscriptionTier(tier) },
            ownedTierId,
            subscription: switchableSubscription,
            entitlementLapsing,
        });

    // The tier this buyer actually holds, when one of the live tiers is it.
    // Null for a buyer who owns nothing, and also for a legacy order whose
    // entitlement recorded no tier (ownedTierId '') or one whose tier the seller
    // has since removed from the price list — in both cases there is no row to
    // lift out, so the list renders exactly as it always did.
    const ownedTier = product.pricingModel === 'tiered'
        ? product.priceTiers?.find(t => isOwnedTierAction(tierActionFor(t).kind)) ?? null
        : null;

    // The tier the buyer has ticked, if any. An owner starts with NOTHING
    // ticked: their own tier is not a checkbox (it is not on offer), and
    // pre-ticking one of the others would present a tier they have not chosen
    // as the one they are about to act on.
    const selectedTier = product.pricingModel === 'tiered'
        ? product.priceTiers?.find(t => t.id === selectedTierId)
        : undefined;

    // The tier every "what happens if I act now" question is answered against.
    // With nothing ticked that is the tier the buyer holds, which is what keeps
    // an owner's Download/Resubscribe actions and their billing row on screen
    // while the checkbox column sits empty. Ticking another tier moves the
    // question to that tier; unticking it moves it back.
    const contextTier = selectedTier ?? ownedTier ?? undefined;

    // A tiered product is a subscription purchase only when THIS tier is
    // recurring, so the CTA, the details table, and the recurring note all
    // follow it rather than the product's pricing model.
    const isRecurringPurchase = product.pricingModel === 'subscription' || isSubscriptionTier(contextTier);
    const billingCadence = product.pricingModel === 'subscription'
        ? product.subscriptionFrequency
        : tierFrequency(contextTier);

    // What the tier in play actually ships, for the spec table.
    //
    // Deliberately NOT downloadableFiles: that one answers the entitlement
    // question (what may this buyer pull right now) and is pinned to the tier
    // they bought, which is right for the Download button and wrong for a spec
    // table. The table describes whichever tier is in play — the ticked one, or
    // the buyer's own when nothing is ticked — so a shopper comparing tiers sees
    // each one's file count and size as they click through, instead of the
    // whole product's totals on every tier.
    const detailFiles = downloadCandidates(product, contextTier?.id ?? null).candidates;
    // A pool file always carries `size`; a legacy row can be missing it, and
    // summing those would quietly understate the tier. Fall back to the
    // product's stored total in that case, which is at least an honest figure
    // even though it covers the whole product.
    const detailBytesKnown = detailFiles.every(f => typeof f.size === 'number');
    const detailBytes = detailFiles.reduce((sum, f) => sum + (f.size ?? 0), 0);

    let detailItems: (false | { label: string; value: React.ReactNode; })[] = [];

    // The Product ID is pushed LAST, after the tags: it is a reference for a
    // support request, not something a buyer weighs, so it no longer leads the
    // table it used to open.
    detailItems.push({ label: 'Seller', value: liveSellerName || product.seller });

    // Which tier the rows below describe. Without this the numbers change as the
    // buyer clicks between tiers with nothing saying why.
    if (product.pricingModel === 'tiered' && contextTier) {
        detailItems.push({ label: 'Tier', value: contextTier.name });
    }

    // Recurrence belongs in the spec table too, not only in the price suffix.
    // This is the row a buyer scans to answer "how often am I charged?", and it
    // is the one place a tiered product states the cadence of the tier they have
    // actually selected.
    if (isRecurringPurchase) {
        detailItems.push({ label: 'Billing', value: `Recurring, ${formatRecurring(billingCadence)}` });
    }

    if (hasAccessLinks) {
        detailItems.push({ label: 'Links', value: linkCount });
    }
    if (detailFiles.length > 0) {
        detailItems.push({
            label: 'Total File Size',
            value: detailBytesKnown ? formatFileSize(detailBytes) : (product.details.fileSize || 'N/A'),
        });
        detailItems.push({ label: 'File Count', value: detailFiles.length });
    }
    // The PDF's length, counted when the seller made its page previews.
    const pdfPages = pdfPageCountOf(product.pdfPageCount);
    if (pdfPages && pagePreviewsOf(product.pagePreviews).length > 0) {
        detailItems.push({ label: 'Pages', value: pageCountLabel(pdfPages) });
    }
    // A teaching resource's classroom details, named the visitor's way.
    const classroom = categoryFieldsOf(product);
    for (const field of TEACHING_FIELDS) {
        const slugs = classroom[field.key];
        if (slugs && slugs.length > 0) {
            detailItems.push({ label: fieldName(field, schoolSystem), value: fieldValueSummary(field, slugs, schoolSystem) });
        }
    }
    // What the product is and what it is for (src/lib/catalog-taxonomy.ts),
    // read through effectiveTaxonomyOf, the marketplace's own reader, so this
    // page and the purpose bar always agree, including for a product saved
    // before the fields existed.
    const taxonomy = effectiveTaxonomyOf(product);
    // A resale pack's seller answers for work they usually did not make.
    const resalePack = isResalePack(taxonomy.purposes);
    // Resell rights and AI-assisted, beside the name in the seller row.
    const listingBadges = listingBadgesOf({ aiUse: product.aiUse, purposes: taxonomy.purposes });
    // The seller's AI answer, whichever it is (src/lib/ai-disclosure.ts).
    const aiUse = aiUseOf(product.aiUse);
    if (aiUse) detailItems.push({ label: 'AI use', value: aiUseCopyOf(resalePack)[aiUse].detail });

    const releaseDateStr = getFormattedDateOnly(product.details?.releaseDate ?? (product as any).createdAt);
    detailItems.push({ label: 'Release Date', value: releaseDateStr || 'N/A' });
    const modifiedDateStr = getFormattedDateOnly(product.details?.modifiedDate);
    if (modifiedDateStr) detailItems.push({ label: 'Last Modified', value: modifiedDateStr });
    const langLabel = product.details?.language?.label || product.details?.language?.value;
    if (langLabel) detailItems.push({ label: 'Language', value: langLabel });

    // An owner's refund follows the policy recorded on their purchase (see
    // refundActionAvailable), which the seller may have changed since. Stating
    // the listing's current policy to them put "Seller does not offer
    // self-service refunds" beside a working Request Refund button, so when the
    // two differ the owner is shown the one they bought under.
    const recordedPolicy = product.isPurchased && purchaseRecord?.productId === product.id
        ? purchasedRefundPolicy(purchaseRecord.data, product.id, null)
        : null;
    const livePolicy = product.details.refundPolicy;
    const samePolicy = (a: typeof recordedPolicy, b: typeof livePolicy) =>
        (a?.enabled === true) === (b?.enabled === true)
        && (a?.type ?? '') === (b?.type ?? '')
        && Number(a?.customDays ?? 0) === Number(b?.customDays ?? 0);
    const shownPolicy = recordedPolicy && !samePolicy(recordedPolicy, livePolicy) ? recordedPolicy : null;
    const policyLabel = shownPolicy
        ? `${getRefundPolicyLabel(shownPolicy as typeof livePolicy)} (when you bought)`
        : getRefundPolicyLabel();
    // The custom policy's name and description belong to the live listing.
    const customPolicy = !shownPolicy && livePolicy?.type === 'custom' ? livePolicy : null;
    detailItems.push({
        label: 'Refund Policy',
        value: (
            <div className="flex items-center gap-1">
                {policyLabel}
                {/* A refund returns ONE charge, not the subscription: cancelling
                    is what stops the next one. Without this the policy reads as
                    if it covered everything billed so far. */}
                {isRecurringPurchase && (shownPolicy ?? livePolicy)?.enabled && (
                    <span className="text-xs text-muted-foreground">per charge</span>
                )}
                {/* The disclaimer that makes the label above honest. Shown on
                    EVERY listing, not only the no-refunds ones: the seller's
                    policy is the intended path, and support is the path that
                    always exists. A buyer who cannot see a route here goes to
                    their bank instead, which costs a dispute fee on a case we
                    would often have refunded. */}
                <Popover>
                    <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-5 w-5" aria-label="How refunds work">
                            <Info className="h-4 w-4" aria-hidden />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 text-sm space-y-2">
                        <p className="font-medium">You can always ask for a refund.</p>
                        <p className="text-muted-foreground">
                            The seller sets the policy above, which is the usual route and the
                            fastest one. If it does not cover you, or the window has closed, you can
                            still ask our support team for a refund and we will review it ourselves.
                        </p>
                        <p className="text-muted-foreground">
                            Files that are faulty, corrupted, or not as described are always
                            refundable, whatever the seller&apos;s policy says.
                        </p>
                        <p className="text-muted-foreground">
                            If the seller does not respond within 14 days, the request comes to us
                            automatically.
                        </p>
                        <p>
                            <Link href="/terms#refund-policy" className="text-link underline decoration-link/40 underline-offset-4 hover:decoration-link">
                                Read the full refund policy
                            </Link>
                        </p>
                    </PopoverContent>
                </Popover>
                {customPolicy && (
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-5 w-5" aria-label="Refund policy details"><Info className="h-4 w-4" aria-hidden /></Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80">
                            <div className="grid gap-4">
                                <div className="space-y-2">
                                    <h4 className="leading-none">{customPolicy.name}</h4>
                                    <p className="text-sm text-muted-foreground">{customPolicy.description}</p>
                                </div>
                            </div>
                        </PopoverContent>
                    </Popover>
                )}
            </div>
        )
    });

    // The type and purposes (read above), each a link to that view of the
    // marketplace. Other is left out: it tells a buyer nothing.
    const typeEntry = taxonomy.catalogType === OTHER_SLUG ? null : catalogTypeEntry(taxonomy.catalogType);
    if (typeEntry) {
        detailItems.push({
            label: 'Type',
            value: (
                <Link href={`/marketplace?type=${typeEntry.slug}`} className={TAXONOMY_LINK}>
                    {typeEntry.label}
                </Link>
            ),
        });
    }
    const purposeEntries = taxonomy.purposes
        .filter(slug => slug !== OTHER_SLUG)
        .map(slug => catalogPurposeEntry(slug))
        .filter((entry): entry is NonNullable<typeof entry> => entry !== null);
    if (purposeEntries.length > 0) {
        detailItems.push({
            label: purposeEntries.length > 1 ? 'Purposes' : 'Purpose',
            value: (
                <ul className="flex flex-wrap gap-1.5">
                    {purposeEntries.map(entry => (
                        <li key={entry.slug}>
                            <Link href={`/marketplace?purpose=${entry.slug}`} className={TAXONOMY_LINK}>
                                {entry.label}
                            </Link>
                        </li>
                    ))}
                </ul>
            ),
        });
    }

    // The seller's tags as the crawlable tag links on this page read them
    // (catalog-tags.ts): never the "discounted" marker the discount engine
    // keeps in the same list, which is a price state shown by the price itself.
    const productTagList = catalogTagsOf(product);
    if (productTagList.length > 0) {
        detailItems.push({
            label: 'Tags',
            value: (
                // Each tag links to its tag page, and sits ON the page rather
                // than in a popover. These are the product page's crawlable
                // links into the tag pages: a closed popover renders nothing
                // into the HTML, so before the page was rendered on the server
                // only the text stand-in that preceded it carried them.
                // catalogTagsOf only returns tags that have a page.
                <ul className="flex flex-wrap gap-1.5">
                    {productTagList.map(tag => (
                        <li key={tag}>
                            <Link
                                href={`/tag/${tagSlug(tag)}`}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-ink/10 text-link border border-ink/20 hover:bg-ink/20 transition-colors"
                            >
                                {tag}
                            </Link>
                        </li>
                    ))}
                </ul>
            )
        });
    }

    detailItems.push({ label: 'Product ID', value: product.id });

    const displayedDetails = detailItems.filter(Boolean) as { label: string; value: React.ReactNode; }[];
    
    const handlePwywChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[e\-+]/gi, "");
        if (/^[\d.]*$/.test(value)) {
            setPwywPrice(value);
        }
    };
    // Tick / untick a tier.
    //
    // Unticking is only offered to a buyer who OWNS a tier, because they have
    // something to fall back to: with nothing ticked, contextTier is their own
    // tier and the page answers for that. A buyer who owns nothing must always
    // have exactly one tier in play — clearing the selection there would leave
    // the buy CTA with no tier and no price — so for them the checkboxes stay
    // radio-like, exactly as before.
    const toggleTier = (tier: { id: number }, selectable: boolean) => {
        if (!selectable) return;
        if (selectedTierId === tier.id) {
            if (ownedTier) setSelectedTierId(null);
            return;
        }
        setSelectedTierId(tier.id);
    };

    // One tier row. Shared by both tier containers below so the owned tier and
    // the purchasable ones stay identical in everything except where they sit —
    // the split is about grouping, not about two different renderings of a tier.
    //
    // `interactive` is false for the row in the "Your tier" container: that tier
    // is not on offer, so it carries no checkbox, and no frame of its own inside
    // the frame already around it.
    const renderTierRow = (tier: any, { interactive, showReason = true }: { interactive: boolean; showReason?: boolean }) => {
        const action = tierActionFor(tier);
        const badge = tierActionBadge(action.kind);
        // An unavailable tier stays visible and readable — the buyer
        // may well be deciding what to ask the seller for — but is
        // dimmed and unselectable, so it can never become the CTA.
        const selectable = action.kind !== 'unavailable';
        // The whole card is the checkbox's <label>, so a click anywhere on it
        // ticks the tier, and the tier's name and price are the checkbox's
        // accessible name. It used to point at `String(tier.id)`, an id nothing
        // carried, which labelled nothing: the card did not respond and the
        // checkbox had no name. Prefixed, because a bare number is easily
        // someone else's id. The browser forwards a click on the label to the
        // checkbox and not a click ON the checkbox, so each click toggles once,
        // and a disabled checkbox ignores the forwarded click.
        //
        // The "Your tier" row has no checkbox, so it is a plain element: a
        // label with nothing to point at would label its first labelable
        // descendant instead, the View Details button, and toggle that on every
        // click. It takes Label's typography so the two rows still match.
        const checkboxId = `tier-${tier.id}`;
        const RowRoot: React.ElementType = interactive ? Label : 'div';
        const tierLicence = tierLicenceOf(tier);
        return (
            <Collapsible key={tier.id} asChild>
                <RowRoot {...(interactive ? { htmlFor: checkboxId } : {})} className={cn("flex flex-col transition-all", !interactive && "text-sm font-medium leading-none", interactive && "p-4 border rounded-lg", selectable && interactive ? "cursor-pointer" : "cursor-default", !selectable && "opacity-60", interactive && selectedTierId === tier.id && selectable && "bg-ink/5 border-ink ring-2 ring-ink")}>
                    <div className="flex justify-between items-start w-full">
                        <div className="flex items-center gap-3 flex-wrap">
                            {interactive && (
                                <Checkbox id={checkboxId} checked={selectedTierId === tier.id} disabled={!selectable} onCheckedChange={() => toggleTier(tier, selectable)} />
                            )}
                            <span className="font-semibold text-lg">{tier.name}</span>
                            {isSubscriptionTier(tier) && (
                                <div className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-muted text-muted-foreground">
                                    <Repeat className="h-3 w-3" aria-hidden /> Recurring
                                </div>
                            )}
                            {badge && (
                                <div className={cn(
                                    "text-xs font-bold px-2 py-1 rounded-full",
                                    action.kind === 'pending'
                                        ? "bg-warning/15 text-warning"
                                        : "bg-ink text-ink-foreground",
                                )}>
                                    {badge}
                                </div>
                            )}
                        </div>
                        <div className="text-right flex items-baseline gap-1">
                            <p className="text-xl font-bold">{formatCurrency(tier.price)}</p>
                            {isSubscriptionTier(tier) && (
                                <span className="text-sm font-medium text-muted-foreground">{formatCycleSuffix(tierFrequency(tier))}</span>
                            )}
                            {/* Per tier, not once for the product: a free tier sitting
                                beside paid ones must not be qualified. */}
                            <VatNotice amount={tier.price} />
                        </div>
                    </div>
                    {action.kind === 'unavailable' && showReason && (
                        <p className={cn("text-xs text-muted-foreground mt-2", interactive && "pl-8")}>{action.reason}</p>
                    )}
                    {/* The tier's school licence (src/lib/tier-licences.ts), which
                        the buyer's licence certificate states once they own it. */}
                    {tierLicence && (
                        <p className={cn("text-sm text-muted-foreground mt-2 font-normal leading-snug", interactive && "pl-8")}>
                            <span className="font-medium text-foreground">{licenceName(tierLicence)}.</span>{' '}
                            {licenceSummary(tierLicence)}
                        </p>
                    )}
                    <CollapsibleContent className={cn("text-muted-foreground text-sm mt-2 prose-sm prose-invert max-w-none", interactive && "pl-8")}>
                        {/* `rich-description` is the hook the line and paragraph
                            spacing rules in globals.css key on: a tier
                            description has the spacing control too. The same
                            class is on the seller's previews in the create and
                            edit pages. */}
                        <div className="rich-description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(tier.description) }}/>
                    </CollapsibleContent>
                    <CollapsibleTrigger asChild>
                        <Button variant="link" size="sm" className={cn("group no-underline hover:underline hover:decoration-current text-muted-foreground self-start -ml-2 h-auto py-1 mt-1", interactive && "pl-8")}>
                            <span className="group-data-[state=open]:hidden">Show details</span>
                            <span className="hidden group-data-[state=open]:inline">Hide details</span>
                            <ChevronDownIcon className="h-4 w-4 transition-transform duration-300 group-data-[state=open]:rotate-180" />
                        </Button>
                    </CollapsibleTrigger>
                </RowRoot>
            </Collapsible>
        );
    };

    const getPricingComponent = () => {
        // The line above the price says what the buyer does here, in their
        // words. A one-off price needs no line: "Standard Pricing" named our
        // internal pricing model and told the buyer nothing.
        let pricingModelText = '';
        switch(product.pricingModel) {
            case 'standard': pricingModelText = ''; break;
            case 'tiered': pricingModelText = 'Choose a tier'; break;
            case 'pwyw': pricingModelText = 'Name your price'; break;
            case 'subscription': pricingModelText = 'Subscription'; break;
            case 'free': pricingModelText = 'Free'; break;
        }

        // A tier the buyer already holds is lifted out of the price list into
        // its own container. Left in place it sat in a column of prices being
        // weighed against each other, which reads as an offer — the one thing it
        // is not. Everything else stays in the list it was always in, so a buyer
        // who owns nothing sees exactly what they saw before.
        if (product.pricingModel === 'tiered' && ownedTier) {
            const otherTiers = (product.priceTiers ?? []).filter(t => t.id !== ownedTier!.id);
            // When every remaining tier is blocked for the SAME reason — which is
            // the ordinary case, since one entitlement per product blocks them all
            // alike — say it once above the group instead of repeating it on
            // every row.
            const otherReasons = otherTiers.map(t => tierActionFor(t)).map(a => a.kind === 'unavailable' ? a.reason : '');
            const sharedReason = otherReasons.length > 0 && otherReasons.every(r => r && r === otherReasons[0])
                ? otherReasons[0]
                : '';
            return (
                <div className="space-y-4">
                    <div>
                        <p className="text-sm text-muted-foreground mb-1">Your tier</p>
                        <Card className="bg-ink/5 p-4 rounded-lg border-ink/40">
                            {/* No checkbox: this tier is not on offer, it is already
                                theirs. It is also the tier every action below falls
                                back to (contextTier), so nothing needs to be ticked
                                to get back here — unticking whatever else they were
                                looking at does it. */}
                            {renderTierRow(ownedTier!, { interactive: false })}
                        </Card>
                    </div>
                    {otherTiers.length > 0 && (
                        <div>
                            <p className="text-sm text-muted-foreground mb-1">
                                {switchableSubscription ? 'Switch to another tier' : 'Other tiers'}
                            </p>
                            {sharedReason && (
                                <p className="text-xs text-muted-foreground mb-2">{sharedReason}</p>
                            )}
                            <Card className="bg-card/80 p-4 rounded-lg border">
                                <div className="space-y-3">
                                    {otherTiers.map(tier => renderTierRow(tier, { interactive: true, showReason: !sharedReason }))}
                                </div>
                            </Card>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div>
                {pricingModelText && (
                    product.pricingModel === 'pwyw' ? (
                        <label htmlFor="pwyw-price" className="block text-sm text-muted-foreground mb-1">
                            {pricingModelText}
                        </label>
                    ) : (
                        <p className="text-sm text-muted-foreground mb-1">
                            {pricingModelText}
                        </p>
                    )
                )}
                 <Card className="bg-card/80 p-4 rounded-lg border">
                     {product.pricingModel === 'standard' && (
                        <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-bold">{formatCurrency(product.discountedPrice || product.price)}</span>
                            {product.discountedPrice && (<span className="text-xl font-medium text-muted-foreground line-through">{formatCurrency(product.price)}</span>)}
                            <VatNotice amount={product.discountedPrice || product.price} className="text-sm" />
                        </div>
                    )}
                    {product.pricingModel === 'subscription' && (
                        <div className="flex items-baseline gap-2">
                            <span className="text-4xl font-bold">{formatCurrency(product.subscriptionPrice ?? product.price)}</span>
                            <span className="text-xl font-medium text-muted-foreground">{formatCycleSuffix(product.subscriptionFrequency)}</span>
                            <VatNotice amount={product.subscriptionPrice ?? product.price} className="text-sm" />
                        </div>
                    )}
                    {product.pricingModel === 'tiered' && (
                         <div className="space-y-3">
                            {product.priceTiers?.map(tier => renderTierRow(tier, { interactive: true }))}
                        </div>
                    )}
                     {product.pricingModel === 'pwyw' && (
                        <div>
                            <div className="relative flex-1 mt-1">
                                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">{currencySymbol}</div>
                                {/* min/max are attributes on what the buyer TYPES, so they are
                                    the display-currency form of the AUD bounds enforced below
                                    and on the server. */}
                                <Input id="pwyw-price" type="number" min={toDisplayAmount(Math.max(PRICE_MIN_AUD, product.pwywMinPriceEnabled ? (product.pwywMinPrice ?? 0) : 0))} max={toDisplayAmount(PRICE_MAX_AUD)} value={pwywPrice} onChange={handlePwywChange} placeholder={'Enter amount'} className="pl-10 text-lg md:text-lg font-bold h-12" />
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {/* The whole cent the server takes, not a raw floor that
                                    rounds down to a cent it refuses. See wholeCentFloorAud. */}
                                Minimum: {pwywMinimumShown(Math.max(PRICE_MIN_AUD, product.pwywMinPriceEnabled ? (product.pwywMinPrice ?? 0) : 0))}{' '}
                                {/* Whatever the buyer names is exclusive of VAT, so the
                                    qualifier belongs on the amount they are choosing, not
                                    only on the floor. */}
                                <VatNotice amount={1} />
                            </p>
                            {/* The buyer names a number in their own display currency,
                                but the product is PRICED in AUD and the bounds below are
                                AUD, so the AUD it converts to is stated before they commit.
                                The checkout then converts that AUD total back into whatever
                                currency their card is charged in. */}
                            {!isBaseCurrency && Number.isFinite(pwywAud) && pwywAud > 0 && (
                                <p className="text-xs text-muted-foreground mt-0.5 tabular-nums">Charged as {formatBaseAmount(pwywAud)}.</p>
                            )}
                            {!!pwywPrice && pwywAud < Math.max(PRICE_MIN_AUD, product.pwywMinPriceEnabled ? (product.pwywMinPrice ?? 0) : 0) && (
                                <p className="text-xs text-destructive mt-0.5">Amount is below the minimum.</p>
                            )}
                            {!!pwywPrice && pwywAud > PRICE_MAX_AUD && (
                                <p className="text-xs text-destructive mt-0.5">Amount is above the {formatCurrency(PRICE_MAX_AUD)} maximum.</p>
                            )}
                        </div>
                     )}
                      {product.pricingModel === 'free' && <span className="text-4xl font-bold">{formatCurrency(0)}</span>}
                </Card>
              </div>
        )
    }

    // What the tier in play offers this buyer. Non-tiered products have no
    // tiers, so they keep the plain owned/not-owned split they always had.
    const selectedTierAction = contextTier ? tierActionFor(contextTier) : null;
    const pendingTierChange = productSubscription ? readPendingTierChange(productSubscription) : null;

    // The buy/subscribe CTA is shown ONLY when there is genuinely something to
    // buy. Previously any tier other than the purchased one qualified, which
    // offered an Add to Cart on tiers the server refuses (one entitlement per
    // product) and on tiers a subscriber can only SWITCH to. Those two cases now
    // render their own actions below instead.
    //
    // A WITHDRAWN listing offers nothing to buy, whatever the tier logic says.
    // Only an owner or the seller can still reach a product page that is not
    // live (archived, deleted, taken down), and for a TIERED one the tier they
    // do not own reads as a legitimate 'buy' action, so without this an owner
    // would be shown an Add to Cart that the checkout callable refuses on the
    // same status gate. Same for the tier switch and the resubscribe action
    // below: there is nothing to switch to or resubscribe to.
    const isWithdrawnListing = isOffSale(product.status);
    const showPurchaseActions = !isWithdrawnListing && (selectedTierAction
        ? selectedTierAction.kind === 'buy'
        : !product.isPurchased && !product.isTieredPurchase);
    // The amount the sticky mobile bar repeats. In AUD, like every other price
    // on the page, and handed to formatCurrency/VatNotice which do the display
    // conversion. PWYW reads the buyer's own typed figure once it clears the
    // floor and the minimum until then, so the bar never advertises a number
    // checkout would refuse.
    // Whole cent, like the minimum under the field (see wholeCentFloorAud).
    const pwywFloorAud = wholeCentFloorAud(Math.max(PRICE_MIN_AUD, product.pwywMinPriceEnabled ? (product.pwywMinPrice ?? 0) : 0));
    // A pay-what-you-want amount the checkout would refuse. The inline block
    // says why right under the field, but the bar only exists once that field
    // has scrolled away, so the bar has to carry the reason itself rather than
    // show a disabled button with the explanation off screen.
    const stickyBarPwywUnset = product.pricingModel === 'pwyw' && (
        !pwywPrice || !Number.isFinite(pwywAud) || pwywAud < pwywFloorAud || pwywAud > PRICE_MAX_AUD
    );
    const stickyBarVatAmount =
        product.pricingModel === 'tiered' ? (selectedTier?.price ?? product.price)
        : product.pricingModel === 'subscription' ? (product.subscriptionPrice ?? product.price)
        : product.pricingModel === 'pwyw' ? pwywAud
        : product.pricingModel === 'free' ? 0
        : (product.discountedPrice || product.price);
    // The label above the number, so "Your price" never sits over the platform
    // minimum the buyer did not choose.
    const stickyBarLabel =
        product.pricingModel === 'pwyw'
            ? (stickyBarPwywUnset ? 'Name your price' : 'Your price')
            : 'Price';
    const stickyBarPrice =
        product.pricingModel === 'tiered' && !selectedTier ? 'Choose a tier'
        : stickyBarPwywUnset ? (pwywAud > PRICE_MAX_AUD
            ? `Max ${formatCurrency(PRICE_MAX_AUD)}`
            : `Min ${pwywMinimumShown(pwywFloorAud)}`)
        : formatCurrency(stickyBarVatAmount);

    const stickyBuyButton = (className?: string) => (
      isRecurringPurchase && subscriptionGate ? (
        <Button className={className} asChild>
          <Link href={subscriptionGateHref(subscriptionGate, `/product/${product.id}`)}>
            {SUBSCRIPTION_GATE_COPY[subscriptionGate].title}
          </Link>
        </Button>
      ) : (
        // A pay-what-you-want amount that is not set yet leaves the button
        // ENABLED and sends the buyer to the field instead of refusing.
        // Disabling it here would be a dead end: the field and the reason are
        // both off screen, which is the only reason these bars exist. Everything
        // else keeps the inline button's own disabled rule, and handleAddToCart
        // re-checks the bounds regardless.
        <Button
          variant="brand"
          className={className}
          onClick={(e) => {
            if (stickyBarPwywUnset) {
              const field = document.getElementById('pwyw-price');
              field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              (field as HTMLInputElement | null)?.focus({ preventScroll: true });
              return;
            }
            // Forwarded so the flight launches from this bar's button, wherever
            // the bar is pinned, rather than from the inline CTA off screen.
            handleAddToCart(e);
          }}
          disabled={isRecurringPurchase && isUserLoading}
        >
          {stickyBarPwywUnset ? 'Name your price' : isRecurringPurchase ? 'Subscribe' : 'Add to cart'}
        </Button>
      )
    );

    // `amount` drives nothing but the qualifier's presence; the number itself is
    // rendered beside it. Null while no tier is picked and while a
    // pay-what-you-want amount is still unset: the figure shown then is a bound,
    // not a price, and qualifying a bound would claim VAT on top of a minimum.
    const stickyBarVatProp =
      (product.pricingModel === 'tiered' && !selectedTier) || stickyBarPwywUnset
        ? null
        : stickyBarVatAmount;

    // The per-cycle suffix, which belongs to every surface repeating the price.
    const stickyBarCycleSuffix = (
      <>
        {product.pricingModel === 'subscription' && (
          <span className="font-normal text-muted-foreground">
            {formatCycleSuffix(product.subscriptionFrequency)}
          </span>
        )}
        {selectedTier && isSubscriptionTier(selectedTier) && (
          <span className="font-normal text-muted-foreground">
            {formatCycleSuffix(tierFrequency(selectedTier))}
          </span>
        )}
      </>
    );

    // Switching an existing subscription to the selected recurring tier, and
    // undoing a switch already scheduled to it.
    const showTierSwitchAction = !isWithdrawnListing && selectedTierAction?.kind === 'switch';
    const showTierSwitchPending = selectedTierAction?.kind === 'pending';
    // Starting again after cancelling, while the paid-for cycle is still
    // running. The buyer keeps Download and Review throughout, so this is an
    // extra action in the owned branch rather than a return to the CTA: the
    // question it answers is "I changed my mind", not "how do I buy this".
    // Tiered products offer it only on the buyer's own tier, which is the only
    // one checkout will take against a live entitlement.
    const showResubscribe = !isWithdrawnListing && entitlementLapsing && (
        selectedTierAction ? selectedTierAction.kind === 'resubscribe' : isRecurringPurchase
    );

    // Re-decided here rather than trusting `product.canRefund` alone: by this
    // point the buyer's entitlement has resolved, so the tier they hold and the
    // amount they paid are known, and both narrow the product-level answer. A
    // guest keeps the product-level one, since their entitlement is a doc they
    // cannot read. The server re-checks all of it either way.
    //
    // The POLICY is the one the buyer bought under, which is what submitDispute
    // enforces: recorded on their purchase line at fulfilment, with the live
    // listing only for purchases made before lines carried one. A guest cannot
    // read their purchase doc, so getGuestRefundStatus hands back their line's
    // policy and the guest poll stores it the same way. Reading the live listing alone
    // hid the button from every earlier buyer the moment a seller switched to
    // "No refunds", though the server would still take their request, and
    // offered it on a line recorded with refunds off.
    const purchasedPolicy = purchasedRefundPolicy(
        purchaseRecord?.productId === product.id ? purchaseRecord.data : undefined,
        product.id,
        product,
    );
    const refundActionAvailable = canRequestRefund({
        pricingModel: product.pricingModel,
        price: product.price,
        subscriptionPrice: product.subscriptionPrice,
        priceTiers: product.priceTiers,
        purchasedTierId: product.purchasedTierId,
        pricePaid: purchasePricePaid,
        refundPolicyEnabled: purchasedPolicy?.enabled === true,
    });

    // Whether to offer the "ask supa.place to review this" route, which is a
    // SEPARATE question from whether the seller's own self-service refund is on.
    //
    // The seller's policy governs the self-service button. It does not, and
    // cannot, govern the escalation: supa.place is merchant of record, the Terms
    // promise every buyer a path to a refund, and statutory rights survive any
    // policy — files that are faulty or not as described are always refundable,
    // and an EU/UK buyer whose immediate-delivery consent was never recorded
    // keeps an unconditional 14-day withdrawal right no matter what the listing
    // says (see src/lib/withdrawal-right.ts).
    //
    // Before this, a "No Refunds" listing hid the whole menu entry, so that buyer
    // saw no control at all. The server had a good message for exactly this case
    // (refundWindowClosedMessage's `refunds_disabled` branch names support@ and
    // states the faulty-files carve-out) but the client never let the call
    // through, so it was unreachable copy. A buyer with no visible route goes to
    // their card issuer instead, which costs a dispute fee and a scheme-ratio hit
    // on a case we would frequently have refunded.
    //
    // Gated on the purchase having involved MONEY, not on the policy: a free
    // product has nothing to reverse and the server rejects those outright.
    const refundEscalationAvailable = purchaseWasPaid({
        pricingModel: product.pricingModel,
        price: product.price,
        subscriptionPrice: product.subscriptionPrice,
        priceTiers: product.priceTiers,
        purchasedTierId: product.purchasedTierId,
        pricePaid: purchasePricePaid,
    });

    const currentMedia = allDisplayMedia[activeImageIndex];

    // Where the seller put their gallery, and what shape it is. See
    // src/lib/product-gallery.ts for why this lives on `details` rather than on
    // `design`, and for the height cap the top layout needs.
    // How this seller chose to separate their sections. `section` is the class
    // set for one content section; `header` restores, for the blocks ABOVE the
    // first section, the spacing the outline mode takes off the container so
    // that the boxes below can touch.
    const sectionSeparator = resolveSectionSeparator(product.details);
    const section = sectionSurfaceClasses(sectionSeparator);
    const header = sectionHeaderClasses(sectionSeparator);
    const sectionLayout = sectionLayoutClasses(sectionSeparator);
    // The gallery's own treatment. Its frame is already a bordered box, so in
    // outline mode the block around it becomes the section and the frame gives
    // up its border and corners. See galleryClasses.
    const gallery = galleryClasses(sectionSeparator);
    const flatCard = flattensInnerCard(sectionSeparator) ? FLAT_CARD_CLASSES : undefined;

    const titleBlock = <h1 className={cn("order-1 md:order-none font-seller-heading", header)}>{product.name}</h1>;

    const galleryIsFullWidth = isFullWidthGallery(product.details);
    // Two separate questions, and they are not the same one. Whether a frame is
    // rendered AT ALL is asked of each layout branch below; `galleryStacked`
    // decides where the name, seller and short description sit, and is true for
    // the hidden mode as well, because "no gallery" is the full-width page shape
    // with the picture taken out rather than a third arrangement of its own.
    // Reading both off one boolean is what would put the name and prose in the
    // right-hand column with nothing beside them.
    const galleryStacked = isStackedLayout(product.details);
    const galleryRatio = resolveGalleryRatio(product.details);
    // Whether the thumbnails go beside the frame instead of under it. True for
    // every full-width shape except page-width, which fills the content area
    // and so has no margin to put them in. Asked once and handed to the block,
    // the frame and the picker, so the three cannot disagree about which layout
    // this is.
    const galleryThumbsOnSide = galleryIsFullWidth
      && galleryThumbnailsOnSide(galleryRatio, allDisplayMedia.length);

    /**
     * Everything INSIDE the gallery frame. Built once because it is identical
     * in both layouts — only the frame around it changes — and because building
     * it twice is how the two layouts would drift apart.
     *
     * Constructed even when the seller has chosen no gallery, and then simply
     * never rendered. It carries the fullscreen viewer, the arrows and the
     * counter, so leaving it unmounted is what removes all of them together.
     */
    const galleryContent = (
      <>
        {galleryFilesCleared(product) ? (
          // Soft-deleted with no customers: onProductUpdated has already
          // cleared the storage prefix, so every media URL points at a gone
          // file. Render an explanation in place of the gallery so the seller
          // (the only viewer who can reach this product) sees why nothing loads.
          <div className="w-full h-full bg-muted flex items-center justify-center p-8">
            <p className="text-sm text-center text-muted-foreground max-w-xs leading-relaxed">
              This product was deleted and had no existing
              customers, so its files have been removed.
            </p>
          </div>
        ) : allDisplayMedia.length > 0 && currentMedia ? (
          currentMedia.type === 'image' ? (
            <>
              {!mediaLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-muted z-10">
                  <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                </div>
              )}
              {/* `object-contain`, not `cover`. The frame has a fixed shape and
                  the seller's images do not, so one of the two has to give.
                  Cropping was the old answer and it silently destroyed the
                  composition of every image that was not square — on the one
                  screen where the seller's work is the product. Letterboxing
                  shows what they actually made; the bars read as a frame
                  because the container carries a muted background. */}
              <Image
                key={currentMedia.src}
                src={currentMedia.src}
                alt={`${product.name} image ${activeImageIndex + 1}`}
                fill
                // The left gallery is one column of a two-column grid that tops
                // out at 1368px; the top gallery spans the whole of it. Without
                // an explicit value `fill` assumes 100vw at every breakpoint and
                // serves an oversized image to every visitor.
                sizes={galleryIsFullWidth ? '(max-width: 768px) 100vw, 1368px' : '(max-width: 768px) 100vw, 660px'}
                priority={activeImageIndex === 0}
                unoptimized={skipImageOptimizer(currentMedia.src)}
                className={cn('object-contain transition-all duration-300', !mediaLoaded && 'opacity-0')}
                data-ai-hint="futuristic ui"
                onLoad={() => setMediaLoaded(true)}
              />
            </>
          ) : (
            /* The gallery's own video branch. Routed through toEmbedUrl, the
               same normaliser the description editor uses, so the frame lands
               on a host the CSP actually allows, YouTube's tracking cookies are
               not set before anyone presses play, and a src it cannot parse
               renders a placeholder rather than a blocked frame. */
            (() => {
              const embed = toEmbedUrl(currentMedia.src);
              // toEmbedUrl decides the host; isAllowedEmbedUrl asserts it against
              // the same allowlist the description's iframes are held to, so a
              // gallery frame and a description frame cannot diverge.
              if (!embed || !isAllowedEmbedUrl(embed)) return <PlaceholderMedia />;
              return (
                <iframe
                  className="w-full h-full"
                  src={embed}
                  title={`${product.name} video`}
                  sandbox={EMBED_SANDBOX}
                  referrerPolicy="strict-origin-when-cross-origin"
                  allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                ></iframe>
              );
            })()
          )
        ) : (<PlaceholderMedia />)}
        <div className="absolute top-4 right-4 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 coarse:!opacity-100 transition-opacity"><Button variant="outline" size="icon" onClick={() => setIsFullScreen(true)} aria-label="Open fullscreen viewer"><Maximize className="h-5 w-5" aria-hidden /></Button></div>
        <Suspense>{isFullScreen && <FullscreenMedia media={allDisplayMedia} startIndex={activeImageIndex} onClose={() => setIsFullScreen(false)} />}</Suspense>
        {allDisplayMedia.length > 1 && (<><Button variant="outline" size="icon" onClick={prevImage} className="absolute top-1/2 left-4 -translate-y-1/2 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 coarse:!opacity-100 transition-opacity" aria-label="Previous image"><ChevronLeft className="h-6 w-6 coarse:h-10 coarse:w-10" aria-hidden /></Button><Button variant="outline" size="icon" onClick={nextImage} className="absolute top-1/2 right-4 -translate-y-1/2 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 coarse:!opacity-100 transition-opacity" aria-label="Next image"><ChevronRight className="h-6 w-6 coarse:h-10 coarse:w-10" aria-hidden /></Button><div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-2 py-1 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 coarse:!opacity-100 transition-opacity">{activeImageIndex + 1} / {allDisplayMedia.length}</div></>)}
      </>
    );

    /**
     * The frame. Square in the LEFT layout so the right-hand column always
     * starts at a predictable height; seller-chosen in the TOP layout, where
     * the gallery owns the full width and there is no adjacent column for it to
     * push around. `mx-auto` is what centres a top frame that the height cap has
     * made narrower than the container, putting the spare width either side.
     *
     * That spare width is also where the thumbnails go, for every shape except
     * page-width. See galleryThumbnailsOnSide for why, and note that the rail
     * is only a rail at `md`: on a phone it is the strip underneath, as before.
     */
    const galleryFrame = galleryIsFullWidth ? (
      // The block holds the frame and its thumbnails, and carries a max-width
      // of its own: the frame's ceiling plus the rail when there is one, so the
      // picture keeps the size it had and the rail sits in what was empty
      // margin. Both ceilings are applied — see galleryBlockStyle — so a
      // viewport narrow enough to drop the rail underneath still caps the frame.
      <div
        className={cn('w-full mx-auto', galleryThumbsOnSide && 'md:flex md:items-stretch md:gap-3')}
        style={galleryBlockStyle(galleryRatio, allDisplayMedia.length)}
      >
        <div
          className={cn(
            'relative group w-full rounded-lg overflow-hidden border bg-card',
            gallery.fullWidthFrame,
            // In the rail layout the frame is the flexible half of the row.
            // min-w-0 is not decoration: without it the frame's automatic
            // minimum size stops it shrinking and the rail is pushed out of the
            // block on a narrow container.
            galleryThumbsOnSide && 'md:flex-1 md:min-w-0',
          )}
          style={galleryFrameStyle(galleryRatio)}
        >
          {galleryContent}
        </div>
        <GalleryThumbnails
          media={allDisplayMedia}
          activeIndex={activeImageIndex}
          onSelect={(index) => { setMediaLoaded(false); setActiveImageIndex(index); }}
          orientation={galleryThumbsOnSide ? 'side' : 'bottom'}
          className={galleryThumbsOnSide ? undefined : 'mt-3'}
        />
      </div>
    ) : (
      // The standard two-column frame. Square, so the right-hand column always
      // starts at a predictable height, with the strip underneath it rather
      // than in a rail: there is no spare margin beside a frame that is already
      // exactly one grid column wide.
      <div className={gallery.block}>
        <div className={cn('relative group aspect-square rounded-lg overflow-hidden border bg-card', gallery.frame)}>
          {galleryContent}
        </div>
        {galleryShowsStandardThumbnails(allDisplayMedia.length) && (
          <GalleryThumbnails
            media={allDisplayMedia}
            activeIndex={activeImageIndex}
            onSelect={(index) => { setMediaLoaded(false); setActiveImageIndex(index); }}
            orientation="bottom"
            className={cn('mt-3', gallery.thumbnails)}
          />
        )}
      </div>
    );

    // Title, seller and short description. In the LEFT layout these head the
    // right-hand column, above the buy box, exactly as they always have. In the
    // TOP layout they move into the left column instead, so the page reads
    // image, then name and prose on the left, with the purchase controls alone
    // on the right — and so the right column is not left carrying everything
    // while the left starts at the description.
    //
    // Moving them between columns costs nothing on mobile: below md both
    // wrappers are `display: contents`, so every child of both is flattened
    // into one grid ordered solely by its order-N class, and which wrapper it
    // was declared in stops mattering.
    const sellerBlock = (
      <div className={cn("order-2 md:order-none flex items-center gap-3", listingBadges.length > 0 && "flex-wrap", header)}>
        {product.sellerDeleted ? (
          <div className="flex items-center gap-3">
            {/* No uid passed: an account that is gone is the plain silhouette. */}
            {showSellerAvatar && <UserAvatar />}
            <p className="font-semibold italic text-muted-foreground">Account closed</p>
          </div>
        ) : (
          <Link href={storefrontHref(liveSellerName || product.seller)} {...STOREFRONT_LINK_PROPS} className="flex items-center gap-3 group">
            {showSellerAvatar && (
              <Avatar>
                {/* AvatarPicture rather than UserAvatar, which renders nothing
                    until the browser has loaded the picture after hydration, so
                    the row opened on the placeholder and swapped. In the server's
                    HTML the picture loads with the page, as the storefront's
                    does, and so does the seller's initial when they have none.
                    Unoptimized: the URL the seller stored, as before. */}
                <AvatarPicture
                  src={sellerAvatarUrl}
                  seed={product.sellerId}
                  name={liveSellerName || product.seller}
                  sizes="40px"
                  unoptimized
                />
              </Avatar>
            )}
            {/* The constellation is out of flow and paints first, so the name
                span (relative, later in the DOM) sits over it without a
                negative z-index, and the row keeps its size. */}
            <p className="relative font-semibold group-hover:underline transition-colors"><SellerConstellation seed={product.sellerId} /><span className="relative">{liveSellerName || product.seller}</span></p>
          </Link>
        )}
        {!product.sellerDeleted && sellerTier && !sellerTierHidden && <SellerTier tier={sellerTier} />}
        {/* Each opens what it means. The row wraps only when they are here, to
            put them on a line of their own on a phone. */}
        <ListingBadges badges={listingBadges} />
      </div>
    );
    // The page previews, right under the gallery (or under the name and short
    // description when the gallery spans the page or is hidden), before the
    // description.
    const lookInsidePreviews = pagePreviewsOf(product.pagePreviews);
    const lookInsideBlock = lookInsidePreviews.length > 0 ? (
      <LookInside previews={lookInsidePreviews} pageCount={pdfPageCountOf(product.pdfPageCount)} productName={product.name} />
    ) : null;
    const shortDescriptionBlock = (
      <p className={cn("order-3 md:order-none font-seller-body text-muted-foreground text-lg break-words", header)}>{product.shortDescription}</p>
    );

  return (
    // Extra bottom padding below `md` leaves room for the sticky buy bar, which
    // is fixed and so cannot reserve its own space in the flow. Without it the
    // bar sits on top of the last review.
    //
    // The seller's fonts, as two variables only the seller's own content reads
    // (font-seller-heading / font-seller-body): the title, the short
    // description, the description and its headings, the FAQ and the suggested
    // products. The price, the buy box, the VAT notice and the reviews never
    // opt in, so they stay in the site font whatever the seller picks. Same
    // cascade as the colours (this product's own design, else the seller's
    // brand), and both halves are in the server's seed, so the first HTML is
    // already in the right fonts. The brand counts only once it is settled for
    // THIS product's seller: after a client-side move to another seller's
    // product, `sellerBrand` still holds the last seller's until the new read
    // lands, and their fonts must not dress this page meanwhile. On a page the
    // server rendered it is settled from the first render.
    //
    // (The buy button's colour is not set here: ThemeProvider marks <body>
    // with data-seller-accent when the seller picked an accent, so this page,
    // its dialogs and the header all follow it. See src/app/globals.css.)
    <div
      className="py-12 pb-28 md:pb-12 w-full"
      style={pageFontStyle(resolvePageFonts(
        product.design,
        product.sellerId && sellerBrandSettledFor === product.sellerId ? sellerBrand : null,
      ))}
    >
        {/* The background colour in the server HTML, from the same cascade
            and the same settled-brand guard as the fonts above, so the page
            does not open in the stock colours and swap after hydration. */}
        <SellerPageBackground
          color={resolveDesignWithSellerBrand(
            product.design,
            product.sellerId && sellerBrandSettledFor === product.sellerId ? sellerBrand : null,
          )?.backgroundColor}
        />
        <div className="mb-8">
          <BackButton fallbackHref="/marketplace" />
        </div>
      {/* The gallery, when the seller chose to put it on top. Nothing renders
          here for a seller who chose no gallery, and nothing renders in the
          left column either: the page then opens on the name, with the buy box
          beside it, which is the full-width shape minus the frame.

          POSITION IS FORCED to a sibling ABOVE the grid, not chosen. Below md
          both column wrappers are `display: contents`, which flattens their
          children into ONE grid ordered solely by order-1..order-12 — there is
          no slot above order-1. At md the wrappers become block-level grid
          items, so anything nested inside one is a block child rather than a
          grid item and md:col-span-2 does nothing. A sibling above the grid is
          the only place a full-width element can go.

          It stays inside the page container rather than breaking out to the
          viewport width: <main> has no overflow-x guard anywhere in the app, so
          a 100vw child overshoots by the scrollbar width and puts a horizontal
          scrollbar on the whole document. */}
      {galleryIsFullWidth && <div className="mb-8">{galleryFrame}</div>}
      <div className={cn("grid md:grid-cols-2 md:gap-x-12 md:gap-y-0", sectionLayout.grid)}>
        <div className={cn("contents md:block", sectionLayout.leftColumn)}>
          {!galleryStacked && (
            <div className="order-4 md:order-none">
              {galleryFrame}
              {lookInsideBlock && <div className={cn('pt-6', section)}>{lookInsideBlock}</div>}
            </div>
          )}
          {galleryStacked && titleBlock}
          {galleryStacked && sellerBlock}
          {galleryStacked && shortDescriptionBlock}
          {/* The stacked layouts leave order-4 free in the grid (the gallery
              sits above it, or is hidden), so the previews take it there. */}
          {galleryStacked && lookInsideBlock && (
            <div className={cn('order-4 md:order-none pt-4', section)}>{lookInsideBlock}</div>
          )}
          <div className={cn("order-5 md:order-none prose prose-invert max-w-none py-4 break-words [&_*]:break-words", section)}><BundleContents view={bundleView} bundleId={product.id} /><h2 className="mb-4 font-seller-heading">Description</h2><div className="rich-description font-seller-body" dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(product.longDescription) }} /></div>

          {/* The seller's free sample, immediately after the description on both
              layouts. On mobile the two column wrappers are `display: contents`,
              so every child of both columns is sorted into ONE sequence by these
              order-N values, and the whole page reads in that one order:
              title (1), seller (2), short description (3), gallery (4),
              description (5), this sample (6), price (7), buy (8), guest access
              (9), the spec table (10), reviews (11), FAQ (12). Every slot is
              taken, so anything inserted here renumbers the ones after it
              rather than reusing an integer and leaving the outcome to DOM
              source order, which is a tie-break nothing in this file states or
              tests.

              Mounted only once there is something to render. NOT inside a
              Suspense boundary: nothing in it suspends (a static import), and on
              this page, which renders on the server, a boundary lets React
              stream the sample separately and reveal it 300 ms after the rest,
              pushing the price and buy box down on a phone as it lands. See
              src/app/providers.tsx. */}
          {resolvedPreview && (
            <div className={cn("order-6 md:order-none pt-4", section)}>
              <ProductPreview preview={resolvedPreview} productName={product.name} />
            </div>
          )}

          {(product.details?.faq || []).filter((item: any) => item.question?.trim()).length > 0 && (
            <div className={cn("order-12 md:order-none pt-4", section)}>
              <h2 className="mb-4 font-seller-heading">FAQ</h2>
              {(product.details.faq as Array<{ question: string; answer: string }>)
                .filter(item => item.question?.trim())
                .map((item, i) => (
                  <FaqItem key={i} question={item.question} answer={item.answer} />
                ))}
            </div>
          )}

        </div>
        <div className={cn("contents md:block", sectionLayout.rightColumn)}>
          {!galleryStacked && titleBlock}
          {!galleryStacked && sellerBlock}
          {!galleryStacked && shortDescriptionBlock}
          <div className={cn("order-7 md:order-none", section)}>{getPricingComponent()}<BundlePriceNote view={bundleView} />{!bundleView.isBundle && <AlsoInBundles bundles={pageSeed?.inBundles} />}</div>

          <div className={cn("order-8 md:order-none", section)}>
          {product.isMerchant ? (
            product.status === 'deleted' ? (
              <Button size="lg" className="w-full pointer-events-none cursor-default" variant="secondary" tabIndex={-1} aria-disabled="true">Product deleted</Button>
            ) : product.status === 'archived' ? (
              // Archived keeps the seller's two actions, because unlike a
              // deleted listing this one is coming back: analytics still has
              // history worth reading and the editor is where they restore it.
              // The label is what tells them the page they are previewing is
              // not the page a buyer can reach.
              <div className="space-y-2">
                <Button size="lg" className="w-full pointer-events-none cursor-default" variant="secondary" tabIndex={-1} aria-disabled="true">Archived, not on sale</Button>
                <Button size="lg" variant="outline" className="w-full" asChild>
                  <Link href="/dashboard/products?tab=archived">Manage in archived</Link>
                </Button>
              </div>
            ) : (
              <div className="flex gap-4">
                <Button size="lg" className="flex-1" asChild>
                  <Link href={`/dashboard/analytics?product=${product.id}`}>
                    <BarChart className="h-4 w-4"/>
                    <span className="md:hidden">Analytics</span>
                    <span className="hidden md:inline">View analytics</span>
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="flex-1" asChild>
                  <Link href={`/dashboard/products/edit/${product.id}`}>
                    <Pencil className="h-4 w-4"/>
                    <span className="md:hidden">Edit</span>
                    <span className="hidden md:inline">Edit product</span>
                  </Link>
                </Button>
              </div>
            )
          ) : showPurchaseActions ? (
              // `buyCtaRef` is what the sticky mobile bar watches. It sits on
              // the wrapper rather than the button so the observer still has a
              // node in the subscription-gate branch, which renders a Link.
              <div className="space-y-2" ref={buyCtaRef}>
                <div className="flex gap-4">
                  {isRecurringPurchase && subscriptionGate ? (
                    // Account-only purchase. Send the buyer to sign up rather
                    // than let them fill a cart that checkout has to reject.
                    <Button variant="brand" size="lg" className="flex-1" asChild>
                      <Link href={subscriptionGateHref(subscriptionGate, `/product/${product.id}`)}>
                        {SUBSCRIPTION_GATE_COPY[subscriptionGate].title}
                      </Link>
                    </Button>
                  ) : (
                  <Button variant="brand" size="lg" className="flex-1" onClick={handleAddToCart}
                    disabled={(isRecurringPurchase && isUserLoading) || (product.pricingModel === 'pwyw' && (!pwywPrice || pwywAud < Math.max(PRICE_MIN_AUD, product.pwywMinPriceEnabled ? (product.pwywMinPrice ?? 0) : 0) || pwywAud > PRICE_MAX_AUD))}>
                    {isRecurringPurchase ? (
                      <span>Subscribe</span>
                    ) : isAuthenticated ? (
                      <>
                        <span className="md:hidden">Add</span>
                        <span className="hidden md:inline">Add to cart</span>
                      </>
                    ) : (
                      <span>Add to cart</span>
                    )}
                  </Button>
                  )}
                  {isAuthenticated && (
                      <Button size="lg" variant="outline" onClick={handleSaveToggle}>
                          {/* Filled in its own ink when saved: `fill-white` vanished on the
                              light outline button, so saved and unsaved looked alike. */}
                          <Bookmark className={cn("mr-2", isSaved && "fill-current")} aria-hidden /> {isSaved ? 'Unsave' : 'Save'}
                      </Button>
                  )}
                </div>
                {isRecurringPurchase && (
                    subscriptionGate ? (
                        <p className="text-xs text-muted-foreground">
                            {SUBSCRIPTION_GATE_COPY[subscriptionGate].body}
                        </p>
                    ) : (
                        <p className="text-xs text-muted-foreground">
                            Recurring payment. Cancel anytime, and keep access to the end of the cycle you have paid for.
                            {selectedTier ? ' You can move to another tier later, from this page or your subscriptions.' : ''}
                        </p>
                    )
                )}
                {/* A canceled subscription's deadline is repeated here because
                    this branch is reachable while it still matters: a lapsed
                    subscription drops out of ownedProducts' live set and the
                    page falls back to the Subscribe CTA (say why, rather than
                    let it look like the purchase never happened), and on a
                    tiered product selecting a tier other than the subscribed one
                    shows this branch while access is still running. */}
                {subscriptionAccess && (
                    <p className={cn('text-xs', subscriptionAccess.expired ? 'text-muted-foreground' : 'text-warning')}>
                        {subscriptionAccess.expired
                            ? `Your subscription ended on ${formatUserDate(subscriptionAccess.ms)}, so access to this product has stopped.`
                            : `Your subscription is cancelled. You have access until ${formatUserDate(subscriptionAccess.ms)}.`}
                    </p>
                )}
              </div>
          ) : (
              <div className="space-y-4">
                  {/* Tier switching for an existing subscriber. Rendered ABOVE the
                      owned-product actions rather than instead of them: the buyer
                      still has everything they paid for while a switch is pending,
                      so Download and Review must stay reachable. Nothing here
                      charges anything — the change lands at the next payment. */}
                  {(showTierSwitchAction || showTierSwitchPending) && selectedTier && productSubscription && (
                      <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                          {showTierSwitchPending ? (
                              <>
                                  <p className="text-sm">
                                      <span className="font-semibold">Switching to {selectedTier.name}</span>
                                      {subscriptionNextChargeMs
                                          ? ` on ${formatUserDate(subscriptionNextChargeMs)}.`
                                          : ' at your next payment.'}
                                      {pendingTierChange
                                          ? ` You will then be charged ${formatCurrency(pendingTierChange.priceAud)} ${formatRecurring(pendingTierChange.frequency)}.`
                                          : ''}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                      Until then you stay on {productSubscription.tierName || 'your current tier'} at{' '}
                                      {formatCurrency(productSubscription.priceAud)} {formatRecurring(productSubscription.frequency)},
                                      with the files that tier includes.
                                  </p>
                                  <Button
                                      variant="outline"
                                      size="sm"
                                      loading={isChangingTier}
                                      onClick={() => handleTierChange(productSubscription.tierId ?? '', productSubscription.tierName || 'your current tier')}
                                  >
                                      Keep my current tier
                                  </Button>
                              </>
                          ) : (
                              <>
                                  <p className="text-sm">
                                      You are on <span className="font-semibold">{productSubscription.tierName || 'your current tier'}</span> at{' '}
                                      {formatCurrency(productSubscription.priceAud)} {formatRecurring(productSubscription.frequency)}.
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                      Switching to {selectedTier.name} takes effect
                                      {subscriptionNextChargeMs ? ` on ${formatUserDate(subscriptionNextChargeMs)}` : ' at your next payment'},
                                      when you will be charged {formatCurrency(selectedTier.price)} {formatRecurring(tierFrequency(selectedTier))} instead.
                                      Nothing is charged now, and you keep your current tier until then.
                                  </p>
                                  <Button
                                      size="sm"
                                      loading={isChangingTier}
                                      onClick={() => handleTierChange(selectedTier.id, selectedTier.name)}
                                  >
                                      Switch to {selectedTier.name}
                                  </Button>
                              </>
                          )}
                          {/* Every figure in this block is the seller's price, which
                              excludes the VAT an EU subscriber is actually charged on
                              top of it. Unlike a purchase there is no checkout step
                              after this to correct the number: a tier switch just
                              lands on the next renewal, so "you will be charged
                              A$12.99 monthly" would be the last thing a German
                              subscriber was told before a A$15.46 debit. */}
                          {euVatBuyer && (
                              <p className="text-xs text-muted-foreground">
                                  Amounts shown exclude VAT, which is added to each payment at
                                  your country&apos;s rate.
                              </p>
                          )}
                      </div>
                  )}
                  <div className="flex gap-4">
                      <Dialog open={downloadOpen} onOpenChange={setDownloadOpen}>
                          <DialogTrigger asChild>
                              <Button size="lg" className="flex-1"><Download className="mr-2" /> Download</Button>
                          </DialogTrigger>
                          <Suspense>
                              <DownloadDialog
                                  open={downloadOpen}
                                  files={downloadableFiles}
                                  tierName={purchasedTierName}
                                  isTiered={product.pricingModel === 'tiered'}
                                  tierScopeResolved={tierScopeResolved}
                                  accessLinkCount={linkCount}
                                  isGuest={isGuestPurchase}
                                  productId={product.id}
                                  guestEmail={isGuestPurchase ? getGuestOrderInfo(product.id)?.email : undefined}
                                  onComplete={() => setDownloadOpen(false)}
                                  bundleItems={bundleView.isBundle ? bundleView.resolution.items.map((item) => ({
                                      productId: item.ref.productId,
                                      label: bundleItemLabel(item.grant.productName, item.grant.tierName),
                                  })) : undefined}
                              />
                          </Suspense>
                      </Dialog>
                      {product.status !== 'deleted' && (
                      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                          <DialogTrigger asChild>
                              <Button size="lg" variant="outline" className="flex-1">
                                  <span className="md:hidden">Review</span>
                                  <span className="hidden md:inline">
                                      {(isGuestPurchase ? userGuestReview : userReview) ? 'Edit your review' : 'Write a review'}
                                  </span>
                              </Button>
                          </DialogTrigger>
                          <Suspense>
                              <ReviewDialog
                                  // Remounted when the viewer's review resolves
                                  // or goes away, so its useState seeds from the
                                  // right rating and text (it is mounted before
                                  // the reviews listener resolves), but held
                                  // still while open. See reviewDialogKey.
                                  // The rating an emailed star asked for is part of
                                  // the key, so the form is seeded with it.
                                  key={`${reviewDialogKey.key}:${reviewInitialRating ?? 0}`}
                                  initialRating={reviewInitialRating ?? undefined}
                                  onSave={isGuestPurchase ? handleSaveGuestReview : handleSaveReview}
                                  onDelete={isGuestPurchase ? handleDeleteGuestReview : handleDeleteReview}
                                  onClose={() => setReviewOpen(false)}
                                  existingReview={isGuestPurchase
                                      ? (userGuestReview ? { rating: userGuestReview.rating, text: userGuestReview.body } : undefined)
                                      : (userReview ? { rating: userReview.rating, text: userReview.text } : undefined)}
                              />
                          </Suspense>
                      </Dialog>
                      )}
                      {isGuestPurchase ? (
                          guestOrderInfo && (() => {
                              const receiptHref = getGuestReceiptHref(guestOrderInfo.orderId);
                              // Skip the menu entirely when neither a receipt
                              // link nor a refund action is available — leaves
                              // the row uncluttered for guests we can't help.
                              if (!receiptHref && !refundActionAvailable && !refundEscalationAvailable) return null;
                              return (
                                  <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                          <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0" aria-label="More order actions"><MoreHorizontal className="h-5 w-5" aria-hidden /></Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent>
                                          {receiptHref && (
                                              <DropdownMenuItem asChild>
                                                  <Link href={receiptHref}>View receipt</Link>
                                              </DropdownMenuItem>
                                          )}
                                          {refundActionAvailable && (
                                              guestRefundStatus === 'Pending' || guestRefundStatus === 'Processing' ? (
                                                  <DropdownMenuItem disabled className="text-muted-foreground">Refund pending</DropdownMenuItem>
                                              ) : guestRefundStatus === 'Under Review' ? (
                                                  <DropdownMenuItem disabled className="text-muted-foreground">Refund under review</DropdownMenuItem>
                                              ) : guestRefundStatus === 'Accepted' ? (
                                                  <DropdownMenuItem disabled className="text-muted-foreground">Refund approved</DropdownMenuItem>
                                              ) : guestRefundStatus === 'Denied' ? (
                                                  // Not a dead end. The seller declining is the SELLER's decision, and
                                                  // supa.place is merchant of record, so the buyer can still ask us to
                                                  // review it — which is what the Terms promise. Without a control here
                                                  // that promise lived only in an email footer, and a buyer looking at
                                                  // the app saw a greyed-out "Refund Denied" and went to their bank.
                                                  // Suppressed when WE made the decision; appealing to us against our
                                                  // own decision is a loop that cannot resolve.
                                                  canAppealRefund({
                                                      status: guestRefundStatus,
                                                      resolvedByAdmin: guestRefundResolvedByAdmin,
                                                  }) ? (
                                                      <DropdownMenuItem asChild>
                                                          <Link href={refundAppealHref({
                                                              productId: product.id,
                                                              productName: product.name,
                                                              orderId: guestOrderInfo?.orderId,
                                                              requestedAtMs: guestRefundRequestedAtMs,
                                                          })}>
                                                              Refund denied, ask us to review
                                                          </Link>
                                                      </DropdownMenuItem>
                                                  ) : (
                                                      <DropdownMenuItem disabled className="text-muted-foreground">
                                                          Refund denied
                                                      </DropdownMenuItem>
                                                  )
                                              ) : (
                                                  <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
                                                      <DialogTrigger asChild>
                                                          <DropdownMenuItem onSelect={e => { e.preventDefault(); setRefundDialogOpen(true); }}>Request refund</DropdownMenuItem>
                                                      </DialogTrigger>
                                                      <Suspense>
                                                          <ReportDialog
                                                              productId={product.id}
                                                              productName={product.name}
                                                              sellerId={product.sellerId}
                                                              paymentIntentId={guestOrderInfo.orderId}
                                                              type="Refund"
                                                              isOpen={refundDialogOpen}
                                                              guest={guestOrderInfo}
                                                              onSubmitted={() => setGuestRefundStatus('Pending')}
                                                              onClose={() => setRefundDialogOpen(false)}
                                                          />
                                                      </Suspense>
                                                  </Dialog>
                                              )
                                          )}
                                          {/* The seller switched self-service refunds off. The
                                              escalation is still owed, so it is offered directly
                                              rather than behind a denial that can never happen
                                              here (no request is ever created, so there is no
                                              refund doc to be denied). */}
                                          {!refundActionAvailable && refundEscalationAvailable && (
                                              <DropdownMenuItem asChild>
                                                  <Link href={refundAppealHref({
                                                      productId: product.id,
                                                      productName: product.name,
                                                      orderId: guestOrderInfo?.orderId,
                                                  }, 'no-policy')}>
                                                      Ask us to review this purchase
                                                  </Link>
                                              </DropdownMenuItem>
                                          )}
                                      </DropdownMenuContent>
                                  </DropdownMenu>
                              );
                          })()
                      ) : (
                          <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-11 w-11 shrink-0" aria-label="More order actions"><MoreHorizontal className="h-5 w-5" aria-hidden /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                  {purchasePaymentIntentId && (
                                      <DropdownMenuItem asChild>
                                          <Link href={`/receipt?payment_intent=${purchasePaymentIntentId}&redirect_status=succeeded`}>View receipt</Link>
                                      </DropdownMenuItem>
                                  )}
                                  {refundActionAvailable && (
                                    buyerRefundDispute ? (
                                        buyerRefundDispute.status === 'Denied' ? (
                                            // Same escalation the guest branch offers, which this
                                            // path was missing: a signed-in buyer saw a dead
                                            // "Refund Denied" while a guest with the identical
                                            // order got an appeal link. (The disabled item was
                                            // also nested inside an `asChild` wrapper expecting a
                                            // single element, so it rendered a menu item inside a
                                            // menu item.) Suppressed when WE decided, since
                                            // appealing to us against our own decision is a loop
                                            // that cannot resolve.
                                            canAppealRefund({
                                                status: buyerRefundDispute.status,
                                                resolvedByAdmin: (buyerRefundDispute as { resolvedByAdmin?: boolean }).resolvedByAdmin,
                                            }) ? (
                                                <DropdownMenuItem asChild>
                                                    <Link href={refundAppealHref({
                                                        productId: product.id,
                                                        productName: product.name,
                                                        orderId: purchasePaymentIntentId ?? undefined,
                                                    })}>
                                                        Refund denied, ask us to review
                                                    </Link>
                                                </DropdownMenuItem>
                                            ) : (
                                                <DropdownMenuItem disabled className="text-muted-foreground">
                                                    Refund denied
                                                </DropdownMenuItem>
                                            )
                                        ) : buyerRefundDispute.status === 'Under Review' ? (
                                            <DropdownMenuItem disabled className="text-warning">
                                                Refund under review
                                            </DropdownMenuItem>
                                        ) : buyerRefundDispute.status === 'Processing' ? (
                                            <DropdownMenuItem disabled className="text-muted-foreground">
                                                Refund processing
                                            </DropdownMenuItem>
                                        ) : buyerRefundDispute.status === 'Accepted' ? (
                                            <DropdownMenuItem disabled className="text-muted-foreground">
                                                Refund approved
                                            </DropdownMenuItem>
                                        ) : (
                                            <DropdownMenuItem disabled className="text-muted-foreground">
                                                Refund {String(buyerRefundDispute.status).toLowerCase()}
                                            </DropdownMenuItem>
                                        )
                                    ) : (
                                        <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
                                            <DialogTrigger asChild>
                                                <DropdownMenuItem onSelect={e => { e.preventDefault(); setRefundDialogOpen(true); }}>Request refund</DropdownMenuItem>
                                            </DialogTrigger>
                                            <Suspense>
                                                <ReportDialog
                                                    productId={product.id}
                                                    productName={product.name}
                                                    sellerId={product.sellerId}
                                                    paymentIntentId={purchasePaymentIntentId}
                                                    type="Refund"
                                                    isOpen={refundDialogOpen}
                                                    subscription={!!productSubscription}
                                                    onSubmitted={() => {}}
                                                    onClose={() => setRefundDialogOpen(false)}
                                                />
                                            </Suspense>
                                        </Dialog>
                                    )
                                  )}
                                  {/* Seller has self-service refunds switched off. See
                                      refundEscalationAvailable. */}
                                  {!refundActionAvailable && refundEscalationAvailable && (
                                      <DropdownMenuItem asChild>
                                          <Link href={refundAppealHref({
                                              productId: product.id,
                                              productName: product.name,
                                              orderId: purchasePaymentIntentId ?? undefined,
                                          }, 'no-policy')}>
                                              Ask us to review this purchase
                                          </Link>
                                      </DropdownMenuItem>
                                  )}
                                  {product.status !== 'deleted' && (buyerReportDispute ? (
                                    <DropdownMenuItem disabled className="text-muted-foreground">
                                        Report {String(buyerReportDispute.status).toLowerCase()}
                                    </DropdownMenuItem>
                                  ) : (
                                    <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
                                        <DialogTrigger asChild>
                                            <DropdownMenuItem onSelect={e => { e.preventDefault(); setReportDialogOpen(true); }}>Report product</DropdownMenuItem>
                                        </DialogTrigger>
                                        <Suspense>
                                            <ReportDialog
                                                productId={product.id}
                                                productName={product.name}
                                                sellerId={product.sellerId}
                                                type="Report"
                                                isOpen={reportDialogOpen}
                                                onSubmitted={() => {}}
                                                onClose={() => setReportDialogOpen(false)}
                                            />
                                        </Suspense>
                                    </Dialog>
                                  ))}
                              </DropdownMenuContent>
                          </DropdownMenu>
                      )}
                  </div>

                  {/* "Withdraw from contract here" (CRD Art. 11a): shown in the open,
                      not in the menu above, while an EU/EEA/UK buyer can still
                      withdraw (14 days, ending early once delivery starts on a
                      confirmed consent). The server decides; the receipt carries the
                      same control for every order the right applies to. */}
                  {(isGuestPurchase ? guestOrderInfo?.orderId : purchasePaymentIntentId) && (
                      <WithdrawFromContract
                          target={{
                              orderId: (isGuestPurchase ? guestOrderInfo?.orderId : purchasePaymentIntentId) ?? '',
                              productId: product.id,
                              productName: product.name,
                              ...withdrawFactsFromPurchase(
                                  !isGuestPurchase && purchaseRecord?.productId === product.id ? purchaseRecord.data : undefined,
                                  product.id, viewerGeoCountry,
                              ),
                              ...(isGuestPurchase && guestOrderInfo ? { guestEmail: guestOrderInfo.email } : {}),
                          }}
                          defaultName={isGuestPurchase ? '' : (authUser?.displayName || '')}
                          email={isGuestPurchase ? guestOrderInfo?.email : authUser?.email}
                          onWithdrawn={handleWithdrawn}
                      />
                  )}

                  {/* Recurring purchase: billing is managed from the page the
                      product lives on, not only from the dashboard. Once
                      canceled, the access deadline is stated here too, because this is
                      where the buyer comes to download, so it is where the
                      question "how long do I still have?" actually gets asked. */}
                  {subscriptionAccess && !subscriptionAccess.expired && (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="text-xs text-warning">
                              Subscription cancelled. You have access until {formatUserDate(subscriptionAccess.ms)}.
                          </p>
                          {/* Restarting is allowed right now, on the same tier:
                              the server's entitlement guard blocks stacking a
                              SECOND tier, not changing your mind. Without this
                              the buyer had to wait for access to lapse before
                              the page would offer them anything at all. */}
                          {showResubscribe && (
                              subscriptionGate ? (
                                  <Button variant="outline" size="sm" asChild>
                                      <Link href={subscriptionGateHref(subscriptionGate, `/product/${product.id}`)}>
                                          {SUBSCRIPTION_GATE_COPY[subscriptionGate].title}
                                      </Link>
                                  </Button>
                              ) : (
                                  <Button variant="outline" size="sm" onClick={handleAddToCart} disabled={isUserLoading}>
                                      Subscribe again
                                  </Button>
                              )
                          )}
                      </div>
                  )}
                  {canCancelSubscription && (
                      <div className="flex flex-wrap items-center justify-between gap-3">
                          <p className="text-xs text-muted-foreground">
                              {subscriptionNextChargeMs
                                  ? `Subscription renews ${formatUserDate(subscriptionNextChargeMs)}.`
                                  : 'Recurring subscription.'}
                          </p>
                          <ConfirmDialog
                              trigger={
                                  <Button variant="outline" size="sm" loading={isCancelingSubscription}>
                                      {isCancelingSubscription ? 'Cancelling…' : 'Cancel subscription'}
                                  </Button>
                              }
                              title="Cancel this subscription?"
                              description={
                                  `Future billing for "${product.name}" stops right away. ` +
                                  (subscriptionNextChargeMs && subscriptionNextChargeMs > nowMs
                                      ? `You keep access until ${formatUserDate(subscriptionNextChargeMs)}, the end of the cycle you have already paid for.`
                                      : 'You keep access to the end of the cycle you have already paid for.')
                              }
                              cancelLabel="Keep subscription"
                              confirmLabel="Cancel subscription"
                              onConfirm={handleCancelSubscription}
                          />
                      </div>
                  )}
              </div>
          )}
          {/* The seller's live preview, a public page to look at before buying
              (src/lib/live-preview-link.ts). It sits inside the buy section
              rather than in a slot of its own because every mobile order-N slot
              is taken (see the sample section above), and beside the buy button
              is where a buyer deciding looks for it. resolveLivePreviewUrl is the
              render gate: the field is seller-written and becomes an href here.
              The editors refuse a preview that is one of the paid links. */}
          {livePreviewUrl && product.status !== 'deleted' && (
              <Button asChild size="lg" variant="outline" className="mt-3 w-full">
                  <a href={livePreviewUrl} target="_blank" rel="noopener noreferrer nofollow ugc">
                      <ExternalLink className="h-4 w-4" aria-hidden />
                      Live preview
                      <span className="sr-only"> (opens in a new tab)</span>
                  </a>
              </Button>
          )}
          </div>

          {/* Guest re-access: shown to unverified anonymous/guest users on paid products */}
          {showGuestAccess && (
            renderGuestAccess(cn("order-9 md:order-none text-center", section))
          )}

          {/* The spec table, AFTER the price and the buy controls and before the
              reviews. It used to open the right-hand column, which put a
              collapsed table of file sizes and dates in the slot the purchase
              decision belongs in. A buyer reads the price, decides, and only
              then checks the format and the refund policy, so the card sits
              where that question is actually asked.

              The order-N values matter as much as the source position: below
              `md` both column wrappers are `display: contents`, so the whole
              page is one grid sorted by these numbers alone and moving the JSX
              without moving the number would reorder the desktop columns and
              leave the phone exactly as it was. */}
          <div className={cn("order-10 md:order-none", section)}>
          <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
            <Card className={flatCard}>
              <CardHeader className="flex flex-row items-center justify-between py-3">
                <CardTitle>Product Details</CardTitle>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 coarse:h-10 coarse:w-10 shrink-0" aria-label={detailsOpen ? 'Collapse product details' : 'Expand product details'}>
                    <ChevronDownIcon className={cn("h-4 w-4 transition-transform duration-300", detailsOpen ? "rotate-0" : "-rotate-180")} aria-hidden />
                  </Button>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <CardContent className="grid grid-cols-2 gap-4 text-sm pt-0">
                  {displayedDetails.map(detail => (
                    <div key={detail.label}>
                      <p className="text-muted-foreground">{detail.label}</p>
                      <div className="font-medium">{detail.value}</div>
                    </div>
                  ))}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
          </div>

          {/* Reviews section — always shown */}
          {/* No extra top padding of its own: the column's `space-y-6` (and the
              grid's `gap-y-6` once the columns flatten on a phone) is what sets
              every gap between sections, and an extra `pt-8` here made the step
              from the spec table down to the reviews more than twice the step
              from the price down to the spec table. */}
          <div className={cn("order-11 md:order-none", section)}>
            {/* The heading lives INSIDE the card, the way Product Details' does,
                rather than floating above it, and the card collapses from the
                same chevron.

                Its padding is Product Details' padding EXACTLY, with no flat-card
                exception: both headings have to start at the same x or the two
                boxes in a connected outline read as misaligned. The `px-0` this
                used to take in outline mode pulled the reviews title 24px left
                of the spec table's. */}
            <Collapsible open={reviewsOpen} onOpenChange={setReviewsOpen}>
            <Card className={flatCard}>
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3 space-y-0 py-3">
                <h2>Reviews{totalReviews > 0 && <CountPill count={totalReviews} />}</h2>
                <div className="flex items-center gap-3">
                  {totalReviews > 1 && (
                    <div className="flex items-center gap-1 border rounded-lg p-1">
                        {(['helpful', 'recent', 'controversial'] as const).map(s => (
                            <Button key={s} variant={reviewSort === s ? 'secondary' : 'ghost'} size="sm" className="h-7 px-3 text-xs capitalize" onClick={() => setReviewSort(s)}>
                                {s === 'helpful' ? 'Most helpful' : s === 'recent' ? 'Most recent' : 'Controversial'}
                            </Button>
                        ))}
                    </div>
                  )}
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 coarse:h-10 coarse:w-10 shrink-0" aria-label={reviewsOpen ? 'Collapse reviews' : 'Expand reviews'}>
                      <ChevronDownIcon className={cn("h-4 w-4 transition-transform duration-300", reviewsOpen ? "rotate-0" : "-rotate-180")} aria-hidden />
                    </Button>
                  </CollapsibleTrigger>
                </div>
              </CardHeader>
              <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                <CardContent className="pt-0">
              {/* No reviews draws one quiet line, never a "0.0" score over
                  five empty stars and five 0% bars, which reads as a bad
                  rating on a listing nobody has reviewed yet. */}
              {totalReviews === 0 ? (
                <p className="text-sm text-muted-foreground">No reviews yet.</p>
              ) : (
              <div className="flex items-center gap-8"><div className="text-center w-28"><p className="text-5xl font-bold">{averageRating.toFixed(1)}</p><div className="flex justify-center"><StarRating rating={averageRating} /></div><p className="text-sm text-muted-foreground mt-1">Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}</p></div><div className="flex-1"><div className="space-y-1">{reviewDistribution.map((count, i) => (<div key={5 - i} className="flex items-center gap-2"><span className="w-2 text-sm tabular-nums">{5 - i}</span><Star className="h-4 w-4 text-star fill-star" /><Progress value={(count / (allRatings.length || 1)) * 100} className="w-full h-2" /><span className="w-12 text-right text-sm text-muted-foreground tabular-nums">{((count / (allRatings.length || 1)) * 100).toFixed(0)}%</span></div>))}</div></div></div>
              )}
                </CardContent>
              </CollapsibleContent>
            </Card>
            </Collapsible>
            {/* The list collapses with the box above it. Leaving twenty
                reviews on screen under a collapsed Reviews heading would
                make the chevron look broken; collapsing the section means
                collapsing the section. */}
            {reviewsOpen && (<>
            {sortedReviews.slice(0, reviewsToShow).map(review => (
                <ReviewItem
                    key={review.id}
                    review={review}
                    resolvedAvatarUrl={reviewerAvatarUrls[review.uid] ?? null}
                    resolvedUsername={reviewerUsernames[review.uid]}
                    isDeleted={reviewerExists[review.uid] === false}
                    isAuthenticated={isAuthenticated}
                    formatDate={getFormattedDateOnly}
                    onReply={handleReplyToReview}
                    onEditReply={handleEditReply}
                    onDeleteReply={handleDeleteReply}
                    onVote={handleVoteOnReview}
                    isMerchant={!!product.isMerchant}
                    currentUserId={authUser?.uid}
                    isProductDeleted={product.status === 'deleted'}
                />
            ))}
            {guestReviews.map(review => (
                <GuestReviewItem
                    key={review.id}
                    review={review}
                    formatDate={getFormattedDateOnly}
                    isMerchant={!!product.isMerchant}
                    isAuthenticated={isAuthenticated}
                    onReply={handleReplyToGuestReview}
                    onEditReply={handleEditGuestReply}
                    onDeleteReply={handleDeleteGuestReply}
                    onVote={handleVoteOnGuestReview}
                    currentUserId={authUser?.uid}
                    isProductDeleted={product.status === 'deleted'}
                />
            ))}
            {(sortedReviews.length > reviewsToShow || reviewPage.length >= reviewPageLimit) && (<div className="pt-6 text-center"><Button variant="outline" onClick={showMoreReviews}>Show more reviews</Button></div>)}
            </>)}

          </div>
        </div>
      </div>

      {/* The seller's cross-sell row, BELOW the reviews and outside the grid.
          A sibling rather than another order-N child, for the same reason the
          full-width gallery is one: at `md` the two column wrappers are block
          grid items, so anything nested in one is a block child and cannot span
          both columns. Outside the grid it is full width on desktop and last on
          a phone, which is where "below the reviews" lands in both shapes.

          Renders nothing unless the seller turned the section on and at least
          one of the products they picked is still live; see SuggestedProducts. */}
      <SuggestedProducts
        sellerId={product.sellerId}
        details={product.details}
        currentProductId={product.id}
        separator={sectionSeparator}
      />

      {/* The product page's own top bar, in place of the site nav, which gives
          up its sticky slot on this route (see src/components/layout/header.tsx).
          It carries what a buyer who has scrolled away from the buy box still
          needs to act: what the product is, how it was rated, what it costs and
          the button. `z-30` keeps it UNDER the nav's z-40, so on the way back up
          the nav slides over it rather than through it.

          The price is repeated here, so VatNotice comes with it: an EU or GB
          buyer must not be shown a bare tax-exclusive number, and a hand-rolled
          price block that skips the qualifier is the recurring bug this codebase
          has. See src/lib/vat-notice.ts.

          IT SLIDES DOWN rather than snapping into place. The bar mounts the
          moment the buy controls go off the top of the screen, and appearing
          instantly at full opacity over whatever the buyer was reading reads as
          the page jumping. `slide-in-from-top` with no value is a full -100%
          translate, so the bar travels exactly its own height. `motion-reduce`
          drops the movement for anyone who has asked for that, leaving the bar
          to appear without the travel. */}
      {showPurchaseActions && stickyBarMounted && (
        <div
          className={cn(
            "fixed inset-x-0 top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 duration-300 ease-out motion-reduce:animate-none",
            stickyBarLeaving
              ? "animate-out slide-out-to-top"
              : "animate-in slide-in-from-top",
          )}
        >
          <div className="mx-auto flex max-w-[1368px] items-center gap-3 px-4 py-2 sm:gap-4">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-tight">{product.name}</p>
              {/* Stars, not a number, and the review count beside them. Hidden
                  entirely with no reviews rather than showing five empty stars,
                  which reads as a bad rating rather than as no rating. */}
              {totalReviews > 0 && (
                <StarRating rating={averageRating} totalReviews={totalReviews} className="mt-0.5" />
              )}
            </div>
            {/* Always shown, at every width. The name beside it truncates, so
                the price and the button keep their space on a narrow phone
                rather than the price being the thing that drops out. */}
            <div className="min-w-0 shrink-0 text-right">
              <p className="truncate text-sm font-semibold tabular-nums">
                {stickyBarPrice}
                {stickyBarCycleSuffix}
              </p>
              <VatNotice amount={stickyBarVatProp} className="text-[11px]" />
            </div>
            {stickyBuyButton('shrink-0')}
          </div>
        </div>
      )}

      {/* Sticky mobile buy bar, for the other half of the problem: a CTA that is
          off screen because the buyer has not reached it yet. `buyCtaAbove` is
          what splits the two, so the phone never carries both at once. It stays
          `md:hidden` because from md up the buy box sits beside the gallery and
          is on screen for the whole of that stretch. */}
      {showPurchaseActions && !buyCtaInView && !buyCtaAbove && !stickyBarMounted && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 md:hidden">
          <div className="mx-auto flex max-w-5xl items-center gap-3">
            <div className="min-w-0">
              <p className="text-[11px] leading-none text-muted-foreground">
                {stickyBarLabel}
              </p>
              <p className="truncate text-sm font-semibold tabular-nums">
                {stickyBarPrice}
                {stickyBarCycleSuffix}
              </p>
              <VatNotice amount={stickyBarVatProp} className="text-[11px]" />
            </div>
            {stickyBuyButton('flex-1')}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * @param seed The server's read of the product, its seller and its reviews,
 *   built by page.tsx for a live product (src/lib/product-page-seed.ts). With
 *   it the page renders complete on the server, which puts the product's name,
 *   gallery, price, description, FAQ, tags and reviews into the HTML we serve,
 *   and the browser's first render is the same page. Undefined for anything
 *   else (a draft, an archived or deleted product, a failed read), which loads
 *   in the browser as before.
 * @param sellerTierHidden The seller holds the admin role, so no tier badge is
 *   drawn beside them (src/lib/admin-sellers.ts). Decided by page.tsx with or
 *   without a seed, since the browser cannot read who holds the role.
 *
 * NO SUSPENSE BOUNDARY OF ITS OWN, deliberately. There used to be one, for
 * `useSearchParams`, which bails a subtree out to the client during STATIC
 * prerendering; this route renders per request (the root layout reads
 * headers()), where it never does, and Providers' boundary sits above it
 * regardless. What the boundary did do was stream the page in two pieces:
 * React's server renderer writes any completed boundary larger than about
 * 12.8 KB (Fizz's progressiveChunkSize) after its fallback, then swaps it in,
 * and a product page is twice that. While the fallback was the text stand-in
 * that preceded the page, nobody could tell. With the page rendered whole, the
 * fallback would have been a "Loading product..." line a slow connection could
 * paint before the page replaced it: the very flash this rendering removes.
 */
export default function ProductPageWrapper({ seed, sellerTierHidden = false }: {
    seed?: ProductPageSeed;
    sellerTierHidden?: boolean;
}) {
    return (
        <ProductPage
            loadingFallback={<div className="w-full text-center py-12">Loading product...</div>}
            seed={seed ?? null}
            sellerTierHidden={sellerTierHidden}
        />
    )
}
